import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import {
  isSupabaseSyncEnabled,
  upsertTransactions,
  fetchAllTransactions,
  upsertAgents,
  fetchAllAgents,
  upsertVentures,
  fetchAllVentures,
  upsertAccounts,
  fetchAllAccounts,
  upsertLoans,
  fetchAllLoans,
  toRemoteTransaction,
  fromRemoteTransaction,
  toRemoteAgent,
  fromRemoteAgent,
  toRemoteVenture,
  fromRemoteVenture,
  toRemoteAccount,
  fromRemoteAccount,
  toRemoteLoan,
  fromRemoteLoan,
} from "./services/supabaseSyncService";
import {
  getSupabaseSession,
  onSupabaseAuthStateChange,
  requestSupabasePasswordReset,
  signInWithSupabasePassword,
  signInWithSupabaseOtp,
  signOutOfSupabase,
  updateSupabaseUserMetadata,
} from "./lib/supabaseClient";

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   DEFAULTS & CONSTANTS
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
const DEF_ACCOUNTS = [];

const DEF_VENTURES = [];

const DEF_AGENTS = [];

const DEF_LOANS = [];
const DEF_ITEMS = [];
const EMPTY_VENTURE = { name:'', color:'#c9a84c', description:'', businessType:'general' };
const LEGACY_SAMPLE_ACCOUNTS = [
  { id:'a1',  code:'1100', name:'Cash on Hand',       type:'asset',     normal:'debit'  },
  { id:'a2',  code:'1200', name:'Bank Account',       type:'asset',     normal:'debit'  },
  { id:'a3',  code:'1300', name:'Mobile Money',       type:'asset',     normal:'debit'  },
  { id:'a4',  code:'1400', name:'Accounts Receivable', type:'asset',    normal:'debit'  },
  { id:'a5',  code:'2100', name:'Accounts Payable',   type:'liability', normal:'credit' },
  { id:'a6',  code:'2200', name:'Loans Payable',      type:'liability', normal:'credit' },
  { id:'a7',  code:'3100', name:"Owner's Equity",     type:'equity',    normal:'credit' },
  { id:'a8',  code:'3200', name:'Retained Earnings',  type:'equity',    normal:'credit' },
  { id:'a9',  code:'4100', name:'Sales Revenue',      type:'income',    normal:'credit' },
  { id:'a10', code:'4200', name:'Service Revenue',    type:'income',    normal:'credit' },
  { id:'a11', code:'4900', name:'Other Income',       type:'income',    normal:'credit' },
  { id:'a12', code:'5100', name:'Cost of Goods Sold', type:'expense',   normal:'debit'  },
  { id:'a13', code:'5200', name:'Salaries & Wages',   type:'expense',   normal:'debit'  },
  { id:'a14', code:'5300', name:'Rent',               type:'expense',   normal:'debit'  },
  { id:'a15', code:'5400', name:'Utilities',          type:'expense',   normal:'debit'  },
  { id:'a16', code:'5500', name:'Marketing',          type:'expense',   normal:'debit'  },
  { id:'a17', code:'5600', name:'Transport',          type:'expense',   normal:'debit'  },
  { id:'a18', code:'5900', name:'Other Expenses',     type:'expense',   normal:'debit'  },
  { id:'a19', code:'5800', name:'Short Term Loan Payments', type:'expense', normal:'debit' },
];
const LEGACY_SAMPLE_VENTURES = [
  { id:'v1', name:'Main Business', color:'#c9a84c', description:'Primary operations', businessType:'general', branches:[] },
];
const LEGACY_SAMPLE_AGENTS = [
  { id:'ag1', name:'John Ssemanda', role:'Senior Agent', phone:'+256700123456', commission:0 },
  { id:'ag2', name:'Grace Nakiyemba', role:'Agent', phone:'+256701234567', commission:0 },
];

const PM_ACCOUNT = {
  'Cash':'a1', 'Bank Transfer':'a2',
  'Mobile Money (MTN)':'a3', 'Mobile Money (Airtel)':'a3',
  'Cheque':'a2', 'Card':'a2',
};

const PAYMENT_METHODS = Object.keys(PM_ACCOUNT);
const PALETTE = ['#c9a84c','#2dd4a0','#f05c5c','#60a5fa','#a78bfa','#fb923c','#38bdf8'];
const ACC_TYPES = ['asset','liability','equity','income','expense'];

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   UTILITIES
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
const fmt = n => `UGX ${Number(n||0).toLocaleString('en-UG')}`;
const fmtK = n => {
  const abs = Math.abs(n||0);
  if (abs >= 1e9) return `${(n/1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n/1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n/1e3).toFixed(0)}K`;
  return String(n||0);
};
const uid  = () => Math.random().toString(36).slice(2,10);
const tod  = () => new Date().toISOString().split('T')[0];
const soy  = () => `${new Date().getFullYear()}-01-01`;
const isHotspotCreditIssueTx = t => t?.posType === 'hotspot-agent-credit';
const isIncomeTx = t => t?.type === 'income' && !isHotspotCreditIssueTx(t);
const isExpenseTx = t => t?.type === 'expense';
const getTeamCategoryFromRole = role => (/agent/i.test(String(role || '')) ? 'Agent' : 'Staff');
const isCommissionEligibleMember = role => getTeamCategoryFromRole(role) === 'Agent';
const getTxDisplayType = t => isHotspotCreditIssueTx(t) ? 'credit' : (t?.type || 'unknown');
const getTxBadgeClass = t => {
  const displayType = getTxDisplayType(t);
  if (displayType === 'income') return 'bi';
  if (displayType === 'expense') return 'be';
  if (displayType === 'credit') return 'bCr';
  return '';
};
const getTxAmountClass = t => (isIncomeTx(t) ? 'amt-p' : isExpenseTx(t) ? 'amt-n' : 'amt-g');
const getTxAmountPrefix = t => (isIncomeTx(t) ? '+' : isExpenseTx(t) ? '-' : '');
const mLabel = m => {
  const [y,mo] = m.split('-');
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+mo-1]+' '+y.slice(2);
};

const parseIsoMs = value => {
  const ms = Date.parse(value || '');
  return Number.isNaN(ms) ? 0 : ms;
};

function rowsMatchLegacySeed(rows, legacyRows) {
  if (!Array.isArray(rows) || rows.length !== legacyRows.length) return false;
  return rows.every((row, index) => JSON.stringify(row) === JSON.stringify(legacyRows[index]));
}

function normalizeInitialRows(rows, fallbackRows, legacyRows = []) {
  if (!Array.isArray(rows)) return fallbackRows;
  if (legacyRows.length > 0 && rowsMatchLegacySeed(rows, legacyRows)) return [];
  return rows;
}

function resolvePaymentMethodAccountId(accounts, paymentMethod) {
  const assetAccounts = (accounts || []).filter(account => account.type === 'asset');
  if (!assetAccounts.length) return '';

  const method = String(paymentMethod || '').toLowerCase();
  const keywordMap = {
    cash: ['cash'],
    bank: ['bank', 'card', 'cheque', 'check'],
    mobile: ['mobile', 'momo', 'money', 'mtn', 'airtel'],
  };

  const findByKeywords = keywords => assetAccounts.find(account => {
    const searchable = `${account.code || ''} ${account.name || ''}`.toLowerCase();
    return keywords.some(keyword => searchable.includes(keyword));
  })?.id;

  if (method.includes('cash')) return findByKeywords(keywordMap.cash) || assetAccounts[0].id;
  if (method.includes('bank') || method.includes('card') || method.includes('cheque')) return findByKeywords(keywordMap.bank) || assetAccounts[0].id;
  if (method.includes('mobile') || method.includes('mtn') || method.includes('airtel')) return findByKeywords(keywordMap.mobile) || assetAccounts[0].id;

  return assetAccounts[0].id;
}

function countMergeConflicts(localRows, remoteRows) {
  const localMap = new Map((localRows || []).filter(x => x?.id).map(x => [x.id, x]));
  let conflicts = 0;
  (remoteRows || []).forEach(remote => {
    if (!remote?.id) return;
    const local = localMap.get(remote.id);
    if (!local) return;
    const localUpdated = Math.max(parseIsoMs(local.updatedAt), parseIsoMs(local.createdAt));
    const remoteUpdated = Math.max(parseIsoMs(remote.updatedAt), parseIsoMs(remote.createdAt));
    if (localUpdated !== remoteUpdated) conflicts += 1;
  });
  return conflicts;
}

const SUPABASE_SYNC_DELETIONS_KEY = 'fms:supabase-deletions';
const OFFLINE_AUTH_KEY = 'fms:offline-auth';
const USER_PROFILE_DETAILS_KEY = 'fms:user-profile-details';
const ONBOARDING_CONFIG_KEY = 'fms:onboarding-config';
const DATABASE_CONNECTION_CONFIG_KEY = 'fms:database-config';
const FIRST_INSTALL_COMPLETE_KEY = 'fms:first-install-complete';
const EXPENSE_CATEGORY_MIGRATION_KEY = 'fms:migration-expense-maintenance-repair-v1';
const INCOME_CATEGORY_MIGRATION_KEY = 'fms:migration-income-transport-service-v1';
const HOTSPOT_COLLECTION_REVENUE_MIGRATION_KEY = 'fms:migration-hotspot-collection-revenue-v1';
const REQUIRED_EXPENSE_ACCOUNTS = Object.freeze([
  { code: '5700', name: 'Maintenance Expenses', type: 'expense', normal: 'debit' },
  { code: '5710', name: 'Repair Expenses', type: 'expense', normal: 'debit' },
]);
const REQUIRED_INCOME_ACCOUNTS = Object.freeze([
  { code: '4300', name: 'Transport Service Income', type: 'income', normal: 'credit' },
]);
const EMPTY_SYNC_DELETIONS = Object.freeze({
  transactions: [],
  agents: [],
  ventures: [],
  accounts: [],
  loans: [],
});

const DATABASE_PROVIDER_GUIDES = {
  supabase: {
    title: 'Supabase Setup Guide',
    steps: [
      'Create a Supabase project and copy the Project URL and anon key.',
      'Add values in .env: REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY, REACT_APP_SUPABASE_SCHEMA=public, REACT_APP_SUPABASE_SYNC_ENABLED=true.',
      'Run SQL from docs/supabase-schema.sql in Supabase SQL Editor.',
      'Restart Financial Management System (FMS) and sign in with your Supabase account to start sync.'
    ]
  },
  firebase: {
    title: 'Firebase Setup Guide',
    steps: [
      'Create a Firebase project with Firestore and Authentication enabled.',
      'Create a web app and copy Firebase config credentials.',
      'Implement a Firebase sync adapter in src/services (parallel to supabaseSyncService).',
      'Map Financial Management System (FMS) entities (transactions, agents, ventures, accounts, loans) to Firestore collections.'
    ]
  },
  appwrite: {
    title: 'Appwrite Setup Guide',
    steps: [
      'Create an Appwrite project and database with required collections.',
      'Create API keys and auth rules for per-user data isolation.',
      'Implement an Appwrite client and sync service in src/lib and src/services.',
      'Enable provider mode and verify pull-merge-push sync against local data.'
    ]
  },
  custom: {
    title: 'Custom API Setup Guide',
    steps: [
      'Expose authenticated CRUD endpoints for transactions, agents, ventures, accounts, and loans.',
      'Use a per-user access model similar to Supabase RLS.',
      'Add an adapter service that converts Financial Management System (FMS) local rows to your API payload schema.',
      'Handle offline retries, conflict resolution by updatedAt, and tombstone deletes.'
    ]
  },
  local: {
    title: 'Local-Only Mode',
    steps: [
      'No online database is required to use Financial Management System (FMS) locally.',
      'Your current local data remains available and editable offline.',
      'You can choose an online provider later from the profile menu.',
      'Use File > Backup Data regularly for additional safety.'
    ]
  }
};

const DEFAULT_DATABASE_CONFIG = {
  provider: 'supabase',
  supabase: {
    url: '',
    anonKey: '',
    schema: 'public',
  },
  firebase: {
    apiKey: '',
    authDomain: '',
    projectId: '',
  },
  appwrite: {
    endpoint: '',
    projectId: '',
    databaseId: '',
    apiKey: '',
  },
  custom: {
    baseUrl: '',
    apiKey: '',
  },
};

const SUPABASE_ONBOARDING_SQL_SCRIPT = `-- Financial Management System (FMS) Supabase SQL setup (with RLS)
create extension if not exists pgcrypto;

create or replace function public.current_Financial Management System (FMS)_user_id()
returns uuid
language sql
stable
as $$
  select auth.uid()
$$;

create or replace function public.owns_Financial Management System (FMS)_row(row_user_id uuid)
returns boolean
language sql
stable
as $$
  select public.current_Financial Management System (FMS)_user_id() is not null
    and row_user_id = public.current_Financial Management System (FMS)_user_id()
$$;

create table if not exists public.transactions (
  id text primary key,
  user_id uuid not null default public.current_Financial Management System (FMS)_user_id(),
  type text not null check (type in ('income', 'expense', 'credit')),
  venture_id text,
  branch_id text,
  date date not null,
  description text not null,
  reference text,
  amount numeric(14,2) not null check (amount >= 0),
  category_account_id text,
  balance_account_id text,
  payment_method text,
  agent_id text,
  loan_id text,
  commission numeric(14,2) default 0,
  pos_type text,
  bundles integer default 0,
  duration text,
  plan text,
  customer_name text,
  customer_phone text,
  note text,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now(),
  deleted_at timestamptz
);

create table if not exists public.agents (
  id text primary key,
  user_id uuid not null default public.current_Financial Management System (FMS)_user_id(),
  name text not null,
  phone text,
  role text,
  venture_id text,
  branch_id text,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now(),
  deleted_at timestamptz
);

create table if not exists public.ventures (
  id text primary key,
  user_id uuid not null default public.current_Financial Management System (FMS)_user_id(),
  name text not null,
  color text,
  description text,
  branches text,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now(),
  deleted_at timestamptz
);

create table if not exists public.accounts (
  id text primary key,
  user_id uuid not null default public.current_Financial Management System (FMS)_user_id(),
  code text not null,
  name text not null,
  type text not null check (type in ('asset', 'liability', 'equity', 'income', 'expense')),
  normal text not null check (normal in ('debit', 'credit')),
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now(),
  deleted_at timestamptz
);

create table if not exists public.loans (
  id text primary key,
  user_id uuid not null default public.current_Financial Management System (FMS)_user_id(),
  name text not null,
  lender text,
  principal numeric(14,2) not null check (principal >= 0),
  venture_id text,
  start_date date,
  due_date date,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now(),
  deleted_at timestamptz
);

create or replace function public.set_updated_at()
returns trigger as $$
begin
  new.updated_at = now();
  return new;
end;
$$ language plpgsql;

drop trigger if exists trg_transactions_updated_at on public.transactions;
create trigger trg_transactions_updated_at before update on public.transactions for each row execute procedure public.set_updated_at();
drop trigger if exists trg_agents_updated_at on public.agents;
create trigger trg_agents_updated_at before update on public.agents for each row execute procedure public.set_updated_at();
drop trigger if exists trg_ventures_updated_at on public.ventures;
create trigger trg_ventures_updated_at before update on public.ventures for each row execute procedure public.set_updated_at();
drop trigger if exists trg_accounts_updated_at on public.accounts;
create trigger trg_accounts_updated_at before update on public.accounts for each row execute procedure public.set_updated_at();
drop trigger if exists trg_loans_updated_at on public.loans;
create trigger trg_loans_updated_at before update on public.loans for each row execute procedure public.set_updated_at();

alter table public.transactions enable row level security;
alter table public.agents enable row level security;
alter table public.ventures enable row level security;
alter table public.accounts enable row level security;
alter table public.loans enable row level security;

drop policy if exists own_transactions_select on public.transactions;
drop policy if exists own_transactions_insert on public.transactions;
drop policy if exists own_transactions_update on public.transactions;
drop policy if exists own_transactions_delete on public.transactions;
create policy own_transactions_select on public.transactions for select using (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_transactions_insert on public.transactions for insert with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_transactions_update on public.transactions for update using (public.owns_Financial Management System (FMS)_row(user_id)) with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_transactions_delete on public.transactions for delete using (public.owns_Financial Management System (FMS)_row(user_id));

drop policy if exists own_agents_select on public.agents;
drop policy if exists own_agents_insert on public.agents;
drop policy if exists own_agents_update on public.agents;
drop policy if exists own_agents_delete on public.agents;
create policy own_agents_select on public.agents for select using (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_agents_insert on public.agents for insert with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_agents_update on public.agents for update using (public.owns_Financial Management System (FMS)_row(user_id)) with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_agents_delete on public.agents for delete using (public.owns_Financial Management System (FMS)_row(user_id));

drop policy if exists own_ventures_select on public.ventures;
drop policy if exists own_ventures_insert on public.ventures;
drop policy if exists own_ventures_update on public.ventures;
drop policy if exists own_ventures_delete on public.ventures;
create policy own_ventures_select on public.ventures for select using (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_ventures_insert on public.ventures for insert with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_ventures_update on public.ventures for update using (public.owns_Financial Management System (FMS)_row(user_id)) with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_ventures_delete on public.ventures for delete using (public.owns_Financial Management System (FMS)_row(user_id));

drop policy if exists own_accounts_select on public.accounts;
drop policy if exists own_accounts_insert on public.accounts;
drop policy if exists own_accounts_update on public.accounts;
drop policy if exists own_accounts_delete on public.accounts;
create policy own_accounts_select on public.accounts for select using (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_accounts_insert on public.accounts for insert with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_accounts_update on public.accounts for update using (public.owns_Financial Management System (FMS)_row(user_id)) with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_accounts_delete on public.accounts for delete using (public.owns_Financial Management System (FMS)_row(user_id));

drop policy if exists own_loans_select on public.loans;
drop policy if exists own_loans_insert on public.loans;
drop policy if exists own_loans_update on public.loans;
drop policy if exists own_loans_delete on public.loans;
create policy own_loans_select on public.loans for select using (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_loans_insert on public.loans for insert with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_loans_update on public.loans for update using (public.owns_Financial Management System (FMS)_row(user_id)) with check (public.owns_Financial Management System (FMS)_row(user_id));
create policy own_loans_delete on public.loans for delete using (public.owns_Financial Management System (FMS)_row(user_id));`;

const WELCOME_FEATURE_SCENES = [
  {
    title: 'Welcome to Financial Management System (FMS)',
    subtitle: 'Financial control for modern businesses',
    narration: 'Welcome to Financial Management System (FMS). Let us walk you through the key features that help you run your business finances with confidence.',
    gradient: 'linear-gradient(135deg,#112546 0%,#16386f 100%)',
  },
  {
    title: 'Smart Dashboard',
    subtitle: 'See revenue, expenses, and cash position instantly',
    narration: 'Your dashboard gives a real-time financial snapshot so you always know where your business stands.',
    gradient: 'linear-gradient(135deg,#1b2f4e 0%,#2a4a7f 100%)',
  },
  {
    title: 'Accurate Transaction Tracking',
    subtitle: 'Capture income and expense flows with audit-ready history',
    narration: 'Track every income and expense transaction with reliable double-entry accounting and complete records.',
    gradient: 'linear-gradient(135deg,#20304a 0%,#365f8a 100%)',
  },
  {
    title: 'Professional Reports',
    subtitle: 'Profit & Loss, Balance Sheet, Trial Balance, and Cash Flow',
    narration: 'Generate professional financial reports including profit and loss, balance sheet, trial balance, and cash flow.',
    gradient: 'linear-gradient(135deg,#1d3550 0%,#2f5c87 100%)',
  },
  {
    title: 'Offline First, Cloud Optional',
    subtitle: 'Work anywhere and sync when online',
    narration: 'Financial Management System (FMS) works offline by default and syncs to the cloud when internet is available, so your workflow never stops.',
    gradient: 'linear-gradient(135deg,#24314a 0%,#3b6a8f 100%)',
  },
];

function FeatureIntroPlayer() {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [narrationOn, setNarrationOn] = useState(true);
  const totalScenes = WELCOME_FEATURE_SCENES.length;
  const activeScene = WELCOME_FEATURE_SCENES[sceneIndex];

  useEffect(() => {
    if (!playing) return;

    const words = activeScene.narration.split(/\s+/).filter(Boolean).length;
    const readingDurationMs = Math.max(5200, words * 520);
    let advanceTimer = null;
    let postNarrationTimer = null;

    const advanceScene = () => {
      setSceneIndex(prev => (prev + 1) % totalScenes);
    };

    const clearTimers = () => {
      if (advanceTimer) clearTimeout(advanceTimer);
      if (postNarrationTimer) clearTimeout(postNarrationTimer);
    };

    const scheduleFallbackAdvance = () => {
      advanceTimer = setTimeout(advanceScene, readingDurationMs);
    };

    if (narrationOn && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(activeScene.narration);
      utterance.rate = 0.88;
      utterance.pitch = 1;
      utterance.volume = 0.95;
      utterance.onend = () => {
        if (advanceTimer) clearTimeout(advanceTimer);
        postNarrationTimer = setTimeout(advanceScene, 900);
      };
      utterance.onerror = scheduleFallbackAdvance;
      scheduleFallbackAdvance();
      window.speechSynthesis.speak(utterance);

      return () => {
        clearTimers();
        window.speechSynthesis.cancel();
      };
    }

    scheduleFallbackAdvance();
    return () => clearTimers();
  }, [activeScene.narration, narrationOn, playing, totalScenes]);

  return (
    <div style={{width:'100%',aspectRatio:'16/9',border:'2px solid rgba(201,168,76,.3)',borderRadius:12,marginBottom:24,position:'relative',overflow:'hidden',background:activeScene.gradient,transition:'background 420ms ease'}}>
      <div style={{position:'absolute',inset:0,background:'radial-gradient(circle at 18% 18%,rgba(255,255,255,.14) 0%,rgba(255,255,255,0) 45%)'}} />
      <div style={{position:'relative',height:'100%',display:'grid',gridTemplateRows:'1fr auto',padding:24}}>
        <div style={{display:'grid',alignContent:'center',gap:10,maxWidth:520}}>
          <div style={{fontSize:11,letterSpacing:1.3,textTransform:'uppercase',color:'rgba(255,255,255,.78)',fontFamily:'IBM Plex Mono,monospace'}}>Feature Tour</div>
          <div style={{fontFamily:'Playfair Display,serif',fontSize:31,lineHeight:1.15,color:'#f6e4b3'}}>{activeScene.title}</div>
          <div style={{fontSize:14,color:'rgba(235,241,255,.92)',lineHeight:1.55}}>{activeScene.subtitle}</div>
        </div>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}>
          <div style={{display:'flex',gap:8,alignItems:'center',flex:1}}>
            {WELCOME_FEATURE_SCENES.map((scene, index) => (
              <button
                key={scene.title}
                onClick={() => setSceneIndex(index)}
                aria-label={`Go to ${scene.title}`}
                style={{height:6,flex:1,border:'none',borderRadius:999,background:index === sceneIndex ? 'var(--gold)' : 'rgba(255,255,255,.24)',cursor:'pointer'}}
              />
            ))}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn bg" onClick={() => setNarrationOn(prev => !prev)}>{narrationOn ? 'Narration On' : 'Narration Off'}</button>
            <button className="btn bp" onClick={() => setPlaying(prev => !prev)}>{playing ? 'Pause' : 'Play'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const sanitizeSyncDeletions = value => ({
  transactions: Array.isArray(value?.transactions) ? value.transactions : [],
  agents: Array.isArray(value?.agents) ? value.agents : [],
  ventures: Array.isArray(value?.ventures) ? value.ventures : [],
  accounts: Array.isArray(value?.accounts) ? value.accounts : [],
  loans: Array.isArray(value?.loans) ? value.loans : [],
});

const getRowSyncTimestamp = row => Math.max(
  parseIsoMs(row?.deletedAt),
  parseIsoMs(row?.updatedAt),
  parseIsoMs(row?.createdAt)
);

const stripSyncMeta = row => {
  if (!row || typeof row !== 'object') return row;
  const { createdAt, updatedAt, deletedAt, ...rest } = row;
  return rest;
};

function normalizeStoredRow(row, previousRow, nowIso) {
  if (!row || !row.id) return row;
  const createdAt = row.createdAt || previousRow?.createdAt || nowIso;
  const rowChanged = !previousRow || JSON.stringify(stripSyncMeta(row)) !== JSON.stringify(stripSyncMeta(previousRow));
  const deletedAt = row.deletedAt || '';
  const updatedAt = row.updatedAt || (deletedAt ? deletedAt : rowChanged ? nowIso : previousRow?.updatedAt || createdAt);
  return {
    ...row,
    createdAt,
    updatedAt,
    deletedAt,
  };
}

function normalizeStoredRows(nextRows, prevRows = []) {
  const prevMap = new Map((prevRows || []).filter(row => row?.id).map(row => [row.id, row]));
  const nowIso = new Date().toISOString();
  return (Array.isArray(nextRows) ? nextRows : [])
    .filter(row => row?.id)
    .map(row => normalizeStoredRow(row, prevMap.get(row.id), nowIso));
}

function resolveSyncRows(localRows, localDeletedRows, remoteRows) {
  const merged = new Map();
  [...(localRows || []), ...(localDeletedRows || []), ...(remoteRows || [])].forEach(row => {
    if (!row?.id) return;
    const existing = merged.get(row.id);
    if (!existing || getRowSyncTimestamp(row) >= getRowSyncTimestamp(existing)) {
      merged.set(row.id, row);
    }
  });

  const active = [];
  const deleted = [];
  merged.forEach(row => {
    if (row.deletedAt) {
      deleted.push(row);
      return;
    }
    active.push(row);
  });

  return { active, deleted };
}

function normalizeIdentifier(value) {
  return String(value || '').trim().toLowerCase();
}

function usernameFromEmail(email) {
  const normalized = normalizeIdentifier(email);
  const at = normalized.indexOf('@');
  return at > 0 ? normalized.slice(0, at) : '';
}

function createOfflineAuthRecord(email, userId = '') {
  const normalizedEmail = normalizeIdentifier(email);
  return {
    email: normalizedEmail,
    username: usernameFromEmail(normalizedEmail),
    userId,
    verifiedAt: new Date().toISOString(),
  };
}

function isLocalPlaceholderEmail(email) {
  const normalized = normalizeIdentifier(email);
  return normalized.endsWith('@local');
}

function resolveIdentifierToEmail(identifier, offlineRecord, profile, { allowLocalPlaceholder = false } = {}) {
  const normalized = normalizeIdentifier(identifier);
  if (!normalized) return '';
  if (normalized.includes('@')) {
    if (!allowLocalPlaceholder && isLocalPlaceholderEmail(normalized)) return '';
    return normalized;
  }

  const profileEmail = normalizeIdentifier(profile?.email || '');
  const profileUsername = usernameFromEmail(profileEmail);
  if (profileUsername && profileUsername === normalized) {
    if (!allowLocalPlaceholder && isLocalPlaceholderEmail(profileEmail)) return '';
    return profileEmail;
  }

  const offlineEmail = normalizeIdentifier(offlineRecord?.email || '');
  const offlineUsername = normalizeIdentifier(offlineRecord?.username || usernameFromEmail(offlineEmail));
  if (offlineUsername && offlineUsername === normalized) {
    if (!allowLocalPlaceholder && isLocalPlaceholderEmail(offlineEmail)) return '';
    return offlineEmail;
  }

  return '';
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   STORAGE (Updated for browser localStorage)
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
async function load(key, fb) {
  try { 
    const r = localStorage.getItem(key);
    return r ? JSON.parse(r) : fb;
  } catch { 
    return fb; 
  }
}
async function save(key, val) {
  try { 
    localStorage.setItem(key, JSON.stringify(val));
  } catch (e) {
    console.error('Storage error:', e);
  }
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   DOUBLE-ENTRY ACCOUNTING ENGINE
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function getBalances(txs, accounts) {
  const b = {};
  const defaultPayableAccountId = accounts.find(a => a.code === '2100')?.id || accounts.find(a => a.type === 'liability')?.id || '';
  const defaultAssetAccountId = accounts.find(a => a.type === 'asset')?.id || '';
  accounts.forEach(a => { b[a.id] = { dr:0, cr:0 }; });
  txs.forEach(t => {
    const effectiveBalanceAccountId = b[t.balanceAccountId]
      ? t.balanceAccountId
      : (resolvePaymentMethodAccountId(accounts, t.paymentMethod) || defaultAssetAccountId);

    if (!b[effectiveBalanceAccountId]) return;

    if (isHotspotCreditIssueTx(t)) {
      // Credit issuance creates a receivable and a matching liability until cash is collected.
      const liabilityAccountId = b[t.categoryAccountId] ? t.categoryAccountId : defaultPayableAccountId;
      if (!liabilityAccountId) return;
      if (!b[liabilityAccountId]) return;
      b[effectiveBalanceAccountId].dr += t.amount;   // DR Accounts Receivable
      b[liabilityAccountId].cr += t.amount;   // CR Liability
      return;
    }

    if (!b[t.categoryAccountId]) return;
    if (t.type === 'income') {
      b[effectiveBalanceAccountId].dr += t.amount;   // DR asset
      b[t.categoryAccountId].cr += t.amount;  // CR income
    } else if (t.type === 'expense') {
      b[t.categoryAccountId].dr += t.amount;  // DR expense
      b[effectiveBalanceAccountId].cr  += t.amount;  // CR asset
    }
  });
  return b;
}

function getNetBalance(account, balances) {
  const b = balances[account.id] || { dr:0, cr:0 };
  return account.normal === 'debit' ? b.dr - b.cr : b.cr - b.dr;
}

function isLoanPaymentExpenseAccount(accounts, accountId) {
  const acc = (accounts || []).find(a => a.id === accountId);
  if (!acc) return false;
  return acc.type === 'expense' && (
    acc.code === '5800' || acc.code === '5900' ||
    /loan\s*payments?/i.test(acc.name) ||
    /short\s*term\s*loan\s*payments?/i.test(acc.name)
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   STYLES
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#060a1a;--s1:#0b1228;--s2:#101932;--s3:#162444;
  --gold:#c9a84c;--gold-l:#e4c76c;--gold-d:rgba(201,168,76,.14);
  --txt:#d0daf0;--tm:#6070a0;--tb:#f0f4ff;
  --ok:#2dd4a0;--err:#f05c5c;--info:#60a5fa;
  --brd:rgba(255,255,255,.07);--brdg:rgba(201,168,76,.28);
  --fam:'DM Sans',sans-serif;--mono:'IBM Plex Mono',monospace;--serif:'Playfair Display',serif;
}
body{background:var(--bg);color:var(--txt);font-family:var(--fam);font-size:14px}
.app{display:flex;height:100vh;overflow:hidden}

/* SIDEBAR */
.sb{width:220px;min-width:220px;background:var(--s1);border-right:1px solid var(--brd);display:flex;flex-direction:column;transition:width 0.2s}
.sb.collapsed{width:60px;min-width:60px}
.sb.collapsed .sb-logo h1,.sb.collapsed .sb-logo p,.sb.collapsed .nav-sec,.sb.collapsed .ni-label,.sb.collapsed .sb-foot>div{display:none}
.sb.collapsed .ni{justify-content:center;padding:8px 0}
.sb-logo{padding:22px 20px 18px;border-bottom:1px solid var(--brd);display:flex;justify-content:space-between;align-items:center}
.sb-logo-text{display:flex;flex-direction:column}
.sb-logo-text h1{font-family:var(--serif);font-size:19px;color:var(--gold);letter-spacing:.5px}
.sb-logo-text p{font-size:10px;color:var(--tm);margin-top:3px;letter-spacing:1.2px;text-transform:uppercase}
.sb-toggle{background:transparent;border:none;color:var(--tm);cursor:pointer;font-size:16px;padding:4px 8px;transition:color .15s}
.sb-toggle:hover{color:var(--gold)}

.sb-nav{flex:1;padding:10px 0;overflow-y:auto}
.nav-sec{padding:10px 18px 4px;font-size:9.5px;color:var(--tm);letter-spacing:1.5px;text-transform:uppercase}
.ni{display:flex;align-items:center;gap:10px;padding:8px 20px;cursor:pointer;font-size:13px;color:var(--tm);border-left:2px solid transparent;transition:all .15s}
.ni:hover{color:var(--txt);background:rgba(255,255,255,.04)}
.ni.on{color:var(--gold);border-left-color:var(--gold);background:var(--gold-d)}
.ni-ico{width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center}
.ni-ico svg{width:16px;height:16px;stroke:currentColor;stroke-width:1.8;fill:none;stroke-linecap:round;stroke-linejoin:round}
.sb-foot{padding:14px 18px;border-top:1px solid var(--brd);font-size:11px;color:var(--tm)}

/* MAIN */
.main{flex:1;display:flex;flex-direction:column;overflow:hidden}
.topbar{height:56px;display:flex;align-items:center;justify-content:space-between;padding:0 28px;border-bottom:1px solid var(--brd);background:var(--s1)}
.topbar h2{font-family:var(--serif);font-size:20px;color:var(--tb)}
.tb-acts{display:flex;gap:10px;align-items:center}
.wm{display:flex;align-items:center;gap:6px;padding:8px 20px;border-bottom:1px solid var(--brd);background:rgba(11,18,40,.92);position:relative;z-index:5}
.wm-btn{background:transparent;border:1px solid transparent;color:var(--tm);font-size:12px;font-family:var(--fam);padding:7px 10px;border-radius:8px;cursor:pointer;transition:all .15s}
.wm-btn:hover,.wm-btn.on{color:var(--tb);background:rgba(255,255,255,.05);border-color:rgba(255,255,255,.08)}
.wm-menu{position:absolute;top:calc(100% + 8px);left:0;min-width:220px;background:var(--s2);border:1px solid var(--brd);border-radius:10px;padding:8px;box-shadow:0 18px 38px rgba(0,0,0,.35);display:grid;gap:4px}
.wm-item{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;background:transparent;border:none;color:var(--txt);font-size:12px;font-family:var(--fam);padding:8px 10px;border-radius:8px;cursor:pointer;text-align:left}
.wm-item:hover{background:rgba(255,255,255,.05)}
.wm-item span:last-child{color:var(--tm);font-size:10px;text-transform:uppercase;letter-spacing:.8px}
.wm-sep{height:1px;background:var(--brd);margin:4px 2px}
.content{flex:1;overflow-y:auto;padding:24px 28px}

/* CARDS */
.card{background:var(--s2);border:1px solid var(--brd);border-radius:10px;padding:20px}
.ct{font-size:10.5px;color:var(--tm);text-transform:uppercase;letter-spacing:1px;margin-bottom:10px}

/* KPI */
.kpi-g{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px}
.kpi{background:var(--s2);border:1px solid var(--brd);border-radius:10px;padding:18px 20px;position:relative;overflow:hidden}
.kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--acc,var(--gold))}
.kpi-l{font-size:10px;color:var(--tm);text-transform:uppercase;letter-spacing:1px;margin-bottom:8px}
.kpi-v{font-family:var(--mono);font-size:20px;font-weight:500;color:var(--tb);word-break:break-all}
.kpi-s{font-size:11px;color:var(--tm);margin-top:5px}

/* CHARTS */
.cg2{display:grid;grid-template-columns:2fr 1fr;gap:14px;margin-bottom:22px}

/* TABLE */
.tw{overflow-x:auto}
table{width:100%;border-collapse:collapse;font-size:13px}
th{text-align:left;padding:9px 14px;font-size:10px;color:var(--tm);text-transform:uppercase;letter-spacing:.9px;border-bottom:1px solid var(--brd);font-weight:500}
td{padding:10px 14px;border-bottom:1px solid var(--brd);color:var(--txt)}
tr:hover td{background:rgba(255,255,255,.02)}
.mono{font-family:var(--mono)}
.amt-p{color:var(--ok);font-family:var(--mono)}
.amt-n{color:var(--err);font-family:var(--mono)}
.amt-g{color:var(--gold);font-family:var(--mono)}

/* BADGES */
.badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.4px}
.bi{background:rgba(45,212,160,.15);color:var(--ok)}
.be{background:rgba(240,92,92,.15);color:var(--err)}
.bCr{background:rgba(96,165,250,.15);color:var(--info)}
.bA{background:rgba(96,165,250,.15);color:var(--info)}
.bL{background:rgba(240,92,92,.12);color:#f87171}
.bEq{background:rgba(167,139,250,.15);color:#a78bfa}
.bIn{background:rgba(45,212,160,.12);color:#34d399}
.bEx{background:rgba(251,146,60,.15);color:#fb923c}

/* BUTTONS */
.btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:6px;border:none;font-size:13px;font-family:var(--fam);cursor:pointer;font-weight:500;transition:all .15s}
.bp{background:var(--gold);color:#080c1a}
.bp:hover{background:var(--gold-l)}
.bg{background:transparent;color:var(--tm);border:1px solid var(--brd)}
.bg:hover{color:var(--txt);border-color:rgba(255,255,255,.18)}
.bdr{background:rgba(240,92,92,.14);color:var(--err);border:1px solid rgba(240,92,92,.3)}
.bdr:hover{background:rgba(240,92,92,.22)}
.bs{padding:5px 12px;font-size:12px}
.bi-btn{background:rgba(201,168,76,.14);color:var(--gold);border:1px solid var(--brdg)}
.bi-btn:hover{background:var(--gold-d)}

/* FORMS */
.fg{display:flex;flex-direction:column;gap:5px}
.fg label{font-size:10.5px;color:var(--tm);text-transform:uppercase;letter-spacing:.8px}
input,select,textarea{background:var(--s3);border:1px solid var(--brd);color:var(--txt);border-radius:6px;padding:8px 11px;font-size:13px;font-family:var(--fam);outline:none;transition:border-color .15s;width:100%}
input:focus,select:focus,textarea:focus{border-color:var(--brdg)}
select option{background:var(--s2)}
.fgrid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.full{grid-column:1/-1}

/* TYPE TOGGLE */
.tt{display:flex;gap:8px;margin-bottom:14px}
.ttb{flex:1;padding:9px;border:1px solid var(--brd);background:transparent;border-radius:6px;cursor:pointer;font-size:13px;font-family:var(--fam);color:var(--tm);transition:all .15s}
.ttb.ti.on{background:rgba(45,212,160,.15);color:var(--ok);border-color:var(--ok)}
.ttb.te.on{background:rgba(240,92,92,.15);color:var(--err);border-color:var(--err)}

/* MODAL */
.ov{position:fixed;inset:0;background:rgba(0,0,0,.72);display:flex;align-items:center;justify-content:center;z-index:3000;backdrop-filter:blur(4px);pointer-events:auto}
.modal{background:var(--s2);border:1px solid var(--brdg);border-radius:12px;padding:26px;width:560px;max-width:95vw;max-height:90vh;overflow-y:auto;pointer-events:auto;position:relative;z-index:1}
.modal *{pointer-events:auto}
.modal-t{font-family:var(--serif);font-size:20px;color:var(--tb);margin-bottom:20px;padding-bottom:14px;border-bottom:1px solid var(--brd)}
.modal-a{display:flex;justify-content:flex-end;gap:10px;margin-top:18px;padding-top:14px;border-top:1px solid var(--brd)}
.print-preview-overlay{position:fixed;inset:0;background:rgba(4,8,18,.8);backdrop-filter:blur(3px);z-index:3200;display:flex;align-items:center;justify-content:center;padding:24px}
.print-preview-shell{width:min(980px,96vw);max-height:90vh;display:grid;grid-template-rows:auto 1fr auto;background:var(--s2);border:1px solid var(--brdg);border-radius:18px;box-shadow:0 24px 52px rgba(0,0,0,.45);overflow:hidden}
.print-preview-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:18px 22px;border-bottom:1px solid var(--brd);background:var(--s1)}
.print-preview-paper{padding:22px;background:var(--s2);overflow:auto}
.print-preview-card{background:#fff;color:#111;border-radius:14px;padding:28px;border:1px solid #d8dce5;box-shadow:0 12px 26px rgba(0,0,0,.18)}
.print-preview-meta{display:grid;gap:4px;margin-bottom:18px;padding-bottom:14px;border-bottom:1px solid #d8dce5}
.print-preview-actions{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid var(--brd);background:var(--s1)}

/* Keep print preview in a paper-style light theme regardless of app theme. */
.print-preview-card{
  --bg:#fff;
  --s1:#fff;
  --s2:#fff;
  --s3:#fff;
  --gold:#1f2937;
  --gold-d:#f4f6fa;
  --txt:#111827;
  --tm:#4b5563;
  --tb:#0f172a;
  --ok:#0f766e;
  --err:#b91c1c;
  --info:#1d4ed8;
  --brd:#d1d5db;
  --brdg:#9ca3af;
}
.print-preview-card .card,
.print-preview-card .kpi,
.print-preview-card .vc,
.print-preview-card .jl-item{background:#fff;border-color:#d1d5db;box-shadow:none}
.print-preview-card th,
.print-preview-card td,
.print-preview-card .kpi-l,
.print-preview-card .kpi-s,
.print-preview-card .vc-d,
.print-preview-card .jl-ref,
.print-preview-card .rpt-zero td{color:#4b5563}
.print-preview-card .tab{color:#374151}
.print-preview-card .tab.on{color:#0f172a;border-bottom-color:#0f172a}
.print-preview-card .rpt-s td{background:#eef2f7;color:#1f2937}
.print-preview-card .rpt-grand td{color:#0f172a;border-top-color:#111827}
.print-preview-card tr:hover td{background:#f9fafb}

/* MISC */
.sec-h{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
.sec-t{font-family:var(--serif);font-size:16px;color:var(--tb)}
.empty{text-align:center;padding:50px 20px;color:var(--tm)}
.empty-ico{font-size:36px;margin-bottom:10px}
.tabs{display:flex;gap:2px;margin-bottom:18px;border-bottom:1px solid var(--brd)}
.tab{padding:9px 16px;font-size:13px;cursor:pointer;color:var(--tm);border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .15s}
.tab.on{color:var(--gold);border-bottom-color:var(--gold)}
.tab:hover{color:var(--txt)}
.flbar{display:flex;gap:12px;margin-bottom:18px;flex-wrap:wrap;align-items:flex-end}
.flbar .fg{flex:1;min-width:130px}
.vc-g{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.vc{background:var(--s2);border:1px solid var(--brd);border-radius:10px;padding:18px;border-left:4px solid var(--gold)}
.vc-n{font-family:var(--serif);font-size:15px;color:var(--tb);margin-bottom:3px}
.vc-d{font-size:11px;color:var(--tm);margin-bottom:14px}
.vc-s{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.vc-sv{font-family:var(--mono);font-size:12px;font-weight:500;text-align:center}
.vc-sl{font-size:10px;color:var(--tm);text-align:center;margin-top:2px}

/* REPORT */
.rpt-s td{background:var(--gold-d);color:var(--gold);font-size:11.5px;letter-spacing:.6px;text-transform:uppercase;padding:7px 14px;font-weight:600}
.rpt-r td:last-child,.rpt-t th:last-child{text-align:right;font-family:var(--mono)}
.rpt-sub td{padding-left:30px}
.rpt-tot td{border-top:1px solid var(--brdg);font-weight:600;color:var(--tb)}
.rpt-grand td{border-top:2px solid var(--gold);color:var(--gold);font-weight:700;font-size:14px}
.rpt-r td:last-child{font-family:var(--mono)}
.rpt-zero td{color:var(--tm)}

/* LEDGER */
.jl-item{background:var(--s2);border:1px solid var(--brd);border-radius:8px;padding:14px;margin-bottom:10px}
.jl-hd{display:flex;justify-content:space-between;margin-bottom:10px;align-items:center}
.jl-ref{font-size:11px;color:var(--tm)}
.jl-entry{display:grid;grid-template-columns:1fr 120px 120px;gap:8px;padding:5px 0;border-bottom:1px solid var(--brd);font-size:12px}
.jl-entry:last-child{border-bottom:none}
.jl-acc{color:var(--txt)}
.jl-dr{font-family:var(--mono);color:var(--ok);text-align:right}
.jl-cr{font-family:var(--mono);color:var(--err);text-align:right}

/* SCROLLBAR */
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:rgba(255,255,255,.1);border-radius:3px}

@keyframes spin{
  from{transform:rotate(0deg)}
  to{transform:rotate(360deg)}
}

/* PRINT */
@media print{
  :root{--bg:#fff;--s1:#fff;--s2:#fff;--s3:#fff;--gold:#222;--gold-d:#f2f2f2;--txt:#111;--tm:#555;--tb:#111;--ok:#111;--err:#111;--info:#111;--brd:#d7d7d7;--brdg:#bdbdbd}
  body{background:#fff!important;color:#000!important}
  body *{visibility:hidden!important}
  .print-preview-shell,.print-preview-shell *{visibility:visible!important}
  .print-preview-overlay{position:static!important;display:block!important;background:#fff!important;backdrop-filter:none!important;padding:0!important}
  .print-preview-shell{width:auto!important;max-height:none!important;display:block!important;border:none!important;border-radius:0!important;box-shadow:none!important;background:#fff!important}
  .print-preview-head,.print-preview-actions,.no-print,.sb,.wm,.topbar,.tb-acts,.btn,.flbar{display:none!important}
  .print-preview-paper{padding:0!important;overflow:visible!important;background:#fff!important}
  .print-preview-card{border:none!important;border-radius:0!important;box-shadow:none!important;padding:0!important;background:#fff!important;color:#000!important}
  .main,.content{overflow:visible!important;padding:0!important}
  .card{border:1px solid #ddd;break-inside:avoid}
}
`;

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   UI PRIMITIVES
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function KPI({ label, value, sub, acc }) {
  return (
    <div className="kpi" style={{'--acc':acc||'var(--gold)'}}>
      <div className="kpi-l">{label}</div>
      <div className="kpi-v" style={{color:acc||'var(--tb)'}}>{value}</div>
      {sub && <div className="kpi-s">{sub}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="ov" onMouseDown={e=>{ if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={wide?{width:720}:{}} onMouseDown={e=>e.stopPropagation()} onMouseUp={e=>e.stopPropagation()} onClick={e=>e.stopPropagation()}>
        <div className="modal-t">{title}</div>
        {children}
      </div>
    </div>
  );
}

function AccBadge({ type }) {
  const map = { asset:'bA', liability:'bL', equity:'bEq', income:'bIn', expense:'bEx' };
  return <span className={`badge ${map[type]||''}`}>{type}</span>;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:'#0b1228',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,padding:'10px 14px',fontSize:12}}>
      {label && <div style={{color:'#6070a0',marginBottom:6}}>{label}</div>}
      {payload.map((p,i) => (
        <div key={i} style={{color:p.color,fontFamily:'IBM Plex Mono'}}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   DASHBOARD VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function Dashboard({ txs, ventures, accounts, agents, onOpenAgents }) {
  const [venturePerfMetric, setVenturePerfMetric] = useState(() => {
    try {
      const savedMetric = sessionStorage.getItem('venturePerfMetric');
      return ['net', 'income', 'expense'].includes(savedMetric) ? savedMetric : 'net';
    } catch {
      return 'net';
    }
  });
  const [staffSnapshotRange, setStaffSnapshotRange] = useState(() => {
    try {
      const savedRange = sessionStorage.getItem('staffSnapshotRange');
      return ['day', 'week', 'month', 'year'].includes(savedRange) ? savedRange : 'month';
    } catch {
      return 'month';
    }
  });

  useEffect(() => {
    if (!['net', 'income', 'expense'].includes(venturePerfMetric)) return;
    try {
      sessionStorage.setItem('venturePerfMetric', venturePerfMetric);
    } catch {
      // Ignore session storage errors and keep runtime state only.
    }
  }, [venturePerfMetric]);

  useEffect(() => {
    if (!['day', 'week', 'month', 'year'].includes(staffSnapshotRange)) return;
    try {
      sessionStorage.setItem('staffSnapshotRange', staffSnapshotRange);
    } catch {
      // Ignore session storage errors and keep runtime state only.
    }
  }, [staffSnapshotRange]);

  const totalIncome  = useMemo(() => txs.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0), [txs]);
  const totalExpense = useMemo(() => txs.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0), [txs]);
  const net = totalIncome - totalExpense;
  const margin = totalIncome > 0 ? ((net/totalIncome)*100).toFixed(1) : '0';

  const monthly = useMemo(() => {
    const map = {};
    txs.forEach(t => {
      const m = t.date.slice(0,7);
      if (!map[m]) map[m] = { month:mLabel(m), income:0, expense:0 };
      if (isIncomeTx(t)) map[m].income += t.amount;
      if (isExpenseTx(t)) map[m].expense += t.amount;
    });
    return Object.values(map).sort((a,b)=>a.month<b.month?-1:1).slice(-7);
  }, [txs]);

  const expCat = useMemo(() => {
    const map = {};
    txs.filter(isExpenseTx).forEach(t => {
      const acc = accounts.find(a=>a.id===t.categoryAccountId);
      const n = acc ? acc.name : 'Other';
      map[n] = (map[n]||0) + t.amount;
    });
    return Object.entries(map).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,6);
  }, [txs, accounts]);

  const weekdayVentureStats = useMemo(() => {
    const days = [
      { day: 'Mon' },
      { day: 'Tue' },
      { day: 'Wed' },
      { day: 'Thu' },
      { day: 'Fri' },
      { day: 'Sat' },
      { day: 'Sun' },
    ];

    const rows = days.map(d => {
      const row = { ...d };
      ventures.forEach(v => {
        row[v.id] = 0;
      });
      return row;
    });

    const dayToIndex = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };

    txs.forEach(t => {
      const ventureExists = ventures.some(v => v.id === t.ventureId);
      if (!ventureExists || !t.date) return;

      const jsDay = new Date(`${t.date}T00:00:00`).getDay();
      const idx = dayToIndex[jsDay];
      if (idx === undefined) return;

      let value = 0;
      if (venturePerfMetric === 'net') {
        if (isIncomeTx(t)) value = t.amount;
        if (isExpenseTx(t)) value = -t.amount;
      } else if (venturePerfMetric === 'income') {
        value = isIncomeTx(t) ? t.amount : 0;
      } else if (venturePerfMetric === 'expense') {
        value = isExpenseTx(t) ? t.amount : 0;
      }

      rows[idx][t.ventureId] = (rows[idx][t.ventureId] || 0) + value;
    });

    return rows;
  }, [txs, ventures, venturePerfMetric]);

  const recent = useMemo(() => [...txs].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,8), [txs]);

  const staffSnapshotStart = useMemo(() => {
    const d = new Date();
    if (staffSnapshotRange === 'day') d.setDate(d.getDate() - 1);
    if (staffSnapshotRange === 'week') d.setDate(d.getDate() - 7);
    if (staffSnapshotRange === 'month') d.setDate(d.getDate() - 30);
    if (staffSnapshotRange === 'year') d.setDate(d.getDate() - 365);
    return d.toISOString().split('T')[0];
  }, [staffSnapshotRange]);

  const staffSnapshot = useMemo(() => {
    const hotspotTxs = txs.filter(t => t.posType === 'hotspot-agent-credit' || t.posType === 'hotspot-agent-collection');
    const outstandingMap = {};
    const periodMap = {};

    (agents || []).forEach(agent => {
      outstandingMap[agent.id] = { issued: 0, collected: 0 };
      periodMap[agent.id] = {
        id: agent.id,
        name: agent.name,
        role: agent.role || 'Agent',
        ventureId: agent.ventureId || '',
        creditIssued: 0,
        cashCollected: 0,
      };
    });

    hotspotTxs.forEach(t => {
      if (!t.agentId) return;

      if (!outstandingMap[t.agentId]) outstandingMap[t.agentId] = { issued: 0, collected: 0 };
      if (t.posType === 'hotspot-agent-credit') outstandingMap[t.agentId].issued += Number(t.amount || 0);
      if (t.posType === 'hotspot-agent-collection') outstandingMap[t.agentId].collected += Number(t.amount || 0);

      if (t.date < staffSnapshotStart) return;

      if (!periodMap[t.agentId]) {
        periodMap[t.agentId] = {
          id: t.agentId,
          name: 'Unknown',
          role: 'Agent',
          ventureId: '',
          creditIssued: 0,
          cashCollected: 0,
        };
      }

      if (t.posType === 'hotspot-agent-credit') periodMap[t.agentId].creditIssued += Number(t.amount || 0);
      if (t.posType === 'hotspot-agent-collection') periodMap[t.agentId].cashCollected += Number(t.amount || 0);
    });

    const rows = Object.values(periodMap)
      .map(row => {
        const outstanding = outstandingMap[row.id] || { issued: 0, collected: 0 };
        const totalActivity = row.creditIssued + row.cashCollected;
        return {
          ...row,
          totalActivity,
          collectionRate: row.creditIssued > 0 ? Math.min((row.cashCollected / row.creditIssued) * 100, 100) : 0,
          outstanding: Math.max(outstanding.issued * 0.90 - outstanding.collected, 0),
        };
      })
      .filter(row => row.totalActivity > 0)
      .sort((a, b) => b.totalActivity - a.totalActivity);

    return {
      rows,
      activeCount: rows.length,
      creditIssued: rows.reduce((sum, row) => sum + row.creditIssued, 0),
      cashCollected: rows.reduce((sum, row) => sum + row.cashCollected, 0),
      outstanding: rows.reduce((sum, row) => sum + row.outstanding, 0),
    };
  }, [agents, staffSnapshotStart, txs]);

  const staffSnapshotLabel = staffSnapshotRange === 'day'
    ? 'Last day'
    : staffSnapshotRange === 'week'
      ? 'Last 7 days'
      : staffSnapshotRange === 'month'
        ? 'Last 30 days'
        : 'Last 12 months';

  const topCollector = staffSnapshot.rows.reduce((best, row) => {
    if (!best || row.cashCollected > best.cashCollected) return row;
    return best;
  }, null);

  const highestOutstanding = staffSnapshot.rows.reduce((best, row) => {
    if (!best || row.outstanding > best.outstanding) return row;
    return best;
  }, null);

  const staffSnapshotChartData = useMemo(() => (
    staffSnapshot.rows.slice(0, 6).map(row => ({
      name: row.name,
      issued: row.creditIssued,
      collected: row.cashCollected,
      outstanding: row.outstanding,
    }))
  ), [staffSnapshot.rows]);

  return (
    <div>
      <div className="kpi-g">
        <KPI label="Total Revenue" value={fmt(totalIncome)} sub={`${txs.filter(isIncomeTx).length} transactions`} acc="var(--ok)" />
        <KPI label="Total Expenses" value={fmt(totalExpense)} sub={`${txs.filter(isExpenseTx).length} transactions`} acc="var(--err)" />
        <KPI label="Net Profit / Loss" value={fmt(net)} sub={net>=0?'Profitable':'Loss making'} acc={net>=0?'var(--ok)':'var(--err)'} />
        <KPI label="Profit Margin" value={`${margin}%`} sub="Revenue basis" acc="var(--gold)" />
      </div>

      <div className="cg2">
        <div className="card">
          <div className="ct">Revenue vs Expenses - Monthly Trend</div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={monthly} barGap={3}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" vertical={false}/>
              <XAxis dataKey="month" tick={{fill:'#6070a0',fontSize:11}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'#6070a0',fontSize:10}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend wrapperStyle={{fontSize:11,color:'#6070a0'}}/>
              <Bar dataKey="income" fill="#2dd4a0" name="Income" radius={[3,3,0,0]} maxBarSize={28}/>
              <Bar dataKey="expense" fill="#f05c5c" name="Expenses" radius={[3,3,0,0]} maxBarSize={28}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div className="ct">Expense Breakdown</div>
          {expCat.length > 0 ? (<>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={expCat} cx="50%" cy="50%" innerRadius={42} outerRadius={68} dataKey="value" paddingAngle={2}>
                  {expCat.map((_,i) => <Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                </Pie>
                <Tooltip formatter={v=>[fmt(v),'Amount']} contentStyle={{background:'#0b1228',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,fontSize:12}}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{marginTop:8}}>
              {expCat.map((c,i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11,padding:'3px 0',borderBottom:'1px solid var(--brd)'}}>
                  <span style={{color:'#6070a0',display:'flex',alignItems:'center',gap:6}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:PALETTE[i%PALETTE.length],display:'inline-block'}}/>
                    {c.name}
                  </span>
                  <span style={{fontFamily:'IBM Plex Mono',fontSize:10,color:'var(--txt)'}}>{fmt(c.value)}</span>
                </div>
              ))}
            </div>
          </>) : <div className="empty" style={{padding:'30px 10px'}}><div className="empty-ico">o</div><p style={{fontSize:12}}>No expense data yet</p></div>}
        </div>
      </div>

      {ventures.length > 1 && (
        <div className="card" style={{marginBottom:22}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,marginBottom:8}}>
            <div className="ct" style={{marginBottom:0}}>Venture Performance Comparison (By Day of Week)</div>
            <select
              value={venturePerfMetric}
              onChange={e => setVenturePerfMetric(e.target.value)}
              style={{
                background:'#0b1228',
                border:'1px solid var(--brd)',
                color:'var(--txt)',
                borderRadius:10,
                padding:'6px 10px',
                fontSize:12
              }}
            >
              <option value="net">Net</option>
              <option value="income">Income Only</option>
              <option value="expense">Expenses Only</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weekdayVentureStats} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" vertical={false}/>
              <XAxis dataKey="day" tick={{fill:'#6070a0',fontSize:11}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'#6070a0',fontSize:10}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend wrapperStyle={{fontSize:11,color:'#6070a0'}}/>
              {ventures.map((v, i) => (
                <Bar
                  key={v.id}
                  dataKey={v.id}
                  fill={v.color || PALETTE[i % PALETTE.length]}
                  name={v.name}
                  radius={[3,3,0,0]}
                  maxBarSize={26}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="card" style={{marginBottom:22}}>
        <div className="sec-h" style={{marginBottom:10}}>
          <div>
            <div className="sec-t">Staff & Agent Performance Snapshot</div>
            <div style={{fontSize:11,color:'var(--tm)',marginTop:4}}>{staffSnapshotLabel}</div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <select
              value={staffSnapshotRange}
              onChange={e => setStaffSnapshotRange(e.target.value)}
              style={{
                background:'#0b1228',
                border:'1px solid var(--brd)',
                color:'var(--txt)',
                borderRadius:10,
                padding:'6px 10px',
                fontSize:12,
                minWidth:110
              }}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            <button className="btn bg bs" onClick={onOpenAgents}>Open Agents</button>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))',gap:12,marginBottom:16}}>
          <div className="vc" style={{padding:14,borderLeftColor:'#60a5fa'}}>
            <div className="vc-d" style={{marginBottom:8}}>Active Staff</div>
            <div className="vc-n" style={{fontSize:24,marginBottom:4}}>{staffSnapshot.activeCount}</div>
            <div style={{fontSize:11,color:'var(--tm)'}}>Agents with credit or collection activity</div>
          </div>
          <div className="vc" style={{padding:14,borderLeftColor:'var(--gold)'}}>
            <div className="vc-d" style={{marginBottom:8}}>Credit Issued</div>
            <div className="vc-n" style={{fontSize:20,marginBottom:4,color:'var(--gold)'}}>{fmt(staffSnapshot.creditIssued)}</div>
            <div style={{fontSize:11,color:'var(--tm)'}}>Voucher value assigned in this period</div>
          </div>
          <div className="vc" style={{padding:14,borderLeftColor:'var(--ok)'}}>
            <div className="vc-d" style={{marginBottom:8}}>Cash Collected</div>
            <div className="vc-n" style={{fontSize:20,marginBottom:4,color:'var(--ok)'}}>{fmt(staffSnapshot.cashCollected)}</div>
            <div style={{fontSize:11,color:'var(--tm)'}}>Collections received from agents</div>
          </div>
          <div className="vc" style={{padding:14,borderLeftColor:'#fb923c'}}>
            <div className="vc-d" style={{marginBottom:8}}>Outstanding Credit</div>
            <div className="vc-n" style={{fontSize:20,marginBottom:4,color:'#fb923c'}}>{fmt(staffSnapshot.outstanding)}</div>
            <div style={{fontSize:11,color:'var(--tm)'}}>All unpaid hotspot credit still open</div>
          </div>
        </div>

        {staffSnapshot.rows.length > 0 && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:12,marginBottom:16}}>
            <div className="vc" style={{padding:16,borderLeftColor:'var(--ok)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:10}}>
                <div>
                  <div className="vc-d" style={{marginBottom:6}}>Best Collector</div>
                  <div className="vc-n" style={{marginBottom:4}}>{topCollector?.name || '-'}</div>
                  <div style={{fontSize:11,color:'var(--tm)'}}>{topCollector?.role || 'Agent'} - {ventures.find(v => v.id === topCollector?.ventureId)?.name || 'Unassigned'}</div>
                </div>
                <span className="badge bi">Collected</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(2, 1fr)',gap:10}}>
                <div>
                  <div className="vc-sv" style={{textAlign:'left',color:'var(--ok)'}}>{fmt(topCollector?.cashCollected || 0)}</div>
                  <div className="vc-sl" style={{textAlign:'left'}}>Collected</div>
                </div>
                <div>
                  <div className="vc-sv" style={{textAlign:'left',color:'var(--gold)'}}>{(topCollector?.collectionRate || 0).toFixed(0)}%</div>
                  <div className="vc-sl" style={{textAlign:'left'}}>Collection Rate</div>
                </div>
              </div>
            </div>

            <div className="vc" style={{padding:16,borderLeftColor:'#fb923c'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:10}}>
                <div>
                  <div className="vc-d" style={{marginBottom:6}}>Highest Outstanding</div>
                  <div className="vc-n" style={{marginBottom:4}}>{highestOutstanding?.name || '-'}</div>
                  <div style={{fontSize:11,color:'var(--tm)'}}>{highestOutstanding?.role || 'Agent'} - {ventures.find(v => v.id === highestOutstanding?.ventureId)?.name || 'Unassigned'}</div>
                </div>
                <span className="badge bEx">Outstanding</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(2, 1fr)',gap:10}}>
                <div>
                  <div className="vc-sv" style={{textAlign:'left',color:'#fb923c'}}>{fmt(highestOutstanding?.outstanding || 0)}</div>
                  <div className="vc-sl" style={{textAlign:'left'}}>Outstanding</div>
                </div>
                <div>
                  <div className="vc-sv" style={{textAlign:'left',color:'var(--gold)'}}>{fmt(highestOutstanding?.creditIssued || 0)}</div>
                  <div className="vc-sl" style={{textAlign:'left'}}>Credit Issued</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {staffSnapshotChartData.length > 0 && (
          <div className="card" style={{padding:16,marginBottom:16,background:'rgba(6,10,26,.28)'}}>
            <div className="sec-h" style={{marginBottom:10}}>
              <div className="ct" style={{marginBottom:0}}>Top Staff Activity</div>
              <div style={{fontSize:11,color:'var(--tm)'}}>Issued vs collected vs outstanding</div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={staffSnapshotChartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false}/>
                <XAxis dataKey="name" tick={{fill:'#6070a0',fontSize:11}} axisLine={false} tickLine={false} interval={0} angle={-16} textAnchor="end" height={56}/>
                <YAxis tick={{fill:'#6070a0',fontSize:10}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Legend wrapperStyle={{fontSize:11,color:'#6070a0'}}/>
                <Bar dataKey="issued" fill="#c9a84c" name="Credit Issued" radius={[4,4,0,0]} maxBarSize={24}/>
                <Bar dataKey="collected" fill="#2dd4a0" name="Cash Collected" radius={[4,4,0,0]} maxBarSize={24}/>
                <Bar dataKey="outstanding" fill="#fb923c" name="Outstanding" radius={[4,4,0,0]} maxBarSize={24}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {staffSnapshot.rows.length === 0 ? (
          <div className="empty" style={{padding:'24px 12px'}}>
            <div className="empty-ico">AS</div>
            <p>No staff or agent performance data yet. Activity will appear here after hotspot credit sales and collections are recorded.</p>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:12}}>
            {staffSnapshot.rows.slice(0, 4).map((row, index) => {
              const ventureName = ventures.find(v => v.id === row.ventureId)?.name || 'Unassigned';
              return (
                <div key={row.id} className="vc" style={{padding:16,borderLeftColor:index===0?'var(--gold)':'rgba(255,255,255,.14)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:12}}>
                    <div>
                      <div className="vc-n" style={{marginBottom:4}}>{row.name}</div>
                      <div style={{fontSize:11,color:'var(--tm)'}}>{row.role} - {ventureName}</div>
                    </div>
                    <span className="badge bA">#{index + 1}</span>
                  </div>
                  <div className="vc-s" style={{marginBottom:12}}>
                    <div><div className="vc-sv" style={{color:'var(--gold)'}}>{fmt(row.creditIssued)}</div><div className="vc-sl">Issued</div></div>
                    <div><div className="vc-sv" style={{color:'var(--ok)'}}>{fmt(row.cashCollected)}</div><div className="vc-sl">Collected</div></div>
                    <div><div className="vc-sv" style={{color:'#fb923c'}}>{fmt(row.outstanding)}</div><div className="vc-sl">Outstanding</div></div>
                  </div>
                  <div style={{marginBottom:6,display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--tm)'}}>
                    <span>Collection Rate</span>
                    <span style={{color:'var(--tb)',fontFamily:'var(--mono)'}}>{row.collectionRate.toFixed(0)}%</span>
                  </div>
                  <div style={{height:8,borderRadius:999,background:'rgba(255,255,255,.08)',overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${row.collectionRate}%`,background:'linear-gradient(90deg, var(--gold), var(--ok))'}} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <div className="sec-h"><div className="sec-t">Recent Transactions</div></div>
        {recent.length === 0 ? (
          <div className="empty"><div className="empty-ico">o</div><p>No transactions yet. Click "Add Transaction" to add your first record.</p></div>
        ) : (
          <div className="tw">
            <table>
              <thead><tr><th>Date</th><th>Description</th><th>Venture</th><th>Payment</th><th>Type</th><th>Amount</th></tr></thead>
              <tbody>{recent.map(t => {
                const v = ventures.find(x=>x.id===t.ventureId);
                return (
                  <tr key={t.id}>
                    <td className="mono" style={{fontSize:12}}>{t.date}</td>
                    <td>{t.description}</td>
                    <td><span style={{color:v?.color||'var(--gold)',fontSize:12}}>{v?.name||'-'}</span></td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{t.paymentMethod}</td>
                    <td><span className={`badge ${getTxBadgeClass(t)}`}>{getTxDisplayType(t)}</span></td>
                    <td className={getTxAmountClass(t)}>{getTxAmountPrefix(t)}{fmt(t.amount)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   TRANSACTIONS VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function Transactions({ txs, allTxs, ventures, accounts, items, filters, setFilters, onDelete, onEdit, onAdd }) {
  const totalIn  = txs.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
  const totalOut = txs.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
  const sorted = useMemo(() => [...txs].sort((a,b)=>b.date.localeCompare(a.date)), [txs]);
  const itemCategoryOptions = useMemo(() => {
    return [...new Set((items || []).map(item => String(item.itemCategory || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [items]);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:18}}>
        <KPI label="Filtered Income" value={fmt(totalIn)} acc="var(--ok)"/>
        <KPI label="Filtered Expenses" value={fmt(totalOut)} acc="var(--err)"/>
        <KPI label="Net (Filtered)" value={fmt(totalIn-totalOut)} acc={totalIn-totalOut>=0?'var(--ok)':'var(--err)'}/>
      </div>

      <div className="card">
        <div className="flbar">
          <div className="fg">
            <label>From</label>
            <input type="date" value={filters.from} onChange={e=>setFilters(f=>({...f,from:e.target.value}))}/>
          </div>
          <div className="fg">
            <label>To</label>
            <input type="date" value={filters.to} onChange={e=>setFilters(f=>({...f,to:e.target.value}))}/>
          </div>
          <div className="fg">
            <label>Venture</label>
            <select value={filters.venture} onChange={e=>setFilters(f=>({...f,venture:e.target.value}))}>
              <option value="all">All Ventures</option>
              {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Type</label>
            <select value={filters.type} onChange={e=>setFilters(f=>({...f,type:e.target.value}))}>
              <option value="all">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="credit">Credit</option>
            </select>
          </div>
          <div className="fg">
            <label>Item Category</label>
            <select value={filters.itemCategory} onChange={e=>setFilters(f=>({...f,itemCategory:e.target.value}))}>
              <option value="all">All Item Categories</option>
              <option value="uncategorized">Uncategorized</option>
              {itemCategoryOptions.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Search</label>
            <input placeholder="Description / reference..." value={filters.q} onChange={e=>setFilters(f=>({...f,q:e.target.value}))}/>
          </div>
          <div style={{display:'flex',alignItems:'flex-end'}}>
            <button className="btn bg bs" onClick={()=>setFilters({from:'',to:'',venture:'all',type:'all',itemCategory:'all',q:''})}>Clear</button>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="empty"><div className="empty-ico">o</div><p>No matching transactions</p></div>
        ) : (
          <div className="tw">
            <table>
              <thead><tr><th>Date</th><th>Description</th><th>Item</th><th>Item Category</th><th>Venture</th><th>Category</th><th>Payment</th><th>Ref</th><th>Type</th><th>Amount</th><th></th></tr></thead>
              <tbody>{sorted.map(t => {
                const v = ventures.find(x=>x.id===t.ventureId);
                const cat = accounts.find(a=>a.id===t.categoryAccountId);
                const item = (items || []).find(x => x.id === t.itemId);
                return (
                  <tr key={t.id}>
                    <td className="mono" style={{fontSize:12}}>{t.date}</td>
                    <td style={{maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.description}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{item?.name || '-'}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{item?.itemCategory || '-'}</td>
                    <td><span style={{color:v?.color||'var(--gold)',fontSize:12}}>{v?.name||'-'}</span></td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{cat?.name||'-'}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{t.paymentMethod}</td>
                    <td style={{color:'var(--tm)',fontSize:11}}>{t.reference||'-'}</td>
                    <td><span className={`badge ${getTxBadgeClass(t)}`}>{getTxDisplayType(t)}</span></td>
                    <td className={getTxAmountClass(t)}>{getTxAmountPrefix(t)}{fmt(t.amount)}</td>
                    <td>
                      <button className="btn bg bs" onClick={()=>onEdit(t)} title="Edit" style={{marginRight:6}}>Edit</button>
                      <button className="btn bdr bs" onClick={()=>onDelete(t.id)} title="Delete">Delete</button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
        <div style={{marginTop:12,fontSize:12,color:'var(--tm)',textAlign:'right'}}>
          Showing {sorted.length} of {allTxs.length} total transactions
        </div>
      </div>
    </div>
  );
}

function ItemListPage({ items, accounts, onSaveItems }) {
  const [form, setForm] = useState({
    name: '',
    itemCategory: '',
    type: 'income',
    categoryAccountId: '',
    defaultAmount: '',
    description: '',
  });
  const [editingId, setEditingId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [error, setError] = useState('');

  const incomeAccounts = (accounts || []).filter(account => account.type === 'income');
  const expenseAccounts = (accounts || []).filter(account => account.type === 'expense');
  const accountCategoryOptions = form.type === 'income' ? incomeAccounts : expenseAccounts;

  const resetForm = () => {
    setForm({
      name: '',
      itemCategory: '',
      type: 'income',
      categoryAccountId: '',
      defaultAmount: '',
      description: '',
    });
    setEditingId('');
    setError('');
  };

  const set = (key, value) => {
    setForm(prev => {
      if (key === 'type') {
        return { ...prev, type: value, categoryAccountId: '' };
      }
      return { ...prev, [key]: value };
    });
    if (error) setError('');
  };

  const handleSubmit = () => {
    const trimmedName = String(form.name || '').trim();
    const trimmedItemCategory = String(form.itemCategory || '').trim();
    if (!trimmedName) {
      setError('Item name is required.');
      return;
    }
    if (!trimmedItemCategory) {
      setError('Item category is required.');
      return;
    }
    if (!form.categoryAccountId) {
      setError('Please choose a default category account.');
      return;
    }
    if (form.defaultAmount && Number(form.defaultAmount) < 0) {
      setError('Default amount cannot be negative.');
      return;
    }

    const nextItem = {
      id: editingId || uid(),
      name: trimmedName,
      itemCategory: trimmedItemCategory,
      type: form.type,
      categoryAccountId: form.categoryAccountId,
      defaultAmount: form.defaultAmount === '' ? '' : Number(form.defaultAmount),
      description: String(form.description || '').trim(),
      updatedAt: new Date().toISOString(),
    };

    if (editingId) {
      onSaveItems(prev => prev.map(item => (item.id === editingId ? { ...item, ...nextItem } : item)));
    } else {
      onSaveItems(prev => [nextItem, ...prev]);
    }
    resetForm();
  };

  const handleEdit = item => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      itemCategory: item.itemCategory || '',
      type: item.type === 'expense' ? 'expense' : 'income',
      categoryAccountId: item.categoryAccountId || '',
      defaultAmount: item.defaultAmount === '' || item.defaultAmount === null || item.defaultAmount === undefined ? '' : String(item.defaultAmount),
      description: item.description || '',
    });
    setError('');
  };

  const handleDelete = id => {
    onSaveItems(prev => prev.filter(item => item.id !== id));
    if (editingId === id) resetForm();
  };

  const categoryOptions = [...new Set((items || []).map(item => String(item.itemCategory || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const visibleItems = (items || []).filter(item => categoryFilter === 'all' || String(item.itemCategory || '').trim() === categoryFilter);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:18}}>
        <KPI label="Total Items" value={items.length} acc="var(--info)" />
        <KPI label="Income Items" value={items.filter(item => item.type === 'income').length} acc="var(--ok)" />
        <KPI label="Item Categories" value={categoryOptions.length} acc="var(--err)" />
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="sec-h"><div className="sec-t">{editingId ? 'Edit Item' : 'Create Item'}</div></div>
        <div className="fgrid">
          <div className="fg full">
            <label>Item Name *</label>
            <input placeholder="e.g. Internet bundle, Fuel, Delivery fee" value={form.name} onChange={e=>set('name',e.target.value)} />
          </div>
          <div className="fg">
            <label>Item Category *</label>
            <input placeholder="e.g. Utilities, Inventory, Services" value={form.itemCategory} onChange={e=>set('itemCategory',e.target.value)} />
          </div>
          <div className="fg">
            <label>Type *</label>
            <select value={form.type} onChange={e=>set('type',e.target.value)}>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>
          </div>
          <div className="fg">
            <label>Default Amount (UGX)</label>
            <input type="number" min="0" step="any" placeholder="Optional" value={form.defaultAmount} onChange={e=>set('defaultAmount',e.target.value)} />
          </div>
          <div className="fg full">
            <label>Default Category *</label>
            <select value={form.categoryAccountId} onChange={e=>set('categoryAccountId',e.target.value)}>
              <option value="">- Select Account -</option>
              {accountCategoryOptions.map(account => <option key={account.id} value={account.id}>{account.code} - {account.name}</option>)}
            </select>
          </div>
          <div className="fg full">
            <label>Default Description</label>
            <input placeholder="Optional description used in transaction entry" value={form.description} onChange={e=>set('description',e.target.value)} />
          </div>
        </div>
        {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
        <div style={{marginTop:12,display:'flex',gap:10,justifyContent:'flex-end'}}>
          {editingId && <button className="btn bg" onClick={resetForm}>Cancel</button>}
          <button className="btn bp" onClick={handleSubmit}>{editingId ? 'Save Item' : '+ Add Item'}</button>
        </div>
      </div>

      <div className="card">
        <div className="sec-h">
          <div className="sec-t">Saved Items</div>
          <div className="fg" style={{minWidth:180,maxWidth:240}}>
            <label>Filter by Item Category</label>
            <select value={categoryFilter} onChange={e=>setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categoryOptions.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
        {visibleItems.length === 0 ? (
          <div className="empty"><div className="empty-ico">L</div><p>No items yet. Create an item to quickly fill transaction entry fields.</p></div>
        ) : (
          <div className="tw">
            <table>
              <thead><tr><th>Name</th><th>Item Category</th><th>Type</th><th>Account Category</th><th style={{textAlign:'right'}}>Default Amount</th><th>Description</th><th></th></tr></thead>
              <tbody>
                {visibleItems.map(item => {
                  const category = accounts.find(account => account.id === item.categoryAccountId);
                  return (
                    <tr key={item.id}>
                      <td style={{fontWeight:600}}>{item.name}</td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{item.itemCategory || '-'}</td>
                      <td><span className={`badge ${item.type === 'income' ? 'bi' : 'be'}`}>{item.type}</span></td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{category ? `${category.code} - ${category.name}` : '-'}</td>
                      <td className="mono" style={{textAlign:'right'}}>{item.defaultAmount === '' || item.defaultAmount === null || item.defaultAmount === undefined ? '-' : fmt(item.defaultAmount)}</td>
                      <td style={{color:'var(--tm)',fontSize:12,maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.description || '-'}</td>
                      <td>
                        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                          <button className="btn bg bs" onClick={() => handleEdit(item)}>Edit</button>
                          <button className="btn bdr bs" onClick={() => handleDelete(item.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   VENTURES VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function Ventures({ ventures, txs, onDelete, onDeleteAll, onEdit, onSelectBranch, onUpdateBranch, onAddBranch, onAdd }) {
  const [expandedVenture, setExpandedVenture] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchNameInput, setBranchNameInput] = useState('');
  const [branchModalId, setBranchModalId] = useState(null);
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchLocation, setNewBranchLocation] = useState('');
  const [branchError, setBranchError] = useState('');

  if (ventures.length === 0) return (
    <div className="empty">
      <div className="empty-ico">V</div>
      <p>No ventures yet. Create one to start tracking.</p>
      <button className="btn bp" style={{marginTop:14}} onClick={onAdd}>+ Add Venture</button>
    </div>
  );

  const saveBranchEdit = (ventureId, branchId) => {
    if (branchNameInput.trim()) onUpdateBranch(ventureId, branchId, branchNameInput.trim());
    setEditingBranch(null);
  };

  const submitBranch = () => {
    if (!newBranchName.trim()) { setBranchError('Branch name is required.'); return; }
    setBranchError('');
    onAddBranch(branchModalId, { id: uid(), name: newBranchName.trim(), location: newBranchLocation.trim() });
    setBranchModalId(null);
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:10}}>
        <button className="btn bdr bs" onClick={()=>{ if(window.confirm('Delete all ventures? This cannot be undone.')) onDeleteAll(); }}>Delete All</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:14}}>
        {ventures.map(v => {
          const vt  = txs.filter(t=>t.ventureId===v.id);
          const inc = vt.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
          const exp = vt.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
          const net = inc - exp;
          const isExpanded = expandedVenture === v.id;
          return (
            <div key={v.id} style={{display:'flex',flexDirection:'column'}}>
              <div className="vc" style={{borderLeftColor:v.color,cursor:'pointer'}} onClick={()=>setExpandedVenture(isExpanded?null:v.id)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                  <div>
                    <div className="vc-n">{v.name}</div>
                    <div style={{fontSize:10,color:'var(--tm)',marginTop:4}}>{(v.branches||[]).length} branch{(v.branches||[]).length!==1?'es':''}</div>
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button className="btn bg bs" onClick={e=>{e.stopPropagation();onEdit(v);}} title="Edit">Edit</button>
                    <button className="btn bdr bs" onClick={e=>{e.stopPropagation();onDelete(v.id);}} title="Delete">Delete</button>
                  </div>
                </div>
                <div className="vc-d">{v.description || 'No description'}</div>
                <div className="vc-s">
                  <div><div className="vc-sv" style={{color:'var(--ok)'}}>{fmt(inc)}</div><div className="vc-sl">Income</div></div>
                  <div><div className="vc-sv" style={{color:'var(--err)'}}>{fmt(exp)}</div><div className="vc-sl">Expenses</div></div>
                  <div><div className="vc-sv" style={{color:net>=0?'var(--ok)':'var(--err)'}}>{fmt(net)}</div><div className="vc-sl">Net Profit</div></div>
                </div>
              </div>
              {isExpanded && (
                <div style={{background:'var(--s3)',border:'1px solid var(--brd)',borderTop:'none',borderBottomLeftRadius:10,borderBottomRightRadius:10,padding:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <div style={{fontSize:11,color:'var(--tm)',textTransform:'uppercase',letterSpacing:0.8,fontWeight:600}}>Branch Performance</div>
                    <button className="btn bi-btn bs" onClick={e=>{e.stopPropagation();setBranchModalId(v.id);setNewBranchName('');setNewBranchLocation('');}}>+ Branch</button>
                  </div>
                  {(v.branches||[]).length===0 && <div style={{fontSize:12,color:'var(--tm)',padding:'8px 4px'}}>No branches yet. Click "+ Branch" to add one.</div>}
                  {(v.branches||[]).map(b=>{
                    const bt = vt.filter(t=>t.branchId===b.id);
                    const binc = bt.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
                    const bexp = bt.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
                    const bnet = binc - bexp;
                    const isEditingThis = editingBranch?.ventureId===v.id && editingBranch?.branchId===b.id;
                    return (
                      <div key={b.id} style={{background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:6,padding:10,marginBottom:8,cursor:'pointer'}}
                        onClick={()=>{ if(!isEditingThis) onSelectBranch(v.id,b); }}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                          <div style={{flex:1}}>
                            {isEditingThis ? (
                              <div style={{display:'flex',gap:6,alignItems:'center'}} onClick={e=>e.stopPropagation()}>
                                <input autoFocus value={branchNameInput} onChange={e=>setBranchNameInput(e.target.value)}
                                  onKeyDown={e=>e.key==='Enter'?saveBranchEdit(v.id,b.id):e.key==='Escape'&&setEditingBranch(null)}
                                  style={{flex:1,fontSize:13,padding:'6px 8px',background:'var(--s3)',border:'1px solid var(--brdg)',color:'var(--txt)',borderRadius:4}}/>
                                <button className="btn bi-btn bs" onClick={e=>{e.stopPropagation();saveBranchEdit(v.id,b.id);}}>Save</button>
                                <button className="btn bg bs" onClick={e=>{e.stopPropagation();setEditingBranch(null);}}>Cancel</button>
                              </div>
                            ) : (
                              <div onClick={e=>{e.stopPropagation();setEditingBranch({ventureId:v.id,branchId:b.id});setBranchNameInput(b.name);}} style={{cursor:'pointer',padding:'4px 6px',borderRadius:4}}>
                                <div style={{fontSize:13,color:'var(--txt)',fontWeight:500}}>{b.name}</div>
                                {b.location && <div style={{fontSize:11,color:'var(--tm)',marginTop:2}}>{b.location}</div>}
                              </div>
                            )}
                          </div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:14,fontFamily:'IBM Plex Mono',color:bnet>=0?'var(--ok)':'var(--err)',fontWeight:600}}>{fmt(bnet)}</div>
                            <div style={{fontSize:10,color:'var(--tm)',marginTop:2}}>Net Profit</div>
                          </div>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:11}}>
                          <div style={{background:'rgba(45,212,160,.08)',padding:8,borderRadius:4,color:'var(--ok)'}}>
                            <div>Income</div><div style={{fontFamily:'IBM Plex Mono',fontSize:12,marginTop:2}}>{fmt(binc)}</div>
                          </div>
                          <div style={{background:'rgba(240,92,92,.08)',padding:8,borderRadius:4,color:'var(--err)'}}>
                            <div>Expenses</div><div style={{fontFamily:'IBM Plex Mono',fontSize:12,marginTop:2}}>{fmt(bexp)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {branchModalId && (
        <Modal title="Add New Branch" onClose={()=>setBranchModalId(null)}>
          <div className="fgrid">
            <div className="fg full"><label>Branch Name *</label><input autoFocus value={newBranchName} onChange={e=>{ setNewBranchName(e.target.value); if (branchError) setBranchError(''); }} placeholder="e.g. Kampala Branch" onKeyDown={e=>e.key==='Enter'&&submitBranch()}/></div>
            <div className="fg full"><label>Location (Optional)</label><input value={newBranchLocation} onChange={e=>{ setNewBranchLocation(e.target.value); if (branchError) setBranchError(''); }} placeholder="e.g. Kololo"/></div>
          </div>
          {branchError && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{branchError}</div>}
          <div className="modal-a">
            <button className="btn bg" onClick={()=>setBranchModalId(null)}>Cancel</button>
            <button className="btn bp" onClick={submitBranch}>Add Branch</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   CHART OF ACCOUNTS VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function Accounts({ accounts, txs, onDelete }) {
  const balances = useMemo(() => getBalances(txs, accounts), [txs, accounts]);
  const byType = ACC_TYPES.reduce((m,t) => ({...m,[t]:accounts.filter(a=>a.type===t)}), {});

  return (
    <div className="card">
      <div className="tw">
        <table>
          <thead>
            <tr>
              <th>Code</th><th>Account Name</th><th>Type</th><th>Normal Balance</th>
              <th style={{textAlign:'right'}}>Debit Total</th>
              <th style={{textAlign:'right'}}>Credit Total</th>
              <th style={{textAlign:'right'}}>Net Balance</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ACC_TYPES.map(type => (<>
              <tr key={`h-${type}`}>
                <td colSpan={8} style={{background:'var(--gold-d)',color:'var(--gold)',fontSize:10,textTransform:'uppercase',letterSpacing:1,fontWeight:600,padding:'7px 14px'}}>
                  {type.toUpperCase()}
                </td>
              </tr>
              {byType[type].map(a => {
                const b = balances[a.id]||{dr:0,cr:0};
                const net = getNetBalance(a, balances);
                const isDefault = a.id.startsWith('a') && !isNaN(a.id.slice(1));
                return (
                  <tr key={a.id}>
                    <td className="mono" style={{fontSize:12,color:'var(--tm)'}}>{a.code}</td>
                    <td style={{fontWeight:500}}>{a.name}</td>
                    <td><AccBadge type={a.type}/></td>
                    <td><span style={{fontSize:11,color:'var(--tm)',textTransform:'capitalize'}}>{a.normal}</span></td>
                    <td className="mono" style={{textAlign:'right',color:'var(--ok)',fontSize:12}}>{b.dr>0?fmt(b.dr):'-'}</td>
                    <td className="mono" style={{textAlign:'right',color:'var(--err)',fontSize:12}}>{b.cr>0?fmt(b.cr):'-'}</td>
                    <td className="mono" style={{textAlign:'right',fontSize:12,fontWeight:600,color:net>0?'var(--ok)':net<0?'var(--err)':'var(--tm)'}}>{net!==0?fmt(net):'-'}</td>
                    <td>{!isDefault&&<button className="btn bdr bs" onClick={()=>onDelete(a.id)}>x</button>}</td>
                  </tr>
                );
              })}
            </>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   REPORTS
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function PLReport({ txs, accounts }) {
  const incAccs = accounts.filter(a=>a.type==='income');
  const expAccs = accounts.filter(a=>a.type==='expense');

  const incRows = incAccs.map(a => ({
    ...a, amt: txs.filter(t=>isIncomeTx(t)&&t.categoryAccountId===a.id).reduce((s,t)=>s+t.amount,0)
  })).filter(r=>r.amt>0);

  const expRows = expAccs.map(a => ({
    ...a, amt: txs.filter(t=>isExpenseTx(t)&&t.categoryAccountId===a.id).reduce((s,t)=>s+t.amount,0)
  })).filter(r=>r.amt>0);

  const totalInc = incRows.reduce((s,r)=>s+r.amt,0);
  const totalExp = expRows.reduce((s,r)=>s+r.amt,0);
  const net = totalInc - totalExp;

  return (
    <table className="rpt-t" style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
      <thead><tr style={{borderBottom:'1px solid var(--brd)'}}><th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Account</th><th style={{padding:'8px 14px',textAlign:'right',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Amount (UGX)</th></tr></thead>
      <tbody>
        <tr className="rpt-s"><td colSpan={2}>INCOME</td></tr>
        {incRows.length===0?<tr><td colSpan={2} style={{padding:'8px 14px',color:'var(--tm)',fontSize:12}}>No income recorded</td></tr>:
          incRows.map(r=><tr key={r.id} className="rpt-sub rpt-r"><td style={{padding:'8px 14px 8px 30px'}}>{r.name}</td><td style={{padding:'8px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',color:'var(--ok)'}}>{fmt(r.amt)}</td></tr>)
        }
        <tr className="rpt-tot rpt-r"><td style={{padding:'10px 14px'}}>Total Income</td><td style={{padding:'10px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',color:'var(--ok)'}}>{fmt(totalInc)}</td></tr>

        <tr className="rpt-s"><td colSpan={2}>EXPENSES</td></tr>
        {expRows.length===0?<tr><td colSpan={2} style={{padding:'8px 14px',color:'var(--tm)',fontSize:12}}>No expenses recorded</td></tr>:
          expRows.map(r=><tr key={r.id} className="rpt-sub rpt-r"><td style={{padding:'8px 14px 8px 30px'}}>{r.name}</td><td style={{padding:'8px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',color:'var(--err)'}}>{fmt(r.amt)}</td></tr>)
        }
        <tr className="rpt-tot rpt-r"><td style={{padding:'10px 14px'}}>Total Expenses</td><td style={{padding:'10px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',color:'var(--err)'}}>{fmt(totalExp)}</td></tr>

        <tr className="rpt-grand rpt-r">
          <td style={{padding:'12px 14px'}}>NET PROFIT / (LOSS)</td>
          <td style={{padding:'12px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',color:net>=0?'var(--ok)':'var(--err)'}}>{fmt(net)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function BSReport({ txs, accounts }) {
  const balances = getBalances(txs, accounts);
  const assetAccs = accounts.filter(a=>a.type==='asset');
  const liabAccs  = accounts.filter(a=>a.type==='liability');
  const eqAccs    = accounts.filter(a=>a.type==='equity');

  const totalInc = txs.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
  const totalExp = txs.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
  const netProfit = totalInc - totalExp;

  const assetRows = assetAccs.map(a=>({...a,net:getNetBalance(a,balances)})).filter(r=>r.net!==0);
  const liabRows  = liabAccs.map(a=>({...a,net:getNetBalance(a,balances)})).filter(r=>r.net!==0);
  const eqRows    = eqAccs.map(a=>({...a,net:getNetBalance(a,balances)})).filter(r=>r.net!==0);

  const totalAssets = assetRows.reduce((s,r)=>s+r.net,0);
  const totalLiab   = liabRows.reduce((s,r)=>s+r.net,0);
  const totalEq     = eqRows.reduce((s,r)=>s+r.net,0) + netProfit;

  const Row = ({label,amt,indent,bold,color}) => (
    <tr className="rpt-r">
      <td style={{padding:`${bold?'10':'8'}px 14px ${bold?'10':'8'}px ${indent?30:14}px`,fontWeight:bold?600:400,color:bold?'var(--tb)':'var(--txt)'}}>{label}</td>
      <td style={{padding:'8px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',fontWeight:bold?600:400,color:color||'var(--txt)'}}>{fmt(amt)}</td>
    </tr>
  );

  return (
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
      <thead><tr style={{borderBottom:'1px solid var(--brd)'}}><th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Account</th><th style={{padding:'8px 14px',textAlign:'right',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Amount (UGX)</th></tr></thead>
      <tbody>
        <tr className="rpt-s"><td colSpan={2}>ASSETS</td></tr>
        {assetRows.map(r=><Row key={r.id} label={r.name} amt={r.net} indent/>)}
        {assetRows.length===0&&<tr><td colSpan={2} style={{padding:'8px 14px 8px 30px',color:'var(--tm)',fontSize:12}}>No asset balances</td></tr>}
        <tr className="rpt-tot rpt-r"><td style={{padding:'10px 14px',fontWeight:600,color:'var(--tb)',borderTop:'1px solid var(--brdg)'}}>Total Assets</td><td style={{padding:'10px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',fontWeight:600,borderTop:'1px solid var(--brdg)',color:'var(--info)'}}>{fmt(totalAssets)}</td></tr>

        <tr className="rpt-s"><td colSpan={2}>LIABILITIES</td></tr>
        {liabRows.map(r=><Row key={r.id} label={r.name} amt={r.net} indent/>)}
        {liabRows.length===0&&<tr><td colSpan={2} style={{padding:'8px 14px 8px 30px',color:'var(--tm)',fontSize:12}}>No liabilities recorded</td></tr>}
        <Row label="Total Liabilities" amt={totalLiab} bold color="var(--err)"/>

        <tr className="rpt-s"><td colSpan={2}>EQUITY</td></tr>
        {eqRows.map(r=><Row key={r.id} label={r.name} amt={r.net} indent/>)}
        <Row label="Net Profit / (Loss)" amt={netProfit} indent color={netProfit>=0?'var(--ok)':'var(--err)'}/>
        <Row label="Total Equity" amt={totalEq} bold color="var(--ok)"/>

        <tr className="rpt-grand rpt-r">
          <td style={{padding:'12px 14px',borderTop:'2px solid var(--gold)',fontWeight:700,color:'var(--gold)'}}>TOTAL LIABILITIES + EQUITY</td>
          <td style={{padding:'12px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',borderTop:'2px solid var(--gold)',fontWeight:700,color:'var(--gold)'}}>{fmt(totalLiab+totalEq)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function TBReport({ txs, accounts }) {
  const balances = getBalances(txs, accounts);
  const rows = accounts.map(a => ({ ...a, ...balances[a.id] })).filter(r=>r.dr>0||r.cr>0);
  const totDr = rows.reduce((s,r)=>s+r.dr,0);
  const totCr = rows.reduce((s,r)=>s+r.cr,0);
  const balanced = Math.abs(totDr-totCr) < 0.01;

  return (
    <div>
      <div style={{marginBottom:12,display:'flex',gap:12,alignItems:'center'}}>
        <span style={{fontSize:12,padding:'4px 12px',borderRadius:20,background:balanced?'rgba(45,212,160,.15)':'rgba(240,92,92,.15)',color:balanced?'var(--ok)':'var(--err)',fontWeight:600}}>
          {balanced?'Balanced':'Out of Balance'}
        </span>
        {!balanced&&<span style={{fontSize:11,color:'var(--err)'}}>Difference: {fmt(Math.abs(totDr-totCr))}</span>}
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead><tr style={{borderBottom:'1px solid var(--brd)'}}>
          <th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Code</th>
          <th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Account</th>
          <th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Type</th>
          <th style={{padding:'8px 14px',textAlign:'right',fontSize:10,color:'var(--ok)',textTransform:'uppercase',letterSpacing:1}}>Debit (UGX)</th>
          <th style={{padding:'8px 14px',textAlign:'right',fontSize:10,color:'var(--err)',textTransform:'uppercase',letterSpacing:1}}>Credit (UGX)</th>
        </tr></thead>
        <tbody>
          {rows.map(r=>(
            <tr key={r.id} style={{borderBottom:'1px solid var(--brd)'}}>
              <td className="mono" style={{padding:'8px 14px',fontSize:12,color:'var(--tm)'}}>{r.code}</td>
              <td style={{padding:'8px 14px'}}>{r.name}</td>
              <td style={{padding:'8px 14px'}}><AccBadge type={r.type}/></td>
              <td className="mono" style={{padding:'8px 14px',textAlign:'right',color:'var(--ok)',fontSize:12}}>{r.dr>0?fmt(r.dr):'-'}</td>
              <td className="mono" style={{padding:'8px 14px',textAlign:'right',color:'var(--err)',fontSize:12}}>{r.cr>0?fmt(r.cr):'-'}</td>
            </tr>
          ))}
          {rows.length===0&&<tr><td colSpan={5} className="empty" style={{padding:40}}>No transactions yet</td></tr>}
          <tr style={{borderTop:'2px solid var(--gold)',background:'var(--gold-d)'}}>
            <td colSpan={3} style={{padding:'10px 14px',fontWeight:700,color:'var(--gold)',fontSize:13}}>TOTALS</td>
            <td className="mono" style={{padding:'10px 14px',textAlign:'right',fontWeight:700,color:'var(--ok)'}}>{fmt(totDr)}</td>
            <td className="mono" style={{padding:'10px 14px',textAlign:'right',fontWeight:700,color:'var(--err)'}}>{fmt(totCr)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CFReport({ txs, accounts }) {
  const totalIn  = txs.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
  const totalOut = txs.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
  const net = totalIn - totalOut;

  // Group by payment method (= cash vs bank vs mobile)
  const byPM = PAYMENT_METHODS.reduce((m,pm)=>({...m,[pm]:{in:0,out:0}}),[]);
  PAYMENT_METHODS.forEach(pm=>{byPM[pm]={in:0,out:0};});
  txs.forEach(t=>{
    if(byPM[t.paymentMethod]){
      if (isIncomeTx(t)) byPM[t.paymentMethod].in += t.amount;
      if (isExpenseTx(t)) byPM[t.paymentMethod].out += t.amount;
    }
  });

  const Row = ({label,amt,indent,bold,color}) => (
    <tr>
      <td style={{padding:`${bold?10:8}px 14px`,paddingLeft:indent?30:14,fontWeight:bold?600:400,color:bold?'var(--tb)':'var(--txt)'}}>{label}</td>
      <td style={{padding:'8px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',fontWeight:bold?600:400,color:color||'var(--txt)'}}>{amt>=0?'+':''}{fmt(amt)}</td>
    </tr>
  );

  return (
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
      <thead><tr style={{borderBottom:'1px solid var(--brd)'}}>
        <th style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Activity</th>
        <th style={{padding:'8px 14px',textAlign:'right',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Amount (UGX)</th>
      </tr></thead>
      <tbody>
        <tr className="rpt-s"><td colSpan={2}>OPERATING ACTIVITIES</td></tr>
        <Row label="Cash received from customers / clients" amt={totalIn} indent color="var(--ok)"/>
        <Row label="Cash paid for operating expenses" amt={-totalOut} indent color="var(--err)"/>
        <tr style={{borderTop:'1px solid var(--brdg)'}}>
          <td style={{padding:'10px 14px',fontWeight:600,color:'var(--tb)'}}>Net Cash from Operating Activities</td>
          <td style={{padding:'10px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',fontWeight:600,color:net>=0?'var(--ok)':'var(--err)'}}>{fmt(net)}</td>
        </tr>

        <tr className="rpt-s"><td colSpan={2}>BREAKDOWN BY PAYMENT METHOD</td></tr>
        {PAYMENT_METHODS.filter(pm=>byPM[pm].in>0||byPM[pm].out>0).map(pm=>(
          <tr key={pm} style={{borderBottom:'1px solid var(--brd)'}}>
            <td style={{padding:'8px 14px 8px 30px',color:'var(--txt)'}}>{pm}</td>
            <td style={{padding:'8px 14px',textAlign:'right'}}>
              <span style={{fontFamily:'IBM Plex Mono',fontSize:12,color:'var(--ok)'}}>+{fmt(byPM[pm].in)}</span>
              <span style={{color:'var(--tm)',margin:'0 6px',fontSize:11}}>/</span>
              <span style={{fontFamily:'IBM Plex Mono',fontSize:12,color:'var(--err)'}}>-{fmt(byPM[pm].out)}</span>
            </td>
          </tr>
        ))}
        <tr className="rpt-grand">
          <td style={{padding:'12px 14px',borderTop:'2px solid var(--gold)',fontWeight:700,color:'var(--gold)'}}>NET CASH POSITION</td>
          <td style={{padding:'12px 14px',textAlign:'right',fontFamily:'IBM Plex Mono',borderTop:'2px solid var(--gold)',fontWeight:700,color:net>=0?'var(--ok)':'var(--err)'}}>{fmt(net)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function VentureComparison({ txs, ventures }) {
  const rows = ventures.map(v => {
    const vt  = txs.filter(t=>t.ventureId===v.id);
    const inc = vt.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
    const exp = vt.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
    const net = inc - exp;
    const margin = inc>0?((net/inc)*100).toFixed(1):'-';
    return { ...v, inc, exp, net, margin, count:vt.length };
  });
  const totInc = rows.reduce((s,r)=>s+r.inc,0);
  const totExp = rows.reduce((s,r)=>s+r.exp,0);

  return (
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
      <thead><tr style={{borderBottom:'1px solid var(--brd)'}}>
        {['Venture','Transactions','Revenue','Expenses','Net Profit','Margin'].map(h=>(
          <th key={h} style={{padding:'8px 14px',fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1,textAlign:h==='Venture'?'left':'right'}}>{h}</th>
        ))}
      </tr></thead>
      <tbody>
        {rows.map(r=>(
          <tr key={r.id} style={{borderBottom:'1px solid var(--brd)'}}>
            <td style={{padding:'10px 14px'}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
                <span style={{width:10,height:10,borderRadius:'50%',background:r.color,flexShrink:0}}/>
                <strong>{r.name}</strong>
              </span>
            </td>
            <td className="mono" style={{textAlign:'right',padding:'10px 14px',color:'var(--tm)'}}>{r.count}</td>
            <td className="mono" style={{textAlign:'right',padding:'10px 14px',color:'var(--ok)'}}>{fmt(r.inc)}</td>
            <td className="mono" style={{textAlign:'right',padding:'10px 14px',color:'var(--err)'}}>{fmt(r.exp)}</td>
            <td className="mono" style={{textAlign:'right',padding:'10px 14px',color:r.net>=0?'var(--ok)':'var(--err)',fontWeight:600}}>{fmt(r.net)}</td>
            <td className="mono" style={{textAlign:'right',padding:'10px 14px',color:parseFloat(r.margin)>=0?'var(--ok)':'var(--err)'}}>{r.margin}{r.margin!=='-'?'%':''}</td>
          </tr>
        ))}
        <tr style={{background:'var(--gold-d)',borderTop:'2px solid var(--gold)'}}>
          <td style={{padding:'10px 14px',fontWeight:700,color:'var(--gold)'}}>TOTALS</td>
          <td className="mono" style={{textAlign:'right',padding:'10px 14px',fontWeight:700,color:'var(--gold)'}}>{txs.length}</td>
          <td className="mono" style={{textAlign:'right',padding:'10px 14px',fontWeight:700,color:'var(--ok)'}}>{fmt(totInc)}</td>
          <td className="mono" style={{textAlign:'right',padding:'10px 14px',fontWeight:700,color:'var(--err)'}}>{fmt(totExp)}</td>
          <td className="mono" style={{textAlign:'right',padding:'10px 14px',fontWeight:700,color:totInc-totExp>=0?'var(--ok)':'var(--err)'}}>{fmt(totInc-totExp)}</td>
          <td className="mono" style={{textAlign:'right',padding:'10px 14px',fontWeight:700,color:'var(--tm)'}}>{totInc>0?((((totInc-totExp)/totInc)*100).toFixed(1))+'%':'-'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Reports({ txs, ventures, accounts, reportTab = 'pl' }) {
  const [tab, setTab] = useState(reportTab);
  const [rFrom, setRFrom] = useState(soy());
  const [rTo, setRTo] = useState(tod());
  const [rVenture, setRVenture] = useState('all');
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);

  useEffect(() => {
    setTab(reportTab);
  }, [reportTab]);

  const filtered = useMemo(() => txs.filter(t=>{
    if(rVenture!=='all'&&t.ventureId!==rVenture) return false;
    if(t.date<rFrom||t.date>rTo) return false;
    return true;
  }), [txs, rFrom, rTo, rVenture]);

  const tabs = [
    {id:'pl',label:'Profit & Loss'},
    {id:'bs',label:'Balance Sheet'},
    {id:'tb',label:'Trial Balance'},
    {id:'cf',label:'Cash Flow'},
    {id:'vc',label:'Venture Comparison'},
  ];

  const activeReport = tabs.find(item => item.id === tab) || tabs[0];
  const activeVentureName = rVenture === 'all' ? 'All Ventures' : (ventures.find(v => v.id === rVenture)?.name || 'Selected Venture');

  const renderReportContent = () => {
    if (tab === 'pl') return <PLReport txs={filtered} accounts={accounts}/>;
    if (tab === 'bs') return <BSReport txs={filtered} accounts={accounts}/>;
    if (tab === 'tb') return <TBReport txs={filtered} accounts={accounts}/>;
    if (tab === 'cf') return <CFReport txs={filtered} accounts={accounts}/>;
    return <VentureComparison txs={filtered} ventures={ventures}/>;
  };

  const handlePrint = () => {
    setPrintPreviewOpen(true);
  };

  const handleConfirmPrint = () => {
    if (typeof window === 'undefined') return;
    window.print();
  };

  const handleCSV = () => {
    const rows = [['Date','Description','Venture','Category','Payment','Type','Amount']];
    filtered.forEach(t=>{
      const v = ventures.find(x=>x.id===t.ventureId);
      const cat = accounts.find(a=>a.id===t.categoryAccountId);
      rows.push([t.date,t.description,v?.name||'',cat?.name||'',t.paymentMethod,t.type,t.amount]);
    });
    const csv = rows.map(r=>r.map(c=>`"${c}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
    a.download = `financial-report-${rFrom}-${rTo}.csv`;
    a.click();
  };

  return (
    <div>
      <div className="card" style={{marginBottom:16}}>
        <div className="flbar" style={{marginBottom:0}}>
          <div className="fg"><label>From Date</label><input type="date" value={rFrom} onChange={e=>setRFrom(e.target.value)}/></div>
          <div className="fg"><label>To Date</label><input type="date" value={rTo} onChange={e=>setRTo(e.target.value)}/></div>
          <div className="fg">
            <label>Venture</label>
            <select value={rVenture} onChange={e=>setRVenture(e.target.value)}>
              <option value="all">All Ventures</option>
              {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div style={{display:'flex',alignItems:'flex-end',gap:8}}>
            <button className="btn bi-btn bs" onClick={handleCSV}>Export CSV</button>
            <button className="btn bg bs" onClick={handlePrint}>Print</button>
          </div>
        </div>
        <div style={{marginTop:10,fontSize:11,color:'var(--tm)'}}>
          Showing {filtered.length} transactions - Period: {rFrom} to {rTo}
        </div>
      </div>

      <div className="card">
        <div className="tabs">
          {tabs.map(t=><div key={t.id} className={`tab ${tab===t.id?'on':''}`} onClick={()=>setTab(t.id)}>{t.label}</div>)}
        </div>
        {renderReportContent()}
      </div>

      {printPreviewOpen && (
        <div className="print-preview-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setPrintPreviewOpen(false); }}>
          <div className="print-preview-shell">
            <div className="print-preview-head no-print">
              <div style={{display:'grid',gap:4}}>
                <div style={{fontSize:11,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Print Preview</div>
                <div style={{fontFamily:'var(--serif)',fontSize:28,color:'var(--tb)'}}>{activeReport.label}</div>
                <div style={{fontSize:12,color:'var(--tm)'}}>Only this report will be printed. App menus and navigation will be excluded.</div>
              </div>
              <button className="btn bg bs" onClick={() => setPrintPreviewOpen(false)}>Close</button>
            </div>

            <div className="print-preview-paper">
              <div className="print-preview-card">
                <div className="print-preview-meta">
                  <div style={{fontFamily:'var(--serif)',fontSize:28,fontWeight:700,color:'#111'}}>Financial Management System (FMS)</div>
                  <div style={{fontSize:18,fontWeight:700,color:'#222'}}>{activeReport.label}</div>
                  <div style={{fontSize:12,color:'#555'}}>Period: {rFrom} to {rTo}</div>
                  <div style={{fontSize:12,color:'#555'}}>Scope: {activeVentureName}</div>
                  <div style={{fontSize:12,color:'#555'}}>Generated: {new Date().toLocaleString()}</div>
                </div>
                {renderReportContent()}
              </div>
            </div>

            <div className="print-preview-actions no-print">
              <button className="btn bg" onClick={() => setPrintPreviewOpen(false)}>Back</button>
              <button className="btn bp" onClick={handleConfirmPrint}>Print This Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   JOURNAL / GENERAL LEDGER VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function Journal({ txs, accounts, ventures }) {
  const sorted = useMemo(() => [...txs].sort((a,b)=>b.date.localeCompare(a.date)), [txs]);
  const totDr = txs.reduce((s,t)=>s+t.amount,0)*2;

  if (sorted.length === 0) return (
    <div className="card"><div className="empty"><div className="empty-ico">J</div><p>No journal entries yet.</p></div></div>
  );

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:18}}>
        <KPI label="Journal Entries" value={txs.length*2} sub="2 lines per transaction" acc="var(--info)"/>
        <KPI label="Total Debits" value={fmt(totDr/2)} sub="Matches total credits" acc="var(--ok)"/>
        <KPI label="Total Credits" value={fmt(totDr/2)} sub="Double-entry balanced" acc="var(--err)"/>
      </div>
      {sorted.map(t => {
        const v   = ventures.find(x=>x.id===t.ventureId);
        const bal = accounts.find(a=>a.id===t.balanceAccountId);
        const cat = accounts.find(a=>a.id===t.categoryAccountId);
        const isIncomeSide = isIncomeTx(t) || isHotspotCreditIssueTx(t);
        const drAcc = isIncomeSide ? bal : cat;
        const crAcc = isIncomeSide ? cat : bal;
        return (
          <div key={t.id} className="jl-item">
            <div className="jl-hd">
              <div>
                <div style={{fontWeight:600,fontSize:13,color:'var(--tb)',marginBottom:2}}>{t.description}</div>
                <div className="jl-ref">{t.date} - {v?.name||'-'} - {t.paymentMethod}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <span className={`badge ${getTxBadgeClass(t)}`}>{getTxDisplayType(t)}</span>
                {t.reference&&<div style={{fontSize:10,color:'var(--tm)',marginTop:4}}>Ref: {t.reference}</div>}
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 120px 120px',gap:8,padding:'5px 0',borderBottom:'1px solid var(--brd)'}}>
              <div style={{fontSize:11,color:'var(--tm)',paddingLeft:8}}>ACCOUNT</div>
              <div style={{fontSize:11,color:'var(--ok)',textAlign:'right'}}>DEBIT</div>
              <div style={{fontSize:11,color:'var(--err)',textAlign:'right'}}>CREDIT</div>
            </div>
            <div className="jl-entry">
              <div className="jl-acc">{drAcc?.code} - {drAcc?.name}</div>
              <div className="jl-dr">{fmt(t.amount)}</div>
              <div className="jl-cr">-</div>
            </div>
            <div className="jl-entry" style={{paddingLeft:20}}>
              <div className="jl-acc" style={{paddingLeft:16,color:'var(--tm)'}}>{crAcc?.code} - {crAcc?.name}</div>
              <div className="jl-dr">-</div>
              <div className="jl-cr">{fmt(t.amount)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   AGENTS & STAFF VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function AgentsView({ agents, txs, ventures, onDelete, onDeleteAll, onEdit, onAdd }) {
  const [perfRange, setPerfRange] = useState('month');
  const [teamFilter, setTeamFilter] = useState('all');
  const getCommission = id => {
    const member = (agents || []).find(a => a.id === id);
    if (!isCommissionEligibleMember(member?.role)) return 0;
    return txs.filter(t => t.posType === 'hotspot-agent-credit' && t.agentId === id)
      .reduce((s, t) => s + Number(t.amount || 0) * 0.10, 0);
  };
  const getVentureName = ventureId => ventures.find(v=>v.id===ventureId)?.name || 'Unassigned';
  const getBranchName = (ventureId, branchId) => {
    if (!ventureId || !branchId) return 'Unassigned';
    const venture = ventures.find(v => v.id === ventureId);
    return venture?.branches?.find(b => b.id === branchId)?.name || 'Unassigned';
  };

  const hotspotTxs = useMemo(() => txs.filter(t => t.posType === 'hotspot-agent-credit' || t.posType === 'hotspot-agent-collection'), [txs]);
  const orderedHotspotTxs = useMemo(() => {
    return [...hotspotTxs].sort((a, b) => {
      const byDate = String(a?.date || '').localeCompare(String(b?.date || ''));
      if (byDate !== 0) return byDate;
      return String(a?.createdAt || '').localeCompare(String(b?.createdAt || ''));
    });
  }, [hotspotTxs]);

  const agentCreditMap = useMemo(() => {
    const map = {};
    (agents || []).forEach(a => {
      map[a.id] = { issued: 0, collected: 0, commission: 0, availableCredit: 0 };
    });

    orderedHotspotTxs.forEach(t => {
      if (!t.agentId) return;
      if (!map[t.agentId]) map[t.agentId] = { issued: 0, collected: 0, commission: 0, availableCredit: 0 };
      if (t.posType === 'hotspot-agent-credit') map[t.agentId].issued += Number(t.amount || 0);
      if (t.posType === 'hotspot-agent-collection') map[t.agentId].collected += Number(t.amount || 0);
      map[t.agentId].commission = map[t.agentId].issued * 0.10;
      map[t.agentId].availableCredit = Math.max(map[t.agentId].issued - (map[t.agentId].collected + map[t.agentId].commission), 0);
      if (map[t.agentId].availableCredit <= 0) {
        map[t.agentId] = { issued: 0, collected: 0, commission: 0, availableCredit: 0 };
      }
    });

    return map;
  }, [agents, orderedHotspotTxs]);

  const rangeStartDate = useMemo(() => {
    const d = new Date();
    if (perfRange === 'day') d.setDate(d.getDate() - 1);
    if (perfRange === 'week') d.setDate(d.getDate() - 7);
    if (perfRange === 'month') d.setDate(d.getDate() - 30);
    if (perfRange === 'year') d.setDate(d.getDate() - 365);
    return d.toISOString().split('T')[0];
  }, [perfRange]);

  const performanceRows = useMemo(() => {
    const map = {};
    (agents || []).forEach(a => {
      map[a.id] = {
        id: a.id,
        name: a.name,
        creditIssued: 0,
        cashCollected: 0,
        availableCredit: 0,
      };
    });

    hotspotTxs
      .filter(t => t.date >= rangeStartDate)
      .forEach(t => {
        if (!t.agentId) return;
        if (!map[t.agentId]) {
          map[t.agentId] = {
            id: t.agentId,
            name: 'Unknown',
            creditIssued: 0,
            cashCollected: 0,
            availableCredit: 0,
          };
        }
        if (t.posType === 'hotspot-agent-credit') map[t.agentId].creditIssued += Number(t.amount || 0);
        if (t.posType === 'hotspot-agent-collection') map[t.agentId].cashCollected += Number(t.amount || 0);
      });

    return Object.values(map)
      .map(r => ({ ...r, availableCredit: Math.max(r.creditIssued - (r.cashCollected + (r.creditIssued * 0.10)), 0) }))
      .filter(r => r.creditIssued > 0 || r.cashCollected > 0)
      .sort((a, b) => (b.creditIssued + b.cashCollected) - (a.creditIssued + a.cashCollected));
  }, [agents, hotspotTxs, rangeStartDate]);

  const perfLabel = perfRange === 'day' ? 'Last Day' : perfRange === 'week' ? 'Last 7 Days' : perfRange === 'month' ? 'Last 30 Days' : 'Last 12 Months';
  const agentCount = (agents || []).filter(ag => getTeamCategoryFromRole(ag.role) === 'Agent').length;
  const staffCount = (agents || []).filter(ag => getTeamCategoryFromRole(ag.role) === 'Staff').length;
  const filteredAgents = useMemo(() => {
    if (teamFilter === 'all') return agents;
    return (agents || []).filter(ag => getTeamCategoryFromRole(ag.role).toLowerCase() === teamFilter);
  }, [agents, teamFilter]);

  return (
    <div>
      <div className="card" style={{marginBottom:14}}>
        <div className="sec-h" style={{marginBottom:10}}>
          <div className="sec-t">Agent Performance</div>
          <div className="fg" style={{minWidth:170}}>
            <label>Range</label>
            <select value={perfRange} onChange={e=>setPerfRange(e.target.value)}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </div>
        </div>
        <div style={{fontSize:11,color:'var(--tm)',marginBottom:8}}>{perfLabel}</div>
        {performanceRows.length === 0 ? (
          <div className="empty" style={{padding:'24px 12px'}}><p>No hotspot credit activity in this period.</p></div>
        ) : (
          <div style={{width:'100%',height:280}}>
            <ResponsiveContainer>
              <BarChart data={performanceRows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.08)" />
                <XAxis dataKey="name" tick={{ fill:'#6070a0', fontSize:11 }} interval={0} angle={-18} textAnchor="end" height={58} />
                <YAxis tick={{ fill:'#6070a0', fontSize:11 }} tickFormatter={fmtK} />
                <Tooltip formatter={(val) => fmt(val)} />
                <Legend />
                <Bar dataKey="creditIssued" name="Credit Issued" fill="#c9a84c" radius={[4,4,0,0]} />
                <Bar dataKey="cashCollected" name="Cash Collected" fill="#2dd4a0" radius={[4,4,0,0]} />
                <Bar dataKey="availableCredit" name="Available Credit" fill="#fb923c" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {agents.length === 0 ? (
        <div className="empty">
          <div className="empty-ico">AG</div>
          <p>No agents or staff added yet.</p>
          <button className="btn bp" style={{marginTop:14}} onClick={onAdd}>+ Add Agent</button>
        </div>
      ) : (
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,gap:10,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:12,color:'var(--tm)'}}>Agents: {agentCount}</span>
              <span style={{fontSize:12,color:'var(--tm)'}}>Staff: {staffCount}</span>
              <div className="fg" style={{minWidth:170}}>
                <label>Team Filter</label>
                <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)}>
                  <option value="all">All Team Members</option>
                  <option value="agent">Agents Only</option>
                  <option value="staff">Staff Only</option>
                </select>
              </div>
            </div>
            <button className="btn bdr bs" onClick={()=>{ if(window.confirm('Delete all agents and staff? This cannot be undone.')) onDeleteAll(); }}>Delete All</button>
          </div>
          <div className="tw">
            <table>
              <thead><tr><th>Name</th><th>Role</th><th>Category</th><th>Phone</th><th>Venture</th><th>Branch</th><th style={{textAlign:'right'}}>Available Credit</th><th style={{textAlign:'right'}}>Total Commission</th><th></th></tr></thead>
              <tbody>
                {filteredAgents.map(ag=>(
                  <tr key={ag.id}>
                    <td style={{fontWeight:500}}>{ag.name}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{ag.role}</td>
                    <td style={{fontSize:12,color:getTeamCategoryFromRole(ag.role)==='Agent'?'var(--gold)':'var(--info)',fontWeight:600}}>{getTeamCategoryFromRole(ag.role)}</td>
                    <td style={{color:'var(--tm)',fontSize:12,fontFamily:'var(--mono)'}}>{ag.phone}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{getVentureName(ag.ventureId)}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{getBranchName(ag.ventureId, ag.branchId)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:(agentCreditMap[ag.id]?.availableCredit||0)>0?'#fb923c':'var(--tm)',fontWeight:500}}>{fmt(agentCreditMap[ag.id]?.availableCredit || 0)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--ok)',fontWeight:500}}>{fmt(getCommission(ag.id))}</td>
                    <td>
                      <button className="btn bg bs" style={{marginRight:4}} onClick={()=>onEdit(ag)} title="Edit">Edit</button>
                      <button className="btn bdr bs" onClick={()=>onDelete(ag.id)} title="Delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SnapshotsPage({ txs, ventures, agents }) {
  const [range, setRange] = useState('month');

  const rangeStart = useMemo(() => {
    if (range === 'all') return '';
    const d = new Date();
    if (range === 'week') d.setDate(d.getDate() - 7);
    if (range === 'month') d.setDate(d.getDate() - 30);
    if (range === 'quarter') d.setDate(d.getDate() - 90);
    if (range === 'year') d.setDate(d.getDate() - 365);
    return d.toISOString().split('T')[0];
  }, [range]);

  const periodTxs = useMemo(() => {
    if (!rangeStart) return txs;
    return txs.filter(t => t.date >= rangeStart);
  }, [txs, rangeStart]);

  const ventureRows = useMemo(() => {
    return ventures.map(v => {
      const vtx = periodTxs.filter(t => t.ventureId === v.id);
      const income = vtx.filter(isIncomeTx).reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const expenses = vtx.filter(isExpenseTx).reduce((sum, t) => sum + Number(t.amount || 0), 0);
      return {
        id: v.id,
        name: v.name,
        branches: (v.branches || []).length,
        txCount: vtx.length,
        income,
        expenses,
        net: income - expenses,
      };
    }).sort((a, b) => b.net - a.net);
  }, [periodTxs, ventures]);

  const totalIncome = ventureRows.reduce((sum, row) => sum + row.income, 0);
  const totalExpenses = ventureRows.reduce((sum, row) => sum + row.expenses, 0);
  const totalNet = totalIncome - totalExpenses;

  const roleRows = useMemo(() => {
    const roleMap = {};
    const roleByAgentId = {};

    (agents || []).forEach(agent => {
      const role = String(agent.role || 'Unassigned').trim() || 'Unassigned';
      roleByAgentId[agent.id] = role;
      if (!roleMap[role]) {
        roleMap[role] = {
          role,
          staffCount: 0,
          activeStaffCount: 0,
          incomeHandled: 0,
          commission: 0,
          creditIssued: 0,
          cashCollected: 0,
          outstanding: 0,
          txCount: 0,
        };
      }
      roleMap[role].staffCount += 1;
    });

    const activeStaffByRole = {};

    periodTxs.forEach(t => {
      if (!t.agentId) return;
      const role = roleByAgentId[t.agentId] || 'Unassigned';
      if (!roleMap[role]) {
        roleMap[role] = {
          role,
          staffCount: 0,
          activeStaffCount: 0,
          incomeHandled: 0,
          commission: 0,
          creditIssued: 0,
          cashCollected: 0,
          outstanding: 0,
          txCount: 0,
        };
      }

      roleMap[role].txCount += 1;
      if (!activeStaffByRole[role]) activeStaffByRole[role] = new Set();
      activeStaffByRole[role].add(t.agentId);

      if (isIncomeTx(t)) {
        roleMap[role].incomeHandled += Number(t.amount || 0);
        if (isCommissionEligibleMember(role)) roleMap[role].commission += Number(t.commission || 0);
      }
      if (t.posType === 'hotspot-agent-credit') roleMap[role].creditIssued += Number(t.amount || 0);
      if (t.posType === 'hotspot-agent-collection') roleMap[role].cashCollected += Number(t.amount || 0);
    });

    return Object.values(roleMap)
      .map(row => {
        const roleCommission = isCommissionEligibleMember(row.role) ? row.creditIssued * 0.10 : 0;
        return {
          ...row,
          activeStaffCount: activeStaffByRole[row.role]?.size || 0,
          commission: roleCommission,
          outstanding: Math.max(row.creditIssued - roleCommission - row.cashCollected, 0),
        };
      })
      .sort((a, b) => (b.incomeHandled + b.cashCollected) - (a.incomeHandled + a.cashCollected));
  }, [agents, periodTxs]);

  const staffRows = useMemo(() => {
    const ventureNameById = {};
    ventures.forEach(v => { ventureNameById[v.id] = v.name; });

    return (agents || []).map(agent => {
      const atx = periodTxs.filter(t => t.agentId === agent.id);
      const incomeHandled = atx.filter(isIncomeTx).reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const creditIssued = atx.filter(t => t.posType === 'hotspot-agent-credit').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const cashCollected = atx.filter(t => t.posType === 'hotspot-agent-collection').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const commission = isCommissionEligibleMember(agent.role) ? creditIssued * 0.10 : 0;
      const outstanding = Math.max(creditIssued - commission - cashCollected, 0);

      return {
        id: agent.id,
        name: agent.name,
        role: agent.role || 'Unassigned',
        category: getTeamCategoryFromRole(agent.role),
        venture: ventureNameById[agent.ventureId] || 'Unassigned',
        txCount: atx.length,
        incomeHandled,
        commission,
        creditIssued,
        cashCollected,
        outstanding,
      };
    }).sort((a, b) => (b.incomeHandled + b.cashCollected) - (a.incomeHandled + a.cashCollected));
  }, [agents, periodTxs, ventures]);

  const rangeLabel = range === 'week'
    ? 'Last 7 days'
    : range === 'month'
      ? 'Last 30 days'
      : range === 'quarter'
        ? 'Last 90 days'
        : range === 'year'
          ? 'Last 12 months'
          : 'All time';

  return (
    <div>
      <div className="card" style={{marginBottom:14}}>
        <div className="sec-h" style={{marginBottom:10}}>
          <div>
            <div className="sec-t">Business, Agent & Staff Snapshots</div>
            <div style={{fontSize:11,color:'var(--tm)',marginTop:4}}>{rangeLabel}</div>
          </div>
          <div className="fg" style={{minWidth:180}}>
            <label>Range</label>
            <select value={range} onChange={e=>setRange(e.target.value)}>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
        <div className="kpi-g" style={{marginTop:6}}>
          <KPI label="Business Income" value={fmt(totalIncome)} sub={`${periodTxs.filter(isIncomeTx).length} income transactions`} acc="var(--ok)" />
          <KPI label="Business Expenses" value={fmt(totalExpenses)} sub={`${periodTxs.filter(isExpenseTx).length} expense transactions`} acc="var(--err)" />
          <KPI label="Business Net" value={fmt(totalNet)} sub="Income minus expenses" acc={totalNet>=0?'var(--ok)':'var(--err)'} />
          <KPI label="Active Staff / Agents" value={`${staffRows.filter(r=>r.txCount>0).length}/${staffRows.length}`} sub="With posted activity" acc="var(--info)" />
        </div>
      </div>

      <div className="card" style={{marginBottom:14}}>
        <div className="sec-h" style={{marginBottom:10}}><div className="sec-t">Business Snapshot by Venture</div></div>
        {ventureRows.length === 0 ? (
          <div className="empty"><div className="empty-ico">B</div><p>No ventures available yet.</p></div>
        ) : (
          <div className="tw">
            <table>
              <thead><tr><th>Venture</th><th>Branches</th><th>Transactions</th><th style={{textAlign:'right'}}>Income</th><th style={{textAlign:'right'}}>Expenses</th><th style={{textAlign:'right'}}>Net</th></tr></thead>
              <tbody>
                {ventureRows.map(row => (
                  <tr key={row.id}>
                    <td style={{fontWeight:600}}>{row.name}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{row.branches}</td>
                    <td style={{color:'var(--tm)',fontSize:12}}>{row.txCount}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--ok)'}}>{fmt(row.income)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--err)'}}>{fmt(row.expenses)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:row.net>=0?'var(--ok)':'var(--err)',fontWeight:600}}>{fmt(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="cg2" style={{marginBottom:14}}>
        <div className="card">
          <div className="sec-h" style={{marginBottom:10}}><div className="sec-t">Role Performance Snapshot</div></div>
          {roleRows.length === 0 ? (
            <div className="empty"><div className="empty-ico">R</div><p>No role activity in this range.</p></div>
          ) : (
            <div className="tw">
              <table>
                <thead><tr><th>Role</th><th>Staff</th><th>Active</th><th style={{textAlign:'right'}}>Income Handled</th><th style={{textAlign:'right'}}>Commission</th><th style={{textAlign:'right'}}>Outstanding</th></tr></thead>
                <tbody>
                  {roleRows.map(row => (
                    <tr key={row.role}>
                      <td style={{fontWeight:600}}>{row.role}</td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{row.staffCount}</td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{row.activeStaffCount}</td>
                      <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--ok)'}}>{fmt(row.incomeHandled)}</td>
                      <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--info)'}}>{fmt(row.commission)}</td>
                      <td style={{textAlign:'right',fontFamily:'var(--mono)',color:row.outstanding>0?'#fb923c':'var(--tm)'}}>{fmt(row.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="sec-h" style={{marginBottom:10}}><div className="sec-t">Role Comparison</div></div>
          {roleRows.length === 0 ? (
            <div className="empty"><div className="empty-ico">C</div><p>No chart data yet.</p></div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={roleRows.slice(0, 8)} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.08)" />
                <XAxis dataKey="role" tick={{ fill:'#6070a0', fontSize:11 }} interval={0} angle={-16} textAnchor="end" height={58} />
                <YAxis tick={{ fill:'#6070a0', fontSize:11 }} tickFormatter={fmtK} />
                <Tooltip formatter={(val)=>fmt(val)} />
                <Legend />
                <Bar dataKey="incomeHandled" name="Income Handled" fill="#2dd4a0" radius={[4,4,0,0]} />
                <Bar dataKey="commission" name="Commission" fill="#60a5fa" radius={[4,4,0,0]} />
                <Bar dataKey="outstanding" name="Outstanding" fill="#fb923c" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card">
        <div className="sec-h" style={{marginBottom:10}}><div className="sec-t">Individual Staff & Agent Snapshot</div></div>
        {staffRows.length === 0 ? (
          <div className="empty"><div className="empty-ico">S</div><p>No agents or staff available yet.</p></div>
        ) : (
          <div className="tw">
            <table>
                <thead><tr><th>Name</th><th>Role</th><th>Category</th><th>Venture</th><th>Activity</th><th style={{textAlign:'right'}}>Income Handled</th><th style={{textAlign:'right'}}>Commission</th><th style={{textAlign:'right'}}>Outstanding</th></tr></thead>
              <tbody>
                {staffRows.map(row => (
                  <tr key={row.id}>
                    <td style={{fontWeight:600}}>{row.name}</td>
                    <td style={{fontSize:12,color:'var(--tm)'}}>{row.role}</td>
                      <td style={{fontSize:12,color:row.category==='Agent'?'var(--gold)':'var(--info)',fontWeight:600}}>{row.category}</td>
                    <td style={{fontSize:12,color:'var(--tm)'}}>{row.venture}</td>
                    <td style={{fontSize:12,color:'var(--tm)'}}>{row.txCount}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--ok)'}}>{fmt(row.incomeHandled)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:'var(--info)'}}>{fmt(row.commission)}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--mono)',color:row.outstanding>0?'#fb923c':'var(--tm)'}}>{fmt(row.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   BRANCH DETAIL VIEW
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function BranchDetail({ venture, branch, transactions, accounts, onBack }) {
  const branchTxs = transactions.filter(t=>t.ventureId===venture?.id && t.branchId===branch.id);
  const inc = branchTxs.filter(isIncomeTx).reduce((s,t)=>s+t.amount,0);
  const exp = branchTxs.filter(isExpenseTx).reduce((s,t)=>s+t.amount,0);
  const net = inc - exp;
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18}}>
        <button className="btn bg bs" onClick={onBack}>Back to Ventures</button>
        <div>
          <div style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--tb)'}}>{branch.name}</div>
          <div style={{fontSize:11,color:'var(--tm)',marginTop:2}}>{venture?.name}{branch.location ? ` - ${branch.location}` : ''}</div>
        </div>
      </div>
      <div className="kpi-g" style={{marginBottom:18}}>
        <KPI label="Branch Income" value={fmt(inc)} acc="var(--ok)"/>
        <KPI label="Branch Expenses" value={fmt(exp)} acc="var(--err)"/>
        <KPI label="Net Profit" value={fmt(net)} acc={net>=0?'var(--ok)':'var(--err)'}/>
        <KPI label="Transactions" value={branchTxs.length} acc="var(--info)"/>
      </div>
      <div className="card">
        <div className="sec-h"><div className="sec-t">Branch Transactions</div></div>
        {branchTxs.length===0 ? (
          <div className="empty"><div className="empty-ico">BR</div><p>No transactions for this branch yet.</p></div>
        ) : (
          <div className="tw">
            <table>
              <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Payment</th><th>Type</th><th>Amount</th></tr></thead>
              <tbody>
                {[...branchTxs].sort((a,b)=>b.date.localeCompare(a.date)).map(t=>{
                  const cat = accounts.find(a=>a.id===t.categoryAccountId);
                  return (
                    <tr key={t.id}>
                      <td className="mono" style={{fontSize:12}}>{t.date}</td>
                      <td>{t.description}</td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{cat?.name||'-'}</td>
                      <td style={{color:'var(--tm)',fontSize:12}}>{t.paymentMethod}</td>
                      <td><span className={`badge ${getTxBadgeClass(t)}`}>{getTxDisplayType(t)}</span></td>
                      <td className={getTxAmountClass(t)}>{getTxAmountPrefix(t)}{fmt(t.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   SHORT TERM LOAN TRACKER
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function ShortTermLoanPage({ txs, ventures, accounts, loans, onAddLoan, onEditLoan, onDeleteLoan }) {
  const [loanForm, setLoanForm] = useState({ name:'', lender:'', principal:'', ventureId: ventures[0]?.id||'', startDate:tod(), dueDate:'' });
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [loanError, setLoanError] = useState('');
  const setLF = (k,v) => {
    setLoanForm(f=>({...f,[k]:v}));
    if (loanError) setLoanError('');
  };

  const resetForm = () => {
    setLoanForm({ name:'', lender:'', principal:'', ventureId: ventures[0]?.id||'', startDate:tod(), dueDate:'' });
    setEditingLoanId(null);
    setLoanError('');
  };
  const startEdit = loan => { setEditingLoanId(loan.id); setLoanForm({ name:loan.name||'', lender:loan.lender||'', principal:String(loan.principal||''), ventureId:loan.ventureId||ventures[0]?.id||'', startDate:loan.startDate||tod(), dueDate:loan.dueDate||'' }); };

  const submit = async () => {
    if (!loanForm.name.trim() || !loanForm.principal || Number(loanForm.principal) <= 0) {
      setLoanError('Enter loan name and valid principal amount.');
      return;
    }
    setLoanError('');
    const payload = { id:editingLoanId||uid(), name:loanForm.name.trim(), lender:loanForm.lender.trim(), principal:Number(loanForm.principal), ventureId:loanForm.ventureId||'', startDate:loanForm.startDate||tod(), dueDate:loanForm.dueDate||'', updatedAt:new Date().toISOString() };
    if (editingLoanId) { await onEditLoan(payload); } else { await onAddLoan({...payload, createdAt:new Date().toISOString()}); }
    resetForm();
  };

  const remove = async id => {
    const linked = txs.filter(t=>t.loanId===id&&t.type==='expense'&&isLoanPaymentExpenseAccount(accounts,t.categoryAccountId));
    if (linked.length > 0) { alert(`This loan has ${linked.length} linked payment(s). Delete those transactions first.`); return; }
    if (!window.confirm('Delete this loan?')) return;
    await onDeleteLoan(id);
  };

  const stlTxs = txs.filter(t=>t.type==='expense'&&isLoanPaymentExpenseAccount(accounts,t.categoryAccountId));
  const loanRows = (loans||[]).map(l => {
    const paid = stlTxs.filter(t=>t.loanId===l.id).reduce((s,t)=>s+t.amount,0);
    return { ...l, paid, balance:(l.principal||0)-paid };
  });
  const totalPrincipal = loanRows.reduce((s,l)=>s+(l.principal||0),0);
  const totalPaid = stlTxs.reduce((s,t)=>s+t.amount,0);
  const totalOutstanding = loanRows.reduce((s,l)=>s+Math.max(l.balance,0),0);
  const recent = [...stlTxs].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,10);

  return (
    <div>
      <div className="card" style={{marginBottom:22}}>
        <div className="sec-h" style={{marginBottom:16}}>
          <div className="sec-t">Short Term Loan Tracker</div>
          <div style={{fontSize:11,color:'var(--tm)'}}>Track loans and payments recorded under "Short Term Loan Payments" account</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'minmax(260px,320px) 1fr',gap:16,marginBottom:14}}>
          <div style={{background:'var(--s3)',border:'1px solid var(--brd)',borderRadius:10,padding:12}}>
            <div style={{fontSize:11,color:'var(--tm)',fontWeight:700,marginBottom:10}}>{editingLoanId ? 'EDIT LOAN' : 'ADD LOAN'}</div>
            <div className="fg" style={{marginBottom:10}}><label>Loan Name *</label><input value={loanForm.name} onChange={e=>setLF('name',e.target.value)} placeholder="e.g. Working Capital Loan"/></div>
            <div className="fg" style={{marginBottom:10}}><label>Lender</label><input value={loanForm.lender} onChange={e=>setLF('lender',e.target.value)} placeholder="Bank or person (optional)"/></div>
            <div className="fg" style={{marginBottom:10}}><label>Principal (UGX) *</label><input type="number" min="0" step="any" value={loanForm.principal} onChange={e=>setLF('principal',e.target.value)} placeholder="0"/></div>
            <div className="fg" style={{marginBottom:10}}><label>Venture</label>
              <select value={loanForm.ventureId} onChange={e=>setLF('ventureId',e.target.value)}>
                <option value="">Unassigned</option>
                {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="fg" style={{marginBottom:10}}><label>Start Date</label><input type="date" value={loanForm.startDate} onChange={e=>setLF('startDate',e.target.value)}/></div>
            <div className="fg" style={{marginBottom:10}}><label>Due Date</label><input type="date" value={loanForm.dueDate} onChange={e=>setLF('dueDate',e.target.value)}/></div>
            {loanError && <div style={{marginBottom:10,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{loanError}</div>}
            <div style={{display:'flex',gap:8,marginTop:8}}>
              <button className="btn bp" onClick={submit}>{editingLoanId ? 'Save Loan' : 'Add Loan'}</button>
              {editingLoanId && <button className="btn bg" onClick={resetForm}>Cancel</button>}
            </div>
          </div>
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,minmax(110px,1fr))',gap:10,marginBottom:12}}>
              {[{label:'Loans',val:loanRows.length,c:'var(--tb)'},{label:'Principal',val:fmt(totalPrincipal),c:'var(--gold)'},{label:'Total Paid',val:fmt(totalPaid),c:'var(--ok)'},{label:'Outstanding',val:fmt(totalOutstanding),c:'#fb923c'}].map(k=>(
                <div key={k.label} style={{background:'var(--s3)',borderRadius:8,padding:'10px 12px'}}>
                  <div style={{fontSize:11,color:'var(--tm)'}}>{k.label}</div>
                  <div style={{fontSize:18,fontWeight:700,color:k.c,fontFamily:'var(--mono)'}}>{k.val}</div>
                </div>
              ))}
            </div>
            {loanRows.length === 0 ? (
              <div className="empty"><div className="empty-ico">LN</div><p>No loans yet. Add one on the left.</p></div>
            ) : (
              <div className="tw" style={{marginBottom:12}}>
                <table>
                  <thead><tr><th>Loan</th><th>Venture</th><th style={{textAlign:'right'}}>Principal</th><th style={{textAlign:'right'}}>Paid</th><th style={{textAlign:'right'}}>Balance</th><th></th></tr></thead>
                  <tbody>
                    {loanRows.map(l=>{
                      const v = ventures.find(x=>x.id===l.ventureId);
                      return (
                        <tr key={l.id}>
                          <td><div style={{fontWeight:600,color:'var(--txt)'}}>{l.name}</div>{l.lender&&<div style={{fontSize:11,color:'var(--tm)'}}>{l.lender}</div>}</td>
                          <td><span style={{color:v?.color||'var(--tm)',fontSize:12}}>{v?.name||'Unassigned'}</span></td>
                          <td className="mono" style={{textAlign:'right',color:'var(--gold)'}}>{fmt(l.principal)}</td>
                          <td className="mono" style={{textAlign:'right',color:'var(--ok)'}}>{fmt(l.paid)}</td>
                          <td className="mono" style={{textAlign:'right',color:l.balance>0?'#fb923c':'var(--ok)',fontWeight:600}}>{fmt(l.balance)}</td>
                          <td>
                            <button className="btn bg bs" style={{marginRight:4}} onClick={()=>startEdit(l)} title="Edit">Edit</button>
                            <button className="btn bdr bs" onClick={()=>remove(l.id)} title="Delete">Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {recent.length > 0 && (
              <div>
                <div style={{fontSize:10,color:'var(--tm)',marginBottom:8,fontWeight:600}}>RECENT LOAN PAYMENTS</div>
                <div className="tw">
                  <table>
                    <thead><tr><th>Date</th><th>Description</th><th>Loan</th><th>Venture</th><th>Amount</th></tr></thead>
                    <tbody>
                      {recent.map(t=>{
                        const v = ventures.find(x=>x.id===t.ventureId);
                        const loan = (loans||[]).find(l=>l.id===t.loanId);
                        return (
                          <tr key={t.id}>
                            <td className="mono" style={{fontSize:11}}>{t.date}</td>
                            <td style={{fontSize:11}}>{t.description}</td>
                            <td style={{fontSize:11}}>{loan?.name||'Unlinked'}</td>
                            <td><span style={{color:v?.color||'var(--gold)',fontSize:11}}>{v?.name||'-'}</span></td>
                            <td style={{fontFamily:'var(--mono)',color:'var(--gold)',fontSize:11}}>{fmt(t.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   HOTSPOT POINT OF SALE PAGE
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function HotspotPOSPage({ txs, ventures, accounts, agents, onRecordSale, onUpdateSale, onDeleteSale }) {
  const incomeAccounts = accounts.filter(a => a.type === 'income');
  const liabilityAccounts = accounts.filter(a => a.type === 'liability');
  const hotspotVentures = useMemo(() => {
    const hotspotKeywords = ['wifi', 'wi-fi', 'hotspot', 'network'];
    return (ventures || []).filter(v => {
      const name = String(v?.name || '').toLowerCase();
      return hotspotKeywords.some(k => name.includes(k));
    });
  }, [ventures]);
  const DIRECT_SALE_PLANS = ['Family Devices', 'Personal'];
  const CREDIT_DURATION_OPTIONS = [
    { value: 'Daily', label: 'Daily (UGX 1,000)', unitPrice: 1000 },
    { value: 'Week', label: 'Week (UGX 6,000)', unitPrice: 6000 },
    { value: 'Month', label: 'Month (UGX 20,000)', unitPrice: 20000 },
  ];
  const receivableAccountId = accounts.find(a => a.code === '1400')?.id || accounts.find(a => a.type === 'asset')?.id || '';
  const defaultLiabilityAccountId = liabilityAccounts.find(a => a.code === '2100')?.id || liabilityAccounts[0]?.id || '';
  const defaultRevenueAccountId = incomeAccounts.find(a => a.code === '4200')?.id || incomeAccounts[0]?.id || '';
  const [form, setForm] = useState({
    mode: 'issue-credit',
    ventureId: hotspotVentures[0]?.id || '',
    agentId: agents[0]?.id || '',
    amount: '',
    bundles: '1',
    duration: 'Daily',
    plan: 'Personal',
    paymentMethod: PAYMENT_METHODS[0] || 'Cash',
    categoryAccountId: defaultRevenueAccountId,
    customerName: '',
    customerPhone: '',
    note: '',
  });
  const [editingPOSId, setEditingPOSId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(prev => {
      const next = { ...prev };
      if (next.mode === 'issue-credit') {
        if (!liabilityAccounts.some(a => a.id === next.categoryAccountId)) {
          next.categoryAccountId = defaultLiabilityAccountId;
        }
      } else if (!incomeAccounts.some(a => a.id === next.categoryAccountId)) {
        next.categoryAccountId = defaultRevenueAccountId;
      }
      if (!next.agentId && agents[0]?.id) next.agentId = agents[0].id;
      const hasValidHotspotVenture = hotspotVentures.some(v => v.id === next.ventureId);
      if (!hasValidHotspotVenture) next.ventureId = hotspotVentures[0]?.id || '';
      return next;
    });
  }, [defaultRevenueAccountId, defaultLiabilityAccountId, agents, hotspotVentures, incomeAccounts, liabilityAccounts]);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (error) setError('');
  };

  const hotspotAgentTxs = useMemo(() => {
    return txs.filter(t => t.posType === 'hotspot-agent-credit' || t.posType === 'hotspot-agent-collection');
  }, [txs]);

  const editingPOSTx = useMemo(() => {
    if (!editingPOSId) return null;
    return txs.find(t => t.id === editingPOSId) || null;
  }, [txs, editingPOSId]);

  const agentStats = useMemo(() => {
    const AGENT_COMMISSION_RATE = 0.10;
    const byAgent = {};
    (agents || []).forEach(a => {
      byAgent[a.id] = {
        agentId: a.id,
        agentName: a.name,
        role: a.role || 'Unassigned',
        vouchersIssued: 0,
        creditIssued: 0,
        cashCollected: 0,
        commission: 0,
        netReceivable: 0,
        outstanding: 0,
        lastDate: '',
      };
    });

    const orderedTxs = [...hotspotAgentTxs].sort((a, b) => {
      const byDate = String(a?.date || '').localeCompare(String(b?.date || ''));
      if (byDate !== 0) return byDate;
      return String(a?.createdAt || '').localeCompare(String(b?.createdAt || ''));
    });

    orderedTxs.forEach(t => {
      if (!t.agentId) return;
      if (!byAgent[t.agentId]) {
        byAgent[t.agentId] = {
          agentId: t.agentId,
          agentName: 'Unknown Agent',
          role: 'Unassigned',
          vouchersIssued: 0,
          creditIssued: 0,
          cashCollected: 0,
          commission: 0,
          netReceivable: 0,
          outstanding: 0,
          lastDate: '',
        };
      }
      const row = byAgent[t.agentId];
      if (t.posType === 'hotspot-agent-credit') {
        row.vouchersIssued += Number(t.bundles || 0);
        row.creditIssued += Number(t.amount || 0);
      }
      if (t.posType === 'hotspot-agent-collection') {
        row.cashCollected += Number(t.amount || 0);
      }
      row.commission = row.creditIssued * AGENT_COMMISSION_RATE;
      row.netReceivable = Math.max(row.creditIssued - row.commission, 0);
      row.outstanding = Math.max(row.creditIssued - (row.cashCollected + row.commission), 0);
      if (row.outstanding <= 0) {
        row.vouchersIssued = 0;
        row.creditIssued = 0;
        row.cashCollected = 0;
        row.commission = 0;
        row.netReceivable = 0;
        row.outstanding = 0;
      }
      if (!row.lastDate || t.date > row.lastDate) row.lastDate = t.date;
    });

    return Object.values(byAgent).sort((a, b) => b.outstanding - a.outstanding);
  }, [agents, hotspotAgentTxs]);

  const selectedAgentStats = agentStats.find(r => r.agentId === form.agentId) || null;
  const selectedAgentFinalReceivable = Math.max(
    Number((selectedAgentStats?.creditIssued || 0) - ((selectedAgentStats?.cashCollected || 0) + (selectedAgentStats?.commission || 0))),
    0
  );
  const selectedDurationOption = CREDIT_DURATION_OPTIONS.find(d => d.value === form.duration) || CREDIT_DURATION_OPTIONS[0];
  const computedCreditAmount = form.mode === 'issue-credit'
    ? Number(form.bundles || 0) * Number(selectedDurationOption?.unitPrice || 0)
    : Number(form.amount || 0);
  const isEditingPOS = Boolean(editingPOSId);

  useEffect(() => {
    if (form.mode !== 'issue-credit') return;
    setForm(prev => ({ ...prev, amount: String(computedCreditAmount) }));
  }, [form.mode, form.bundles, form.duration, computedCreditAmount]);

  useEffect(() => {
    if (form.mode !== 'collect-payment' || isEditingPOS) return;
    setForm(prev => ({ ...prev, amount: String(selectedAgentFinalReceivable || 0) }));
  }, [form.mode, form.agentId, selectedAgentFinalReceivable, isEditingPOS]);

  const startEditPOS = tx => {
    const mode = tx.posType === 'hotspot-agent-credit'
      ? 'issue-credit'
      : tx.posType === 'hotspot-agent-collection'
        ? 'collect-payment'
        : 'direct-sale';

    setEditingPOSId(tx.id);
    setForm(prev => ({
      ...prev,
      mode,
      ventureId: tx.ventureId || prev.ventureId,
      agentId: tx.agentId || prev.agentId,
      amount: String(tx.amount ?? ''),
      bundles: String(tx.bundles || 1),
      duration: tx.duration || 'Daily',
      plan: tx.plan || 'Personal',
      paymentMethod: tx.paymentMethod || prev.paymentMethod,
      categoryAccountId: tx.categoryAccountId || '',
      customerName: tx.customerName || '',
      customerPhone: tx.customerPhone || '',
      note: tx.note || '',
    }));
    if (error) setError('');
  };

  const clearPOSForm = () => {
    setEditingPOSId('');
    setForm(prev => ({
      ...prev,
      mode: 'issue-credit',
      ventureId: hotspotVentures[0]?.id || '',
      agentId: agents[0]?.id || '',
      amount: '',
      bundles: '1',
      duration: 'Daily',
      plan: 'Personal',
      paymentMethod: PAYMENT_METHODS[0] || 'Cash',
      categoryAccountId: defaultRevenueAccountId,
      customerName: '',
      customerPhone: '',
      note: '',
    }));
    setError('');
  };

  const deletePOS = async tx => {
    if (!tx?.id || typeof onDeleteSale !== 'function') return;
    const ok = window.confirm(`Delete this POS entry?\n\n${tx.description || 'POS transaction'}\n${fmt(tx.amount || 0)}`);
    if (!ok) return;
    if (editingPOSId === tx.id) clearPOSForm();
    await onDeleteSale(tx.id);
  };

  const submitSale = async () => {
    const amount = form.mode === 'issue-credit' ? computedCreditAmount : Number(form.amount);
    const bundles = Number(form.bundles || 0);
    if (!form.ventureId) {
      setError('Please select a venture.');
      return;
    }
    if (!form.agentId) {
      setError('Please select the agent receiving/settling vouchers.');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Enter a valid amount.');
      return;
    }

    const agent = (agents || []).find(a => a.id === form.agentId);
    if (!agent) {
      setError('Selected agent was not found.');
      return;
    }

    const reference = editingPOSTx?.reference || `POS-${Date.now().toString().slice(-6)}`;
    const txDate = editingPOSTx?.date || tod();
    const createdAt = editingPOSTx?.createdAt || new Date().toISOString();
    const paymentAccountId = resolvePaymentMethodAccountId(accounts, form.paymentMethod);
    const savePOS = isEditingPOS && typeof onUpdateSale === 'function' ? onUpdateSale : onRecordSale;

    if (form.mode === 'issue-credit') {
      if (!form.categoryAccountId) {
        setError('Please select a liability account for voucher credit issue.');
        return;
      }
      if (!receivableAccountId) {
        setError('Create an asset account first so voucher receivables can be tracked.');
        return;
      }
      if (!bundles || bundles <= 0) {
        setError('Vouchers issued must be greater than zero.');
        return;
      }
      if (!form.duration) {
        setError('Please select a voucher duration.');
        return;
      }
      if ((selectedAgentStats?.outstanding || 0) > 0 && !isEditingPOS) {
        setError(`Collect ${fmt(selectedAgentStats.outstanding)} from ${agent.name} before issuing a new voucher batch.`);
        return;
      }

      await savePOS({
        id: editingPOSTx?.id || uid(),
        type: 'credit',
        ventureId: form.ventureId,
        branchId: '',
        date: txDate,
        description: `Hotspot vouchers on credit (${form.duration}) x${bundles} to ${agent.name}`,
        reference,
        amount,
        categoryAccountId: form.categoryAccountId,
        balanceAccountId: receivableAccountId,
        paymentMethod: 'Agent Credit',
        agentId: form.agentId,
        loanId: '',
        commission: 0,
        createdAt,
        updatedAt: new Date().toISOString(),
        posType: 'hotspot-agent-credit',
        bundles,
        duration: form.duration,
        customerName: '',
        customerPhone: '',
        note: form.note.trim(),
      });
    } else if (form.mode === 'collect-payment') {
      const outstanding = selectedAgentFinalReceivable;
      if (outstanding <= 0) {
        setError(`${agent.name} has no outstanding net receivable to collect.`);
        return;
      }
      if (!form.categoryAccountId) {
        setError('Please select an income category for agent payment.');
        return;
      }
      if (amount > outstanding && !isEditingPOS) {
        setError(`Agent payment cannot exceed amount due (${fmt(outstanding)}).`);
        return;
      }
      if (!paymentAccountId) {
        setError('Create an asset account first so collections can be posted to the correct payment account.');
        return;
      }

      await savePOS({
        id: editingPOSTx?.id || uid(),
        type: 'income',
        ventureId: form.ventureId,
        branchId: '',
        date: txDate,
        description: `Hotspot credit collection from ${agent.name}`,
        reference,
        amount,
        categoryAccountId: form.categoryAccountId,
        balanceAccountId: paymentAccountId,
        paymentMethod: form.paymentMethod,
        agentId: form.agentId,
        loanId: '',
        commission: 0,
        createdAt,
        updatedAt: new Date().toISOString(),
        posType: 'hotspot-agent-collection',
        bundles: 0,
        customerName: '',
        customerPhone: '',
        note: form.note.trim(),
      });
    } else {
      if (!form.categoryAccountId) {
        setError('Please select an income account for hotspot sales.');
        return;
      }
      if (!form.plan) {
        setError('Please select a direct-sale plan.');
        return;
      }
      if (!paymentAccountId) {
        setError('Create an asset account first so sales can be posted to the correct payment account.');
        return;
      }
      const customerBits = [form.customerName.trim(), form.customerPhone.trim()].filter(Boolean).join(' Â· ');
      const descriptionBase = `Hotspot ${form.plan} plan top-up`;
      const description = customerBits ? `${descriptionBase} (${customerBits})` : descriptionBase;
      await savePOS({
        id: editingPOSTx?.id || uid(),
        type: 'income',
        ventureId: form.ventureId,
        branchId: '',
        date: txDate,
        description,
        reference,
        amount,
        categoryAccountId: form.categoryAccountId,
        balanceAccountId: paymentAccountId,
        paymentMethod: form.paymentMethod,
        agentId: form.agentId,
        loanId: '',
        commission: 0,
        createdAt,
        updatedAt: new Date().toISOString(),
        posType: 'hotspot-balance',
        bundles: 0,
        plan: form.plan,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        note: form.note.trim(),
      });
    }

    clearPOSForm();
  };

  const recentPOSRows = useMemo(() => {
    return txs
      .filter(t => t.posType === 'hotspot-balance' || t.posType === 'hotspot-agent-credit' || t.posType === 'hotspot-agent-collection')
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 12);
  }, [txs]);

  const totals = useMemo(() => {
    return agentStats.reduce((acc, row) => {
      acc.vouchers += row.vouchersIssued;
      acc.credit += row.creditIssued;
      acc.collected += row.cashCollected;
      acc.commission += row.commission;
      acc.outstanding += row.outstanding;
      return acc;
    }, { vouchers: 0, credit: 0, collected: 0, commission: 0, outstanding: 0 });
  }, [agentStats]);

  return (
    <div>
      <div className="kpi-g" style={{marginBottom:18}}>
        <KPI label="Vouchers Issued" value={totals.vouchers} sub="Total units issued on credit" acc="var(--info)" />
        <KPI label="Credit Value" value={fmt(totals.credit)} sub="Cash value of issued vouchers" acc="var(--gold)" />
        <KPI label="Cash Collected" value={fmt(totals.collected)} sub="Collections from agents" acc="var(--ok)" />
        <KPI label="Agent Commission (10%)" value={fmt(totals.commission)} sub="Commission on voucher sales" acc="var(--warn)" />
        <KPI label="Outstanding" value={fmt(totals.outstanding)} sub="Unpaid agent balances" acc={totals.outstanding>0?'#fb923c':'var(--ok)'} />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'minmax(280px,380px) 1fr',gap:14,marginBottom:14}}>
        <div className="card">
          <div className="sec-h"><div className="sec-t">Hotspot Agent POS</div></div>
          <div className="fgrid">
            <div className="fg full">
              <label>POS Action *</label>
              <select value={form.mode} onChange={e => set('mode', e.target.value)}>
                <option value="issue-credit">Vouchers on Credit</option>
                <option value="collect-payment">Agent Payment</option>
                <option value="direct-sale">Direct Customer Sale</option>
              </select>
            </div>
            <div className="fg full">
              <label>Agent *</label>
              <select value={form.agentId} onChange={e => set('agentId', e.target.value)}>
                <option value="">- Select Agent -</option>
                {(agents || []).map(ag => <option key={ag.id} value={ag.id}>{ag.name}</option>)}
              </select>
            </div>
            <div className="fg full">
              <label>Venture *</label>
              <select value={form.ventureId} onChange={e => set('ventureId', e.target.value)}>
                <option value="">- Select Venture -</option>
                {hotspotVentures.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>{form.mode === 'collect-payment' ? 'Agent Payment Amount (UGX) *' : 'Cash Equivalent Amount (UGX) *'}</label>
              <input
                type="number"
                min="0"
                step="any"
                value={form.mode === 'issue-credit' ? computedCreditAmount : form.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="0"
                readOnly={form.mode === 'issue-credit' || (form.mode === 'collect-payment' && !isEditingPOS)}
              />
            </div>
            {form.mode === 'collect-payment' && (
              <div className="fg">
                <label>Final Amount Due (UGX)</label>
                <input value={fmt(selectedAgentFinalReceivable)} readOnly style={{color:selectedAgentFinalReceivable > 0 ? '#fb923c' : 'var(--ok)',fontWeight:600}} />
              </div>
            )}
            {form.mode === 'direct-sale' ? (
              <div className="fg">
                <label>Plan *</label>
                <select value={form.plan} onChange={e => set('plan', e.target.value)}>
                  {DIRECT_SALE_PLANS.map(plan => <option key={plan} value={plan}>{plan}</option>)}
                </select>
              </div>
            ) : form.mode !== 'collect-payment' && (
              <>
                <div className="fg">
                  <label>Duration *</label>
                  <select value={form.duration} onChange={e => set('duration', e.target.value)}>
                    {CREDIT_DURATION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="fg">
                  <label>Total Vouchers *</label>
                  <input type="number" min="1" step="1" value={form.bundles} onChange={e => set('bundles', e.target.value)} placeholder="1" />
                </div>
              </>
            )}
            {form.mode !== 'issue-credit' && (
              <div className="fg full">
                <label>Payment Method</label>
                <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                  {PAYMENT_METHODS.map(pm => <option key={pm} value={pm}>{pm}</option>)}
                </select>
              </div>
            )}
            {(form.mode === 'issue-credit' || form.mode === 'direct-sale' || form.mode === 'collect-payment') && (
              <div className="fg full">
                <label>
                  {form.mode === 'issue-credit'
                    ? 'Liability Account *'
                    : form.mode === 'collect-payment'
                      ? 'INCOME CATEGORY *'
                      : 'Income Account *'}
                </label>
                <select value={form.categoryAccountId} onChange={e => set('categoryAccountId', e.target.value)}>
                  <option value="">
                    {form.mode === 'issue-credit'
                      ? '- Select Liability Account -'
                      : '- Select Income Category -'}
                  </option>
                  {(form.mode === 'issue-credit' ? liabilityAccounts : incomeAccounts).map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                </select>
              </div>
            )}
            {form.mode === 'direct-sale' && (
              <>
                <div className="fg">
                  <label>Customer Name</label>
                  <input value={form.customerName} onChange={e => set('customerName', e.target.value)} placeholder="Optional" />
                </div>
                <div className="fg">
                  <label>Customer Phone</label>
                  <input value={form.customerPhone} onChange={e => set('customerPhone', e.target.value)} placeholder="Optional" />
                </div>
              </>
            )}
            <div className="fg full">
              <label>Note</label>
              <input value={form.note} onChange={e => set('note', e.target.value)} placeholder="Optional note" />
            </div>
          </div>

          {selectedAgentStats && (
            <div style={{marginTop:12,padding:'10px 12px',background:'var(--s3)',border:'1px solid var(--brd)',borderRadius:8,fontSize:12,color:'var(--tm)'}}>
              <div style={{marginBottom:4}}>Agent status: <strong style={{color:'var(--tb)'}}>{selectedAgentStats.agentName}</strong></div>
              <div>Vouchers issued: <span className="mono" style={{color:'var(--tb)'}}>{selectedAgentStats.vouchersIssued}</span></div>
              <div>Credit issued: <span className="mono" style={{color:'var(--gold)'}}>{fmt(selectedAgentStats.creditIssued)}</span></div>
              <div>Net receivable after commission: <span className="mono" style={{color:'var(--tb)'}}>{fmt(selectedAgentStats.netReceivable)}</span></div>
              <div>Collected: <span className="mono" style={{color:'var(--ok)'}}>{fmt(selectedAgentStats.cashCollected)}</span></div>
              <div>Commission (10%): <span className="mono" style={{color:'var(--warn)'}}>{fmt(selectedAgentStats.commission)}</span></div>
              <div>Outstanding: <span className="mono" style={{color:selectedAgentStats.outstanding>0?'#fb923c':'var(--ok)'}}>{fmt(selectedAgentStats.outstanding)}</span></div>
            </div>
          )}

          {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
          <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:14}}>
            {isEditingPOS && (
              <button className="btn bg" onClick={clearPOSForm}>Cancel Edit</button>
            )}
            <button className="btn bp" onClick={submitSale}>
              {isEditingPOS ? 'Save POS Changes' : (form.mode === 'issue-credit' ? 'Issue Voucher Batch' : form.mode === 'collect-payment' ? 'Record Agent Payment' : 'Record POS Sale')}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="sec-h"><div className="sec-t">Agent Voucher Credit Summary</div></div>
          {agentStats.length === 0 ? (
            <div className="empty"><div className="empty-ico">CR</div><p>No agent voucher credit activity yet.</p></div>
          ) : (
            <div className="tw" style={{marginBottom:14}}>
              <table>
                <thead><tr><th>Agent</th><th style={{textAlign:'right'}}>Vouchers</th><th style={{textAlign:'right'}}>Credit Value</th><th style={{textAlign:'right'}}>Collected</th><th style={{textAlign:'right'}}>Commission (10%)</th><th style={{textAlign:'right'}}>Outstanding</th></tr></thead>
                <tbody>
                  {agentStats.map(row => (
                    <tr key={row.agentId}>
                      <td style={{fontSize:12,fontWeight:500}}>{row.agentName}</td>
                      <td className="mono" style={{textAlign:'right'}}>{row.vouchersIssued}</td>
                      <td className="mono" style={{textAlign:'right',color:'var(--gold)'}}>{fmt(row.creditIssued)}</td>
                      <td className="mono" style={{textAlign:'right',color:'var(--ok)'}}>{fmt(row.cashCollected)}</td>
                      <td className="mono" style={{textAlign:'right',color:'var(--warn)'}}>{fmt(row.commission)}</td>
                      <td className="mono" style={{textAlign:'right',color:row.outstanding>0?'#fb923c':'var(--ok)',fontWeight:600}}>{fmt(row.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="sec-h"><div className="sec-t">Recent Hotspot POS Entries</div></div>
          {recentPOSRows.length === 0 ? (
            <div className="empty"><div className="empty-ico">POS</div><p>No hotspot POS transactions yet.</p></div>
          ) : (
            <div className="tw">
              <table>
                <thead><tr><th>Date</th><th>Description</th><th>Agent</th><th>Type</th><th>Payment</th><th>Amount</th><th></th></tr></thead>
                <tbody>
                  {recentPOSRows.map(t => {
                    const ag = (agents || []).find(x => x.id === t.agentId);
                    const typeLabel = t.posType === 'hotspot-agent-credit' ? 'Credit Issue' : t.posType === 'hotspot-agent-collection' ? 'Collection' : 'Direct Sale';
                    return (
                      <tr key={t.id}>
                        <td className="mono" style={{fontSize:12}}>{t.date}</td>
                        <td style={{fontSize:12}}>{t.description}</td>
                        <td style={{fontSize:12}}>{ag?.name || '-'}</td>
                        <td style={{fontSize:12,color:'var(--tm)'}}>{typeLabel}</td>
                        <td style={{color:'var(--tm)',fontSize:12}}>{t.paymentMethod}</td>
                        <td className="amt-p">+{fmt(t.amount)}</td>
                        <td style={{display:'flex',gap:6}}>
                          <button className="btn bg bs" onClick={() => startEditPOS(t)} title="Edit POS Entry">Edit</button>
                          <button className="btn bd bs" onClick={() => deletePOS(t)} title="Delete POS Entry">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   ADD TRANSACTION MODAL
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function AddTransactionModal({ ventures, accounts, agents, loans, items, initialTransaction = null, onClose, onSave }) {
  const incomeAccounts  = accounts.filter(a=>a.type==='income');
  const expenseAccounts = accounts.filter(a=>a.type==='expense');
  const normalizedItems = Array.isArray(items) ? items : [];
  const isEditing = Boolean(initialTransaction?.id);
  const [form, setForm] = useState(() => ({
    type: initialTransaction?.type || 'income',
    ventureId: initialTransaction?.ventureId || ventures[0]?.id || '',
    branchId: initialTransaction?.branchId || '',
    date: initialTransaction?.date || tod(),
    description: initialTransaction?.description || '',
    reference: initialTransaction?.reference || '',
    amount: initialTransaction?.amount === undefined || initialTransaction?.amount === null ? '' : String(initialTransaction.amount),
    categoryAccountId: initialTransaction?.categoryAccountId || '',
    paymentMethod: initialTransaction?.paymentMethod || 'Cash',
    agentId: initialTransaction?.agentId || '',
    loanId: initialTransaction?.loanId || '',
    itemId: initialTransaction?.itemId || '',
  }));
  const [error, setError] = useState('');
  const set = (k,v) => {
    setForm(f=>({...f,[k]:v}));
    if (error) setError('');
  };
  const selectedVenture = ventures.find(v=>v.id===form.ventureId);
  const branches        = selectedVenture?.branches||[];
  const catAccounts     = form.type==='income' ? incomeAccounts : expenseAccounts;
  const isLoanCat       = form.type==='expense' && isLoanPaymentExpenseAccount(accounts, form.categoryAccountId);
  const ventureLoans    = (loans||[]).filter(l=>!l.ventureId||l.ventureId===form.ventureId);
  const typeItems       = normalizedItems.filter(item => item.type === form.type);

  const applyItemTemplate = itemId => {
    const selectedItem = normalizedItems.find(item => item.id === itemId);
    if (!selectedItem) {
      set('itemId', '');
      return;
    }

    const fallbackCategory = (selectedItem.type === 'income' ? incomeAccounts : expenseAccounts)[0]?.id || '';
    setForm(prev => ({
      ...prev,
      type: selectedItem.type === 'expense' ? 'expense' : 'income',
      itemId: selectedItem.id,
      categoryAccountId: selectedItem.categoryAccountId || fallbackCategory,
      description: selectedItem.description || selectedItem.name || prev.description,
      amount: selectedItem.defaultAmount === '' || selectedItem.defaultAmount === null || selectedItem.defaultAmount === undefined
        ? prev.amount
        : String(selectedItem.defaultAmount),
    }));
    if (error) setError('');
  };

  const handleSave = () => {
    if (!form.ventureId)            { setError('Please select a venture.'); return; }
    if (!form.description.trim())   { setError('Please enter a description.'); return; }
    if (!form.amount||Number(form.amount)<=0) { setError('Please enter a valid amount.'); return; }
    if (!form.categoryAccountId)    { setError('Please select a category account.'); return; }
    const paymentAccountId = resolvePaymentMethodAccountId(accounts, form.paymentMethod);
    if (!paymentAccountId)          { setError('Please create at least one asset account before recording transactions.'); return; }
    setError('');
    const ag = (agents||[]).find(a=>a.id===form.agentId);
    const commissionRate = (ag && form.type==='income' && isCommissionEligibleMember(ag.role)) ? 0.10 : 0;
    const commission = Math.round(Number(form.amount) * commissionRate);
    onSave({
      id:initialTransaction?.id || uid(), type:form.type, ventureId:form.ventureId, branchId:form.branchId||'',
      date:form.date, description:form.description.trim(), reference:form.reference.trim(),
      amount:Number(form.amount), categoryAccountId:form.categoryAccountId,
      balanceAccountId: paymentAccountId,
      paymentMethod:form.paymentMethod, agentId:form.agentId||'', loanId:form.loanId||'',
      itemId:form.itemId || '',
      commission, createdAt:initialTransaction?.createdAt || new Date().toISOString(), updatedAt:new Date().toISOString(),
    });
  };

  return (
    <Modal title={isEditing ? "Edit Transaction" : "Record New Transaction"} onClose={onClose} wide>
      <div className="fgrid">
        {/* Income / Expense toggle */}
        <div className="fg full" style={{display:'flex',gap:0,background:'var(--s3)',borderRadius:8,overflow:'hidden',border:'1px solid var(--brd)'}}>
          <button className={`btn${form.type==='income'?' bp':' bg'}`} style={{flex:1,borderRadius:0}} onClick={()=>set('type','income')}>+ Income</button>
          <button className={`btn${form.type==='expense'?' bdr':' bg'}`} style={{flex:1,borderRadius:0}} onClick={()=>set('type','expense')}>- Expense</button>
        </div>
        <div className="fg"><label>Date *</label><input type="date" value={form.date} onChange={e=>set('date',e.target.value)}/></div>
        <div className="fg">
          <label>Venture *</label>
          <select value={form.ventureId} onChange={e=>{set('ventureId',e.target.value);set('branchId','');}}>
            <option value="">- Select Venture -</option>
            {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        {normalizedItems.length > 0 && (
          <div className="fg full">
            <label>Item Template (optional)</label>
            <select value={form.itemId} onChange={e=>applyItemTemplate(e.target.value)}>
              <option value="">- Select Item -</option>
              {typeItems.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}{item.itemCategory ? ` [${item.itemCategory}]` : ''}{item.defaultAmount !== '' && item.defaultAmount !== null && item.defaultAmount !== undefined ? ` - ${fmt(item.defaultAmount)}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {branches.length>0 && (
          <div className="fg full">
            <label>Branch / Location</label>
            <select value={form.branchId} onChange={e=>set('branchId',e.target.value)}>
              <option value="">- All Branches -</option>
              {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="fg full"><label>Description *</label><input placeholder="What is this transaction for?" value={form.description} onChange={e=>set('description',e.target.value)}/></div>
        <div className="fg"><label>Amount (UGX) *</label><input type="number" min="0" step="any" placeholder="0" value={form.amount} onChange={e=>set('amount',e.target.value)}/></div>
        <div className="fg">
          <label>Payment Method</label>
          <select value={form.paymentMethod} onChange={e=>set('paymentMethod',e.target.value)}>
            {['Cash','Mobile Money','Bank Transfer','Cheque','Credit Card','Other'].map(m=><option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="fg full">
          <label>{form.type==='income'?'Income':'Expense'} Category *</label>
          <select value={form.categoryAccountId} onChange={e=>set('categoryAccountId',e.target.value)}>
            <option value="">- Select Account -</option>
            {catAccounts.map(a=><option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
          </select>
        </div>
        {form.type==='income' && agents && agents.length>0 && (
          <div className="fg full">
            <label>Team Member (optional)</label>
            <select value={form.agentId} onChange={e=>set('agentId',e.target.value)}>
              <option value="">- No Team Member -</option>
              {agents.map(a=><option key={a.id} value={a.id}>{a.name} ({a.role}) - {getTeamCategoryFromRole(a.role)}</option>)}
            </select>
          </div>
        )}
        {isLoanCat && ventureLoans.length>0 && (
          <div className="fg full">
            <label>Link to Loan (optional)</label>
            <select value={form.loanId} onChange={e=>set('loanId',e.target.value)}>
              <option value="">- Unlinked -</option>
              {ventureLoans.map(l=><option key={l.id} value={l.id}>{l.name}{l.lender?` (${l.lender})`:''}</option>)}
            </select>
          </div>
        )}
        <div className="fg full"><label>Reference / Invoice No.</label><input placeholder="Optional reference number..." value={form.reference} onChange={e=>set('reference',e.target.value)}/></div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className={`btn ${form.type==='income'?'bp':'bdr'}`} onClick={handleSave}>
          {isEditing ? 'Save Changes' : (form.type==='income' ? '+ Record Income' : '- Record Expense')}
        </button>
      </div>
    </Modal>
  );
}

function AddAgentModal({ ventures, onClose, onSave }) {
  const [form, setForm] = useState({ name:'', role:'', phone:'', ventureId:'', branchId:'' });
  const [error, setError] = useState('');
  const selectedVenture = ventures.find(v => v.id === form.ventureId);
  const availableBranches = selectedVenture?.branches || [];
  const set = (k,v) => {
    setForm(f=>{
      if (k === 'ventureId') return { ...f, ventureId:v, branchId:'' };
      return { ...f, [k]:v };
    });
    if (error) setError('');
  };
  const handleSave = () => {
    if (!form.name.trim() || !form.role.trim() || !form.phone.trim()) { setError('Please fill all required fields.'); return; }
    setError('');
    onSave({ id:uid(), name:form.name.trim(), role:form.role.trim(), phone:form.phone.trim(), ventureId:form.ventureId||'', branchId:form.branchId||'', commission:0 });
  };
  return (
    <Modal title="Add Agent / Staff Member" onClose={onClose}>
      <div className="fgrid">
        <div className="fg full"><label>Full Name *</label><input autoFocus placeholder="Full name..." value={form.name} onChange={e=>set('name',e.target.value)}/></div>
        <div className="fg"><label>Role *</label><input placeholder="e.g. Agent, Manager, Cashier..." value={form.role} onChange={e=>set('role',e.target.value)}/></div>
        <div className="fg"><label>Phone *</label><input placeholder="+256..." value={form.phone} onChange={e=>set('phone',e.target.value)}/></div>
        <div className="fg"><label>Business Venture</label>
          <select value={form.ventureId} onChange={e=>set('ventureId',e.target.value)}>
            <option value="">- Unassigned -</option>
            {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="fg"><label>Branch</label>
          <select value={form.branchId} onChange={e=>set('branchId',e.target.value)} disabled={!form.ventureId || availableBranches.length===0}>
            <option value="">- Unassigned -</option>
            {availableBranches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={handleSave}>Add Agent</button>
      </div>
    </Modal>
  );
}

function EditAgentModal({ agent, ventures, onClose, onSave }) {
  const [form, setForm] = useState({ name:agent.name, role:agent.role, phone:agent.phone, ventureId:agent.ventureId||'', branchId:agent.branchId||'' });
  const [error, setError] = useState('');
  const selectedVenture = ventures.find(v => v.id === form.ventureId);
  const availableBranches = selectedVenture?.branches || [];
  const set = (k,v) => {
    setForm(f=>{
      if (k === 'ventureId') return { ...f, ventureId:v, branchId:'' };
      return { ...f, [k]:v };
    });
    if (error) setError('');
  };
  const handleSave = () => {
    if (!form.name.trim() || !form.role.trim()) { setError('Name and role are required.'); return; }
    setError('');
    onSave({ ...agent, ...form });
  };
  return (
    <Modal title="Edit Agent / Staff" onClose={onClose}>
      <div className="fgrid">
        <div className="fg full"><label>Full Name</label><input value={form.name} onChange={e=>set('name',e.target.value)}/></div>
        <div className="fg"><label>Role</label><input value={form.role} onChange={e=>set('role',e.target.value)}/></div>
        <div className="fg"><label>Phone</label><input value={form.phone} onChange={e=>set('phone',e.target.value)}/></div>
        <div className="fg"><label>Business Venture</label>
          <select value={form.ventureId} onChange={e=>set('ventureId',e.target.value)}>
            <option value="">- Unassigned -</option>
            {ventures.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="fg"><label>Branch</label>
          <select value={form.branchId} onChange={e=>set('branchId',e.target.value)} disabled={!form.ventureId || availableBranches.length===0}>
            <option value="">- Unassigned -</option>
            {availableBranches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={handleSave}>Save Changes</button>
      </div>
    </Modal>
  );
}

function EditVentureModal({ venture, onClose, onSave }) {
  const [form, setForm] = useState({ name:venture.name, description:venture.description||'', color:venture.color });
  const [error, setError] = useState('');
  const set = (k,v) => {
    setForm(f=>({...f,[k]:v}));
    if (error) setError('');
  };
  const handleSave = () => {
    if (!form.name.trim()) { setError('Venture name is required.'); return; }
    setError('');
    onSave({ ...venture, ...form });
  };
  return (
    <Modal title="Edit Venture" onClose={onClose}>
      <div className="fgrid">
        <div className="fg full"><label>Venture Name *</label><input value={form.name} onChange={e=>set('name',e.target.value)}/></div>
        <div className="fg full"><label>Description</label><input value={form.description} onChange={e=>set('description',e.target.value)}/></div>
        <div className="fg full">
          <label>Colour Tag</label>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:4}}>
            {PALETTE.map(c=>(<div key={c} onClick={()=>set('color',c)} style={{width:28,height:28,borderRadius:'50%',background:c,cursor:'pointer',border:`3px solid ${form.color===c?'#fff':'transparent'}`,transition:'border .15s'}}/>))}
          </div>
        </div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={handleSave}>Save Changes</button>
      </div>
    </Modal>
  );
}

function AddVentureModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name:'', description:'', color: PALETTE[Math.floor(Math.random()*PALETTE.length)] });
  const [error, setError] = useState('');
  const set = (k,v) => {
    setForm(f=>({...f,[k]:v}));
    if (error) setError('');
  };
  const handleSave = () => {
    if (!form.name.trim()) { setError('Venture name is required.'); return; }
    setError('');
    onSave({ id:uid(), ...form, branches:[] });  // new ventures start with no branches
  };
  return (
    <Modal title="Add New Venture" onClose={onClose}>
      <div className="fgrid">
        <div className="fg full"><label>Venture Name *</label><input placeholder="e.g. Restaurant, Salon, Farm..." value={form.name} onChange={e=>set('name',e.target.value)}/></div>
        <div className="fg full"><label>Description</label><input placeholder="Brief description of this business..." value={form.description} onChange={e=>set('description',e.target.value)}/></div>
        <div className="fg full">
          <label>Colour Tag</label>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:4}}>
            {PALETTE.map(c=>(
              <div key={c} onClick={()=>set('color',c)} style={{width:28,height:28,borderRadius:'50%',background:c,cursor:'pointer',border:`3px solid ${form.color===c?'#fff':'transparent'}`,transition:'border .15s'}}/>
            ))}
          </div>
        </div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={handleSave}>Create Venture</button>
      </div>
    </Modal>
  );
}

function AddAccountModal({ onClose, onSave }) {
  const [form, setForm] = useState({ code:'', name:'', type:'asset' });
  const [error, setError] = useState('');
  const set = (k,v) => {
    setForm(f=>({...f,[k]:v}));
    if (error) setError('');
  };
  const normalMap = { asset:'debit', liability:'credit', equity:'credit', income:'credit', expense:'debit' };
  const handleSave = () => {
    if (!form.code.trim() || !form.name.trim()) { setError('Code and name are required.'); return; }
    setError('');
    onSave({ id:uid(), ...form, normal: normalMap[form.type] });
  };
  return (
    <Modal title="Add Account to Chart of Accounts" onClose={onClose}>
      <div className="fgrid">
        <div className="fg"><label>Account Code *</label><input placeholder="e.g. 1500, 4300..." value={form.code} onChange={e=>set('code',e.target.value)}/></div>
        <div className="fg">
          <label>Account Type *</label>
          <select value={form.type} onChange={e=>set('type',e.target.value)}>
            {ACC_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
        </div>
        <div className="fg full"><label>Account Name *</label><input placeholder="Descriptive account name..." value={form.name} onChange={e=>set('name',e.target.value)}/></div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div style={{marginTop:12,padding:'9px 12px',background:'var(--s3)',borderRadius:6,fontSize:12,color:'var(--tm)'}}>
        Normal balance for <strong style={{color:'var(--txt)'}}>{form.type}</strong> accounts is <strong style={{color:'var(--gold)'}}>{normalMap[form.type]}</strong>.
      </div>
      <div className="modal-a">
        <button className="btn bg" onClick={onClose}>Cancel</button>
        <button className="btn bp" onClick={handleSave}>Add Account</button>
      </div>
    </Modal>
  );
}

function AuthScreen({ mode, busy, error, info, profile, hasOfflineAuth, offlineAccessGranted, onModeChange, onSubmit, onRequestReset, onOfflineUnlock, onOfflineSignOut }) {
  const [form, setForm] = useState({ identifier: profile?.email || '', password:'' });

  useEffect(() => {
    setForm(prev => ({ ...prev, identifier: prev.identifier || profile?.email || '' }));
  }, [profile?.email]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const isResetMode = mode === 'reset';
  const isOfflineMode = mode === 'offline';

  const handleSubmit = () => {
    if (!form.identifier.trim()) return;
    if (isResetMode) {
      onRequestReset(form.identifier.trim());
      return;
    }
    if (isOfflineMode) {
      onOfflineUnlock(form.identifier.trim());
      return;
    }
    if (!form.password) return;
    onSubmit(form.identifier.trim(), form.password, 'password');
  };

  const handleMagicLink = () => {
    if (!form.identifier.trim()) return;
    onSubmit(form.identifier.trim(), '', 'otp');
  };

  return (
    <div style={{minHeight:'100vh',display:'grid',gridTemplateColumns:'minmax(280px, 480px) minmax(320px, 1fr)',background:'linear-gradient(135deg,#07111f 0%,#0c1730 50%,#111f42 100%)'}}>
      <div style={{padding:'56px 48px',display:'flex',flexDirection:'column',justifyContent:'space-between',borderRight:'1px solid rgba(255,255,255,.08)',background:'radial-gradient(circle at top left, rgba(201,168,76,.18), transparent 36%)'}}>
        <div>
          <div style={{fontFamily:'Playfair Display,serif',fontSize:36,color:'var(--gold)',marginBottom:14}}>Financial Management System (FMS)</div>
          <div style={{fontSize:14,color:'var(--tm)',maxWidth:360,lineHeight:1.7}}>
            Sign in to unlock secure Supabase sync under row-level security. Your financial data will stay isolated per authenticated user.
          </div>
        </div>
        <div style={{display:'grid',gap:12}}>
          <div style={{padding:'16px 18px',border:'1px solid rgba(255,255,255,.08)',borderRadius:12,background:'rgba(6,10,26,.32)'}}>
            <div style={{fontSize:11,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Cloud Security</div>
            <div style={{fontSize:14,color:'var(--txt)',lineHeight:1.6}}>Each synced row is protected by Supabase RLS policies tied to your signed-in account.</div>
          </div>
          {profile?.email && (
            <div style={{padding:'16px 18px',border:'1px solid rgba(255,255,255,.08)',borderRadius:12,background:'rgba(6,10,26,.32)'}}>
              <div style={{fontSize:11,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Last Cloud User</div>
              <div style={{fontSize:15,color:'var(--txt)',fontWeight:600}}>{profile.email}</div>
              {profile.lastSignInAt && <div style={{fontSize:12,color:'var(--tm)',marginTop:6}}>Last sign-in: {new Date(profile.lastSignInAt).toLocaleString()}</div>}
            </div>
          )}
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'48px 32px'}}>
        <div style={{width:'100%',maxWidth:460,padding:'28px 28px 24px',border:'1px solid rgba(255,255,255,.08)',borderRadius:16,background:'rgba(7,17,31,.88)',boxShadow:'0 20px 45px rgba(0,0,0,.28)'}}>
          <div style={{display:'flex',gap:8,marginBottom:18,flexWrap:'wrap'}}>
            <button className={mode === 'sign-in' ? 'btn bp' : 'btn bg'} onClick={()=>onModeChange('sign-in')}>Sign In</button>
            <button className={mode === 'reset' ? 'btn bp' : 'btn bg'} onClick={()=>onModeChange('reset')}>Reset Password</button>
            {hasOfflineAuth && <button className={mode === 'offline' ? 'btn bp' : 'btn bg'} onClick={()=>onModeChange('offline')}>Unlock Offline</button>}
          </div>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:24,fontWeight:700,color:'var(--txt)',marginBottom:6}}>{mode === 'reset' ? 'Reset your password' : mode === 'offline' ? 'Unlock local data offline' : 'Welcome back'}</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6}}>{mode === 'reset' ? 'Enter your email or username to send a password reset email.' : mode === 'offline' ? 'Enter your email or username from your last successful online sign-in to unlock local data while offline.' : 'Enter email/username and password to sign in. Magic link remains available as fallback.'}</div>
          </div>

          <div className="fgrid">
            <div className="fg full"><label>Email or Username</label><input value={form.identifier} onChange={e=>set('identifier', e.target.value)} placeholder="name@example.com or username" autoFocus /></div>
            {!isResetMode && !isOfflineMode && <div className="fg full"><label>Password</label><input type="password" value={form.password} onChange={e=>set('password', e.target.value)} placeholder="Your Supabase password" /></div>}
          </div>

          {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
          {info && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(37,99,235,.12)',border:'1px solid rgba(37,99,235,.35)',borderRadius:6,fontSize:12,color:'var(--info)'}}>{info}</div>}

          <div style={{marginTop:16,display:'flex',gap:10,flexWrap:'wrap'}}>
            <button className="btn bp" onClick={handleSubmit} disabled={busy || !form.identifier.trim() || (!isResetMode && !isOfflineMode && !form.password)}>{busy ? 'Please wait...' : mode === 'reset' ? 'Send Reset Email' : mode === 'offline' ? 'Unlock Offline' : 'Sign In'}</button>
            {!isResetMode && !isOfflineMode && <button className="btn bg" onClick={handleMagicLink} disabled={busy || !form.identifier.trim()}>Use Magic Link Instead</button>}
            {isOfflineMode && offlineAccessGranted && <button className="btn bg" onClick={onOfflineSignOut} disabled={busy}>Sign Out Offline</button>}
          </div>

          <div style={{marginTop:16,fontSize:12,color:'var(--tm)',lineHeight:1.6}}>
            Local storage still works offline. After one successful online sign-in, this device can unlock local data offline using your email or username.
          </div>
          <div style={{marginTop:10,fontSize:12,color:'var(--tm)',lineHeight:1.6}}>Tip: Work locally with any identifier. Sign in to Supabase online to enable cloud sync and backup.</div>
        </div>
      </div>
    </div>
  );
}

function ComprehensiveOnboarding({ currentStep, onStepChange, onComplete, onExit, initialVenture, initialAccounts, initialAgents, initialDatabaseConfig }) {
  const [userEmail, setUserEmail] = useState('');
  const [provider, setProvider] = useState(initialDatabaseConfig?.provider || 'supabase');
  const [venture, setVenture] = useState(initialVenture || EMPTY_VENTURE);
  const [agents, setAgents] = useState(initialAgents || []);
  const [agentForm, setAgentForm] = useState({ name: '', role: 'Agent', phone: '', commission: 0 });
  const [databaseConfig, setDatabaseConfig] = useState(() => ({
    ...DEFAULT_DATABASE_CONFIG,
    ...(initialDatabaseConfig || {}),
    supabase: {
      ...DEFAULT_DATABASE_CONFIG.supabase,
      ...(initialDatabaseConfig?.supabase || {}),
    },
    firebase: {
      ...DEFAULT_DATABASE_CONFIG.firebase,
      ...(initialDatabaseConfig?.firebase || {}),
    },
    appwrite: {
      ...DEFAULT_DATABASE_CONFIG.appwrite,
      ...(initialDatabaseConfig?.appwrite || {}),
    },
    custom: {
      ...DEFAULT_DATABASE_CONFIG.custom,
      ...(initialDatabaseConfig?.custom || {}),
    },
  }));
  const [copyMessage, setCopyMessage] = useState('');
  const accountsReview = Array.isArray(initialAccounts) ? initialAccounts : DEF_ACCOUNTS;

  const guide = DATABASE_PROVIDER_GUIDES[provider] || DATABASE_PROVIDER_GUIDES.supabase;

  const handleAddAgent = () => {
    if (agentForm.name.trim()) {
      setAgents([...agents, { id: uid(), ...agentForm }]);
      setAgentForm({ name: '', role: 'Agent', phone: '', commission: 0 });
    }
  };

  const handleRemoveAgent = (id) => {
    setAgents(agents.filter(a => a.id !== id));
  };

  const updateProviderField = (providerName, field, value) => {
    setDatabaseConfig(prev => ({
      ...prev,
      provider: providerName,
      [providerName]: {
        ...(prev[providerName] || {}),
        [field]: value,
      },
    }));
  };

  const copyText = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(message);
      setTimeout(() => setCopyMessage(''), 2400);
    } catch {
      setCopyMessage('Copy failed. Select text manually and copy.');
    }
  };

  const supabaseEnvSnippet = [
    `REACT_APP_SUPABASE_URL=${databaseConfig.supabase?.url || '<your-supabase-url>'}`,
    `REACT_APP_SUPABASE_ANON_KEY=${databaseConfig.supabase?.anonKey || '<your-anon-key>'}`,
    `REACT_APP_SUPABASE_SCHEMA=${databaseConfig.supabase?.schema || 'public'}`,
    'REACT_APP_SUPABASE_SYNC_ENABLED=true',
  ].join('\n');

  const handleFinish = () => {
    if (currentStep === 2 && !userEmail.trim()) {
      alert('Please enter a user identifier or email');
      return;
    }
    if (currentStep === 2 && provider === 'supabase') {
      const hasUrl = Boolean(databaseConfig.supabase?.url?.trim());
      const hasAnon = Boolean(databaseConfig.supabase?.anonKey?.trim());
      if (!hasUrl || !hasAnon) {
        alert('Enter Supabase URL and anon key to link Supabase during onboarding.');
        return;
      }
    }
    if (currentStep === 4 && !venture.name.trim()) {
      alert('Please enter a venture name');
      return;
    }
    if (currentStep === 5) {
      onComplete({
        email: userEmail,
        provider,
        databaseConfig: {
          ...databaseConfig,
          provider,
        },
        venture,
        accounts: accountsReview,
        agents,
      });
    } else {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) onStepChange(currentStep - 1);
  };

  return (
    <div style={{minHeight:'100vh',display:'grid',placeItems:'center',padding:24,background:'linear-gradient(135deg,#07111f 0%,#0c1730 50%,#111f42 100%)'}}>
      <div style={{width:'100%',maxWidth:800,padding:32,border:'1px solid rgba(255,255,255,.08)',borderRadius:16,background:'rgba(7,17,31,.92)',boxShadow:'0 20px 45px rgba(0,0,0,.28)'}}>
        {/* Progress indicator */}
        <div style={{display:'flex',gap:8,marginBottom:32,justifyContent:'center'}}>
          {[0,1,2,3,4,5].map(step => (
            <div
              key={step}
              style={{
                width:40,height:40,borderRadius:'50%',display:'grid',placeItems:'center',
                background: step < currentStep ? 'var(--gold)' : step === currentStep ? 'rgba(201,168,76,.4)' : 'rgba(201,168,76,.2)',
                border: step === currentStep ? '2px solid var(--gold)' : 'none',
                color: step <= currentStep ? 'var(--txt)' : 'var(--tm)',
                fontWeight:600,fontSize:14
              }}
            >
              {step < currentStep ? 'OK' : step + 1}
            </div>
          ))}
        </div>

        {/* Step 0: Welcome Video */}
        {currentStep === 0 && (
          <>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)',marginBottom:8}}>Welcome to Financial Management System (FMS)</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              Discover how Financial Management System (FMS) helps you manage your finances with ease, whether online or offline.
            </div>
            
            <FeatureIntroPlayer />

            {/* Features highlight */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))',gap:12,marginBottom:24}}>
              {[
                {icon:'DB',title:'Dashboard',desc:'Real-time financial overview'},
                {icon:'TX',title:'Transactions',desc:'Track all financial activity'},
                {icon:'RP',title:'Reports',desc:'P&L, Balance Sheet & more'},
                {icon:'MV',title:'Multi-Venture',desc:'Manage multiple businesses'},
                {icon:'SC',title:'Secure',desc:'End-to-end encrypted data'},
                {icon:'CL',title:'Cloud Sync',desc:'Optional online backup'},
              ].map((feature, i) => (
                <div key={i} style={{padding:'12px 14px',background:'rgba(45,212,160,.06)',border:'1px solid rgba(45,212,160,.2)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:20,marginBottom:4}}>{feature.icon}</div>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--gold)',marginBottom:2}}>{feature.title}</div>
                  <div style={{fontSize:10,color:'var(--tm)',lineHeight:1.4}}>{feature.desc}</div>
                </div>
              ))}
            </div>

            {/* Key capabilities list */}
            <div style={{padding:'16px',background:'rgba(96,165,250,.08)',border:'1px solid rgba(96,165,250,.2)',borderRadius:10,marginBottom:24}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--txt)',marginBottom:10}}>What You'll Get:</div>
              <div style={{display:'grid',gap:8,fontSize:12,color:'var(--tm)',lineHeight:1.6}}>
                <div>[OK] Works offline or online with optional cloud backup</div>
                <div>[OK] Track transactions across multiple business ventures</div>
                <div>[OK] Professional financial reports (P&L, Balance Sheet, Cash Flow)</div>
                <div>[OK] Manage agents & staff with commission tracking</div>
                <div>[OK] Double-entry accounting with full audit trail</div>
                <div>[OK] Secure data sync when you're connected</div>
              </div>
            </div>
          </>
        )}

        {/* Step 1: User Setup */}
        {currentStep === 1 && (
          <>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)',marginBottom:8}}>Welcome to Financial Management System (FMS)</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              Let's set up your financial management account. Start by creating a user identifier or entering your email.
            </div>
            <div style={{fontSize:12,color:'var(--tm)',marginBottom:8,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>User Identifier or Email</div>
            <input
              type="text"
              placeholder="john@example.com or john_local"
              value={userEmail}
              onChange={e => setUserEmail(e.target.value)}
              style={{width:'100%',padding:'12px 14px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:14,marginBottom:16,fontFamily:'IBM Plex Mono, monospace'}}
              onKeyPress={e => e.key === 'Enter' && handleFinish()}
            />
            <div style={{padding:'12px 14px',background:'rgba(45,212,160,.08)',border:'1px solid rgba(45,212,160,.25)',borderRadius:8,fontSize:12,color:'#b7f0df',lineHeight:1.6}}>
              <strong>Tip:</strong> Use an email for cloud sync, or any name for local-only mode. You can sign in to cloud services later from the File menu.
            </div>
          </>
        )}

        {/* Step 2: Database Provider */}
        {currentStep === 2 && (
          <>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,marginBottom:8}}>
              <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)'}}>Choose Your Database</div>
              <button className="btn bg" onClick={onExit}>Exit Guide</button>
            </div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              Financial Management System (FMS) works offline by default. Select an optional cloud database for sync and backup, then add credentials to link it during onboarding.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:10,marginBottom:24}}>
              {[
                ['supabase', 'Supabase'],
                ['firebase', 'Firebase'],
                ['appwrite', 'Appwrite'],
                ['custom', 'Custom API'],
                ['local', 'Local Only'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  className={provider === value ? 'btn bp' : 'btn bg'}
                  onClick={() => {
                    setProvider(value);
                    setDatabaseConfig(prev => ({ ...prev, provider: value }));
                  }}
                  style={{justifyContent:'center'}}
                >
                  {label}
                </button>
              ))}
            </div>

            {provider === 'supabase' && (
              <div style={{marginBottom:16,padding:'14px 16px',border:'1px solid rgba(45,212,160,.28)',borderRadius:10,background:'rgba(45,212,160,.08)'}}>
                <div style={{fontSize:14,fontWeight:700,color:'var(--txt)',marginBottom:10}}>Link Supabase Credentials</div>
                <div style={{display:'grid',gap:10}}>
                  <div>
                    <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Supabase Project URL</div>
                    <input
                      value={databaseConfig.supabase?.url || ''}
                      onChange={e => updateProviderField('supabase', 'url', e.target.value)}
                      placeholder="https://your-project-ref.supabase.co"
                      style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}}
                    />
                  </div>
                  <div>
                    <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Supabase Anon Key</div>
                    <textarea
                      value={databaseConfig.supabase?.anonKey || ''}
                      onChange={e => updateProviderField('supabase', 'anonKey', e.target.value)}
                      placeholder="eyJhbGciOi..."
                      rows={3}
                      style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:12,fontFamily:'IBM Plex Mono,monospace',resize:'vertical'}}
                    />
                  </div>
                  <div>
                    <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Schema</div>
                    <input
                      value={databaseConfig.supabase?.schema || 'public'}
                      onChange={e => updateProviderField('supabase', 'schema', e.target.value)}
                      placeholder="public"
                      style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}}
                    />
                  </div>
                </div>

                <div style={{marginTop:12,display:'flex',gap:8,flexWrap:'wrap'}}>
                  <button className="btn bg" onClick={() => copyText(supabaseEnvSnippet, '.env credentials copied')}>
                    Copy .env Snippet
                  </button>
                  <button className="btn bg" onClick={() => copyText(SUPABASE_ONBOARDING_SQL_SCRIPT, 'Supabase SQL script copied')}>
                    Copy SQL Script
                  </button>
                </div>

                {copyMessage && (
                  <div style={{marginTop:10,padding:'8px 10px',border:'1px solid rgba(45,212,160,.35)',borderRadius:8,fontSize:12,color:'#b7f0df',background:'rgba(45,212,160,.10)'}}>{copyMessage}</div>
                )}

                <div style={{marginTop:12}}>
                  <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>.env Preview</div>
                  <textarea
                    readOnly
                    rows={5}
                    value={supabaseEnvSnippet}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.12)',borderRadius:8,background:'rgba(0,0,0,.26)',color:'#d7e6ff',fontSize:11,fontFamily:'IBM Plex Mono,monospace',resize:'vertical'}}
                  />
                </div>

                <div style={{marginTop:12}}>
                  <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>SQL for Supabase SQL Editor (Includes RLS Policies)</div>
                  <textarea
                    readOnly
                    rows={12}
                    value={SUPABASE_ONBOARDING_SQL_SCRIPT}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.12)',borderRadius:8,background:'rgba(0,0,0,.26)',color:'#d7e6ff',fontSize:11,fontFamily:'IBM Plex Mono,monospace',resize:'vertical'}}
                  />
                </div>
              </div>
            )}

            {provider === 'firebase' && (
              <div style={{marginBottom:16,padding:'14px 16px',border:'1px solid rgba(255,255,255,.08)',borderRadius:10,background:'rgba(255,255,255,.02)'}}>
                <div style={{fontSize:14,fontWeight:700,color:'var(--txt)',marginBottom:10}}>Firebase Credentials</div>
                <div style={{display:'grid',gap:10}}>
                  <input value={databaseConfig.firebase?.apiKey || ''} onChange={e => updateProviderField('firebase', 'apiKey', e.target.value)} placeholder="API Key" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.firebase?.authDomain || ''} onChange={e => updateProviderField('firebase', 'authDomain', e.target.value)} placeholder="Auth Domain" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.firebase?.projectId || ''} onChange={e => updateProviderField('firebase', 'projectId', e.target.value)} placeholder="Project ID" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                </div>
              </div>
            )}

            {provider === 'appwrite' && (
              <div style={{marginBottom:16,padding:'14px 16px',border:'1px solid rgba(255,255,255,.08)',borderRadius:10,background:'rgba(255,255,255,.02)'}}>
                <div style={{fontSize:14,fontWeight:700,color:'var(--txt)',marginBottom:10}}>Appwrite Credentials</div>
                <div style={{display:'grid',gap:10}}>
                  <input value={databaseConfig.appwrite?.endpoint || ''} onChange={e => updateProviderField('appwrite', 'endpoint', e.target.value)} placeholder="Endpoint URL" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.appwrite?.projectId || ''} onChange={e => updateProviderField('appwrite', 'projectId', e.target.value)} placeholder="Project ID" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.appwrite?.databaseId || ''} onChange={e => updateProviderField('appwrite', 'databaseId', e.target.value)} placeholder="Database ID" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.appwrite?.apiKey || ''} onChange={e => updateProviderField('appwrite', 'apiKey', e.target.value)} placeholder="API Key" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                </div>
              </div>
            )}

            {provider === 'custom' && (
              <div style={{marginBottom:16,padding:'14px 16px',border:'1px solid rgba(255,255,255,.08)',borderRadius:10,background:'rgba(255,255,255,.02)'}}>
                <div style={{fontSize:14,fontWeight:700,color:'var(--txt)',marginBottom:10}}>Custom API Credentials</div>
                <div style={{display:'grid',gap:10}}>
                  <input value={databaseConfig.custom?.baseUrl || ''} onChange={e => updateProviderField('custom', 'baseUrl', e.target.value)} placeholder="Base API URL" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                  <input value={databaseConfig.custom?.apiKey || ''} onChange={e => updateProviderField('custom', 'apiKey', e.target.value)} placeholder="API Key or Bearer token" style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}} />
                </div>
              </div>
            )}

            <div style={{padding:'14px 16px',border:'1px solid rgba(255,255,255,.08)',borderRadius:10,background:'rgba(255,255,255,.02)'}}>
              <div style={{fontSize:14,fontWeight:600,color:'var(--txt)',marginBottom:8}}>{guide.title}</div>
              <ol style={{margin:'0 0 0 18px',padding:0,color:'var(--tm)',fontSize:12,lineHeight:1.7}}>
                {guide.steps.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </div>
          </>
        )}

        {/* Step 3: Create Venture */}
        {currentStep === 3 && (
          <>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)',marginBottom:8}}>Create Your First Venture</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              A venture is a business unit or project. You can have multiple ventures tracked separately.
            </div>
            <div style={{display:'grid',gap:16}}>
              <div>
                <div style={{fontSize:12,color:'var(--tm)',marginBottom:6,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Venture Name *</div>
                <input
                  type="text"
                  placeholder="Enter your business name"
                  value={venture.name}
                  onChange={e => setVenture({...venture, name: e.target.value})}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}}
                />
              </div>
              <div>
                <div style={{fontSize:12,color:'var(--tm)',marginBottom:6,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Description</div>
                <input
                  type="text"
                  placeholder="Describe what this venture does"
                  value={venture.description}
                  onChange={e => setVenture({...venture, description: e.target.value})}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}}
                />
              </div>
              <div>
                <div style={{fontSize:12,color:'var(--tm)',marginBottom:6,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Business Type</div>
                <select
                  value={venture.businessType || 'general'}
                  onChange={e => setVenture({...venture, businessType: e.target.value})}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(255,255,255,.15)',borderRadius:8,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:13}}
                >
                  <option value="general">General Business</option>
                  <option value="retail">Retail / Shop</option>
                  <option value="services">Services</option>
                  <option value="hotspot">Hotspot / Wifi / Network</option>
                </select>
                <div style={{fontSize:11,color:'var(--tm)',marginTop:6,lineHeight:1.5}}>
                  Hotspot POS is enabled only when at least one venture is set to <strong style={{color:'var(--gold)'}}>Hotspot / Wifi / Network</strong>.
                </div>
              </div>
              <div>
                <div style={{fontSize:12,color:'var(--tm)',marginBottom:6,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Color Tag</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {PALETTE.map(color => (
                    <button
                      key={color}
                      onClick={() => setVenture({...venture, color})}
                      style={{
                        width:40,height:40,borderRadius:8,background:color,
                        border: venture.color === color ? '3px solid var(--gold)' : '2px solid rgba(255,255,255,.1)',
                        cursor:'pointer'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Step 4: Chart of Accounts Review */}
        {currentStep === 4 && (
          <>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)',marginBottom:8}}>Chart of Accounts</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              Financial Management System (FMS) now starts with a clean workspace. You can create your own chart of accounts now or add accounts later from the Accounts menu.
            </div>
            {accountsReview.length > 0 ? (
              <div style={{maxHeight:400,overflowY:'auto',marginBottom:16}}>
                <div style={{display:'grid',gridTemplateColumns:'60px 1fr 80px',gap:8,fontSize:11,color:'var(--tm)',marginBottom:8,paddingBottom:8,borderBottom:'1px solid rgba(255,255,255,.08)',fontWeight:600}}>
                  <div>Code</div>
                  <div>Name</div>
                  <div>Type</div>
                </div>
                {accountsReview.slice(0, 12).map(acc => (
                  <div key={acc.id} style={{display:'grid',gridTemplateColumns:'60px 1fr 80px',gap:8,fontSize:11,color:'var(--txt)',padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,.05)'}}>
                    <div style={{fontFamily:'IBM Plex Mono, monospace',color:'var(--gold)'}}>{acc.code}</div>
                    <div>{acc.name}</div>
                    <div style={{fontSize:10,color:'var(--tm)',textTransform:'uppercase'}}>{acc.type}</div>
                  </div>
                ))}
                {accountsReview.length > 12 && (
                  <div style={{fontSize:11,color:'var(--tm)',padding:'8px 0',fontStyle:'italic'}}>
                    ...and {accountsReview.length - 12} more accounts
                  </div>
                )}
              </div>
            ) : (
              <div style={{marginBottom:16,padding:'16px 18px',background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.09)',borderRadius:10,fontSize:12,color:'var(--tm)',lineHeight:1.7}}>
                No accounts have been created yet. Finish onboarding to start with a blank workspace, then create only the accounts your business needs.
              </div>
            )}
          </>
        )}

        {/* Step 5: Add Agents (Optional) */}
        {currentStep === 5 && (
          <>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'var(--gold)',marginBottom:8}}>Add Agents & Staff (Optional)</div>
            <div style={{fontSize:13,color:'var(--tm)',lineHeight:1.6,marginBottom:24}}>
              You can add team members and track their commission on hotspot sales. This is optional - you can add agents later.
            </div>
            
            {agents.length > 0 && (
              <div style={{marginBottom:20,maxHeight:200,overflowY:'auto'}}>
                {agents.map(agent => (
                  <div key={agent.id} style={{padding:'10px 12px',background:'rgba(45,212,160,.08)',border:'1px solid rgba(45,212,160,.2)',borderRadius:8,marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:'var(--txt)'}}>{agent.name}</div>
                      <div style={{fontSize:11,color:'var(--tm)'}}>{agent.role} - +{agent.phone || 'N/A'}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveAgent(agent.id)}
                      style={{padding:'4px 8px',background:'rgba(240,92,92,.15)',border:'1px solid rgba(240,92,92,.3)',borderRadius:4,color:'#f05c5c',fontSize:11,cursor:'pointer',fontWeight:600}}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{display:'grid',gap:10,marginBottom:16}}>
              <div>
                <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Agent Name</div>
                <input
                  type="text"
                  placeholder="Enter agent name"
                  value={agentForm.name}
                  onChange={e => setAgentForm({...agentForm, name: e.target.value})}
                  style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(255,255,255,.15)',borderRadius:6,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:12}}
                />
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Role</div>
                  <select
                    value={agentForm.role}
                    onChange={e => setAgentForm({...agentForm, role: e.target.value})}
                    style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(255,255,255,.15)',borderRadius:6,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:12}}
                  >
                    <option>Agent</option>
                    <option>Senior Agent</option>
                    <option>Manager</option>
                    <option>Director</option>
                  </select>
                </div>
                <div>
                  <div style={{fontSize:11,color:'var(--tm)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>Phone</div>
                  <input
                    type="text"
                    placeholder="+256700000000"
                    value={agentForm.phone}
                    onChange={e => setAgentForm({...agentForm, phone: e.target.value})}
                    style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(255,255,255,.15)',borderRadius:6,background:'rgba(0,0,0,.3)',color:'var(--txt)',fontSize:12}}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleAddAgent}
              disabled={!agentForm.name.trim()}
              style={{width:'100%',padding:'10px 12px',background:'rgba(45,212,160,.15)',border:'1px solid rgba(45,212,160,.3)',borderRadius:8,color:'#2dd4a0',fontWeight:600,cursor:'pointer',marginBottom:16,opacity:agentForm.name.trim() ? 1 : 0.5}}
            >
              + Add Agent
            </button>

            <div style={{padding:'10px 12px',background:'rgba(96,165,250,.08)',border:'1px solid rgba(96,165,250,.2)',borderRadius:8,fontSize:11,color:'var(--tm)',lineHeight:1.6}}>
              <strong>Tip:</strong> You can skip adding agents now and do it later from the Agents menu.
            </div>
          </>
        )}

        {/* Navigation buttons */}
        <div style={{marginTop:32,display:'flex',gap:12,justifyContent:'space-between'}}>
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="btn bg"
            style={{opacity: currentStep === 0 ? 0.5 : 1,cursor: currentStep === 0 ? 'not-allowed' : 'pointer'}}
          >
            Previous
          </button>
          <button
            onClick={handleFinish}
            className="btn bp"
            style={{flex:1}}
          >
            {currentStep === 5 ? 'Complete Setup' : 'Next >'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProfileModal({ initialProfile, busy, error, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    fullName: initialProfile?.fullName || '',
    username: initialProfile?.username || '',
    location: initialProfile?.location || '',
    phone: initialProfile?.phone || '',
    credentials: initialProfile?.credentials || '',
  }));

  useEffect(() => {
    setForm({
      fullName: initialProfile?.fullName || '',
      username: initialProfile?.username || '',
      location: initialProfile?.location || '',
      phone: initialProfile?.phone || '',
      credentials: initialProfile?.credentials || '',
    });
  }, [initialProfile]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    onSave({
      fullName: form.fullName.trim(),
      username: form.username.trim().toLowerCase(),
      location: form.location.trim(),
      phone: form.phone.trim(),
      credentials: form.credentials.trim(),
    });
  };

  return (
    <Modal title="Edit Profile" onClose={onClose} wide>
      <div className="fgrid">
        <div className="fg"><label>Full Name</label><input value={form.fullName} onChange={e=>set('fullName', e.target.value)} placeholder="Your full name" /></div>
        <div className="fg"><label>Username</label><input value={form.username} onChange={e=>set('username', e.target.value)} placeholder="username" /></div>
        <div className="fg"><label>Location</label><input value={form.location} onChange={e=>set('location', e.target.value)} placeholder="City, Country" /></div>
        <div className="fg"><label>Phone</label><input value={form.phone} onChange={e=>set('phone', e.target.value)} placeholder="+256..." /></div>
        <div className="fg full"><label>Credentials</label><input value={form.credentials} onChange={e=>set('credentials', e.target.value)} placeholder="Role, title, professional credentials" /></div>
      </div>
      {error && <div style={{marginTop:12,padding:'9px 12px',background:'rgba(240,92,92,.12)',border:'1px solid rgba(240,92,92,.35)',borderRadius:6,fontSize:12,color:'var(--err)'}}>{error}</div>}
      <div className="modal-a">
        <button className="btn bg" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn bp" onClick={handleSave} disabled={busy}>{busy ? 'Saving...' : 'Save Profile'}</button>
      </div>
    </Modal>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   MAIN APPLICATION
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function AboutPage() {
  const version = '0.1.30';
  const features = [
    { icon: '◈', label: 'Dashboard',          desc: 'Live KPI cards, income vs expense charts, and venture-level performance at a glance.' },
    { icon: '⇄', label: 'Transactions',        desc: 'Capture income and expense entries with references, payment method, item, and account mapping.' },
    { icon: '⊞', label: 'Ventures & Branches', desc: 'Organise finances across multiple business units and branches independently.' },
    { icon: '▤', label: 'Chart of Accounts',   desc: 'Maintain a structured account list that drives all reports and journal entries.' },
    { icon: '≡', label: 'Item List',            desc: 'Manage reusable products and services with categories to speed up transaction entry.' },
    { icon: '⌞', label: 'Agents & Staff',       desc: 'Track staff activity, commissions, and financial contributions per team member.' },
    { icon: '◎', label: 'Loan Tracker',         desc: 'Monitor short-term loans with repayment schedules and running balances.' },
    { icon: '⊟', label: 'General Journal',      desc: 'Review double-entry journal lines automatically generated by every transaction.' },
    { icon: '□', label: 'Snapshots',             desc: 'Capture point-in-time financial summaries for period-over-period comparison.' },
    { icon: '▦', label: 'Reports',               desc: 'Profit & Loss, Balance Sheet, Trial Balance, Cash Flow, and Venture Comparison.' },
    { icon: '☁', label: 'Cloud Sync',            desc: 'Optional Supabase sync keeps data consistent across devices when online.' },
    { icon: '⟳', label: 'Backups & Updates',    desc: 'Auto-backup every 5 minutes, manual export/import, and guided one-click app updates.' },
  ];
  const docs = [
    { step: '1', title: 'Install and onboard',  body: 'Run FMS-Setup-0.1.30.exe and follow the onboarding wizard to set up your profile, first venture, and preferred sync mode.' },
    { step: '2', title: 'Set up accounts',       body: 'Open Chart of Accounts and add the income and expense categories that match your business. Only create what you need.' },
    { step: '3', title: 'Record daily',          body: 'Use the + Transaction button to log income or expenses. Assign a venture, account category, payment method, and optional item.' },
    { step: '4', title: 'Review reports',        body: 'Open Reports or use the Reports menu to jump to P&L, Balance Sheet, Trial Balance, or Cash Flow filtered by date or venture.' },
    { step: '5', title: 'Backup and update',     body: 'Data auto-backs up every 5 minutes. Use File > Backup for a manual export. Use Help > Check for Updates for new releases.' },
  ];
  return (
    <div style={{maxWidth:880,margin:'0 auto',padding:'4px 0 32px'}}>
      {/* Hero card */}
      <div style={{background:'var(--s2)',border:'1px solid var(--brdg)',borderRadius:14,padding:'22px 24px',marginBottom:14,position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',top:0,right:0,width:200,height:200,background:'radial-gradient(circle at top right,rgba(201,168,76,.13),transparent 65%)',pointerEvents:'none'}}/>
        <div style={{display:'flex',alignItems:'flex-start',gap:20,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:260}}>
            <div style={{fontSize:10,color:'var(--gold)',letterSpacing:'1.8px',textTransform:'uppercase',marginBottom:8,fontWeight:600}}>Financial Management System</div>
            <div style={{fontFamily:'var(--serif)',fontSize:24,color:'var(--tb)',letterSpacing:'-0.01em',lineHeight:1.15,marginBottom:8}}>About FMS</div>
            <p style={{margin:'0 0 14px',color:'var(--tm)',fontSize:13,lineHeight:1.65,maxWidth:520}}>
              FMS is an offline-first financial management platform for real business operations. It centralises transaction recording, multi-venture accounting, professional reporting, backups, and guided updates in one app.
            </p>
            <div style={{display:'flex',flexWrap:'wrap',gap:7}}>
              {[['v'+version,'var(--gold-d)','var(--brdg)','var(--gold-l)'],['Offline First','rgba(45,212,160,.08)','rgba(45,212,160,.22)','var(--ok)'],['Auto-Backup','rgba(96,165,250,.08)','rgba(96,165,250,.22)','var(--info)'],['Cloud Optional','rgba(96,165,250,.08)','rgba(96,165,250,.22)','var(--info)']].map(([lbl,bg,brd,col])=>(
                <span key={lbl} style={{background:bg,border:'1px solid '+brd,color:col,borderRadius:8,padding:'4px 10px',fontSize:10,fontWeight:600}}>{lbl}</span>
              ))}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,flexShrink:0}}>
            {[['12+','App modules'],['100%','Offline capable'],['5 min','Auto-backup'],['Live','Update alerts']].map(([val,lbl])=>(
              <div key={lbl} style={{background:'var(--s3)',border:'1px solid var(--brd)',borderRadius:10,padding:'10px 14px',minWidth:86,textAlign:'center'}}>
                <b style={{display:'block',fontFamily:'var(--serif)',fontSize:18,color:'var(--gold-l)'}}>{val}</b>
                <span style={{fontSize:10,color:'var(--tm)'}}>{lbl}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:10,color:'var(--tm)',letterSpacing:'1.8px',textTransform:'uppercase',marginBottom:9,fontWeight:600}}>Core Features</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:9}}>
          {features.map(f=>(
            <div key={f.label} style={{background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:11,padding:'11px 13px'}}>
              <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:4}}>
                <span style={{color:'var(--gold)',fontSize:13}}>{f.icon}</span>
                <b style={{fontSize:12,color:'var(--txt)'}}>{f.label}</b>
              </div>
              <p style={{margin:0,fontSize:11.5,color:'var(--tm)',lineHeight:1.55}}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick start */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:10,color:'var(--tm)',letterSpacing:'1.8px',textTransform:'uppercase',marginBottom:9,fontWeight:600}}>Quick Start Guide</div>
        <div style={{display:'grid',gap:8}}>
          {docs.map(d=>(
            <div key={d.step} style={{background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:11,padding:'11px 16px',display:'flex',gap:13,alignItems:'flex-start'}}>
              <span style={{background:'var(--gold-d)',border:'1px solid var(--brdg)',color:'var(--gold)',borderRadius:7,minWidth:26,height:26,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,flexShrink:0}}>{d.step}</span>
              <div>
                <b style={{display:'block',fontSize:12.5,color:'var(--txt)',marginBottom:2}}>{d.title}</b>
                <p style={{margin:0,fontSize:12,color:'var(--tm)',lineHeight:1.6}}>{d.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company + tips */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
        <div style={{background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:11,padding:'14px 16px'}}>
          <div style={{fontSize:10,color:'var(--tm)',letterSpacing:'1.8px',textTransform:'uppercase',marginBottom:8,fontWeight:600}}>Developer</div>
          <div style={{fontSize:12,color:'var(--tm)',lineHeight:1.9}}>
            {[['Company','Clana Integrated Systems Limited Uganda'],['Email','info@cis-groups.com'],['Website','cis-groups.com'],['Tel','+256 742 518 220 / +256 762 518 220'],['Postal','P. O. Box 114931 Kampala']].map(([k,v])=>(
              <div key={k}><span style={{color:'var(--txt)'}}>{k}:</span> {v}</div>
            ))}
          </div>
        </div>
        <div style={{background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:11,padding:'14px 16px'}}>
          <div style={{fontSize:10,color:'var(--tm)',letterSpacing:'1.8px',textTransform:'uppercase',marginBottom:8,fontWeight:600}}>Support Tips</div>
          <div style={{fontSize:12,color:'var(--tm)',lineHeight:1.9}}>
            <div>Include your app version and OS when reporting issues.</div>
            <div>Use <span style={{color:'var(--gold)'}}>File &gt; Backup</span> before restoring or migrating data.</div>
            <div>Use <span style={{color:'var(--gold)'}}>Help &gt; Check for Updates</span> to get the latest release.</div>
            <div>For sync issues verify your Supabase credentials in Settings.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarIcon({ name }) {
  switch (name) {
    case 'dashboard':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="5"/><rect x="13" y="10" width="8" height="11"/><rect x="3" y="13" width="8" height="8"/></svg>;
    case 'transactions':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h13"/><path d="M14 4l3 3-3 3"/><path d="M20 17H7"/><path d="M10 14l-3 3 3 3"/></svg>;
    case 'hotspot-pos':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9a10 10 0 0 1 14 0"/><path d="M8 12a6 6 0 0 1 8 0"/><path d="M11 15a2 2 0 0 1 2 0"/><circle cx="12" cy="18" r="1"/></svg>;
    case 'loans':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9l9-5 9 5"/><path d="M5 10v8"/><path d="M9 10v8"/><path d="M15 10v8"/><path d="M19 10v8"/><path d="M3 19h18"/></svg>;
    case 'ventures':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="7" width="18" height="12" rx="2"/><path d="M8 7V5h8v2"/><path d="M3 12h18"/></svg>;
    case 'accounts':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/><path d="M17 4v4"/><path d="M10 10v4"/><path d="M14 16v4"/></svg>;
    case 'agents':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.5"/><path d="M4 19c0-2.8 2.2-5 5-5s5 2.2 5 5"/><path d="M14 19c0-2 1.6-3.6 3.6-3.6 1.5 0 2.8.8 3.4 2.1"/></svg>;
    case 'items':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/><circle cx="7" cy="7" r="1"/><circle cx="7" cy="12" r="1"/><circle cx="7" cy="17" r="1"/></svg>;
    case 'snapshots':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 15l3-3 3 2 4-4"/><path d="M7 8h10"/></svg>;
    case 'reports':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V9"/><path d="M12 20V4"/><path d="M19 20v-7"/><path d="M3 20h18"/></svg>;
    case 'journal':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h11a3 3 0 0 1 3 3v13H9a3 3 0 0 0-3 3"/><path d="M6 4v19"/><path d="M10 8h6"/><path d="M10 12h6"/></svg>;
    case 'about':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="8"/><line x1="12" y1="11" x2="12" y2="17"/></svg>;
    default:
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"/></svg>;
  }
}

const NAV = [
  { id:'dashboard',    iconName:'dashboard',    label:'Dashboard',        group:'overview'   },
  { id:'transactions', iconName:'transactions', label:'Transactions',      group:'overview'   },
  { id:'hotspot-pos',  iconName:'hotspot-pos',  label:'Hotspot POS',       group:'overview'   },
  { id:'loans',        iconName:'loans',        label:'Loan Tracker',      group:'overview'   },
  { id:'ventures',     iconName:'ventures',     label:'Ventures',          group:'management' },
  { id:'accounts',     iconName:'accounts',     label:'Chart of Accounts', group:'management' },
  { id:'items',        iconName:'items',        label:'Item List',         group:'management' },
  { id:'agents',       iconName:'agents',       label:'Agents & Staff',    group:'management' },
  { id:'snapshots',    iconName:'snapshots',    label:'Snapshots',         group:'management' },
  { id:'reports',      iconName:'reports',      label:'Reports',           group:'accounting' },
  { id:'journal',      iconName:'journal',      label:'General Journal',   group:'accounting' },
];

const TITLES = {
  dashboard:'Financial Dashboard', transactions:'Transactions',
  'hotspot-pos':'Hotspot Balance POS',
  loans:'Short Term Loan Tracker', ventures:'Ventures', accounts:'Chart of Accounts',
  items:'Item List',
  reports:'Financial Reports', journal:'General Journal', agents:'Agents & Staff', snapshots:'Snapshots',
  about:'About FMS',
};

const HOTSPOT_TYPE_KEYWORDS = ['hotspot', 'wifi', 'wi-fi', 'network'];

function ventureSupportsHotspot(venture) {
  const explicitType = String(venture?.businessType || '').toLowerCase();
  if (HOTSPOT_TYPE_KEYWORDS.some(keyword => explicitType.includes(keyword))) return true;

  const combinedText = `${venture?.name || ''} ${venture?.description || ''}`.toLowerCase();
  return HOTSPOT_TYPE_KEYWORDS.some(keyword => combinedText.includes(keyword));
}

function formatTransferSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatReleaseNotesItems(notes) {
  if (!notes) return [];
  return String(notes)
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => line.replace(/^[-*+]\s*/, '').trim())
    .filter(Boolean);
}

function normalizeUpdateErrorPayload(payload) {
  if (!payload) {
    return {
      message: 'Financial Management System (FMS) could not complete the update check.',
      detail: '',
      rawDetail: '',
    };
  }

  if (typeof payload === 'string') {
    return {
      message: 'Financial Management System (FMS) could not complete the update check.',
      detail: payload,
      rawDetail: payload,
    };
  }

  return {
    message: payload.message || 'Financial Management System (FMS) could not complete the update check.',
    detail: payload.detail || '',
    rawDetail: payload.rawDetail || '',
  };
}

export default function App() {
  const [view, setView] = useState('dashboard');
  // Determines which report tab should open when menu navigation targets Reports.
  const [reportTab, setReportTab] = useState('pl');
  const [ventures, setVentures] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTx, setShowTx] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [showV, setShowV] = useState(false);
  const [showA, setShowA] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loans, setLoans] = useState([]);
  const [showAg, setShowAg] = useState(false);
  const [agModalKey, setAgModalKey] = useState(0);
  const [editVenture, setEditVenture] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [filters, setFilters] = useState({ from:'', to:'', venture:'all', type:'all', itemCategory:'all', q:'' });
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [updateDownloading, setUpdateDownloading] = useState(false);
  const [updateDownloadPercent, setUpdateDownloadPercent] = useState(0);
  const [updateDownloadMeta, setUpdateDownloadMeta] = useState({ transferred: 0, total: 0, bytesPerSecond: 0 });
  const [updateInfo, setUpdateInfo] = useState({ version: '', releaseNotes: '' });
  const [updateCheckDialog, setUpdateCheckDialog] = useState({
    mode: 'idle',
    version: '',
    message: '',
    detail: '',
  });
  const [migrationNotice, setMigrationNotice] = useState('');
  const [backupStatus, setBackupStatus] = useState('');
  // Controls collapsed (icon-only) vs expanded sidebar mode.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [authReady, setAuthReady] = useState(!isSupabaseSyncEnabled);
  const [authSession, setAuthSession] = useState(null);
  const [offlineAccessGranted, setOfflineAccessGranted] = useState(false);
  const [authMode, setAuthMode] = useState('sign-in');
  const [forceAuthPrompt, setForceAuthPrompt] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [profileSaveBusy, setProfileSaveBusy] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [authProfileCache, setAuthProfileCache] = useState(() => {
    try {
      const raw = localStorage.getItem('fms:supabase-profile');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [offlineAuthCache, setOfflineAuthCache] = useState(() => {
    try {
      const raw = localStorage.getItem(OFFLINE_AUTH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [userProfileDetailsCache, setUserProfileDetailsCache] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_PROFILE_DETAILS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [syncDeletions, setSyncDeletions] = useState(EMPTY_SYNC_DELETIONS);
  const [syncStatus, setSyncStatus] = useState({
    state: isSupabaseSyncEnabled ? 'idle' : 'disabled',
    message: isSupabaseSyncEnabled ? 'Sync ready' : 'Supabase sync disabled',
    lastSyncedAt: '',
  });
  const [syncHistory, setSyncHistory] = useState([]);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [webMenuOpen, setWebMenuOpen] = useState('');
  const [onboardingConfig, setOnboardingConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(ONBOARDING_CONFIG_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        done: Boolean(parsed?.done),
        step: parsed?.step || 1,
        email: parsed?.email || '',
        provider: parsed?.provider || 'supabase',
        databaseConfig: parsed?.databaseConfig || DEFAULT_DATABASE_CONFIG,
        venture: parsed?.venture || EMPTY_VENTURE,
        agents: Array.isArray(parsed?.agents) ? parsed.agents : [],
      };
    } catch {
      return { 
        done: false, 
        step: 1,
        email: '',
        provider: 'supabase',
        databaseConfig: DEFAULT_DATABASE_CONFIG,
        venture: EMPTY_VENTURE,
        agents: []
      };
    }
  });
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      if (localStorage.getItem(FIRST_INSTALL_COMPLETE_KEY) === '1') {
        return false;
      }

      const raw = localStorage.getItem(ONBOARDING_CONFIG_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Boolean(parsed?.done)) {
        localStorage.setItem(FIRST_INSTALL_COMPLETE_KEY, '1');
        return false;
      }

      const storageKeys = ['fms:ventures', 'fms:accounts', 'fms:transactions', 'fms:agents', 'fms:loans'];
      const hasExistingData = storageKeys.some(key => {
        const value = localStorage.getItem(key);
        return value && value !== '[]' && value !== 'null' && value !== '{}';
      });

      if (hasExistingData) {
        localStorage.setItem(FIRST_INSTALL_COMPLETE_KEY, '1');
        return false;
      }

      return true;
    } catch {
      return false;
    }
  });
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(() => {
    try {
      const raw = localStorage.getItem(ONBOARDING_CONFIG_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.step ?? 0;
    } catch {
      return 0;
    }
  });
  const syncInFlightRef = useRef(false);
  const userMenuRef = useRef(null);
  const webMenuRef = useRef(null);
  const importFileInputRef = useRef(null);
  const transactionsRef = useRef([]);
  const agentsRef = useRef([]);
  const venturesRef = useRef([]);
  const accountsRef = useRef([]);
  const loansRef = useRef([]);
  const syncDeletionsRef = useRef(EMPTY_SYNC_DELETIONS);
  const authUser = authSession?.user || null;
  const hasOfflineAuth = Boolean(offlineAuthCache?.email);
  // Offline-first: allow app access always (with or without internet/auth)
  // Sync is optional; work locally and sync when online if authenticated
  const hasAppAccess = true;
  const isWebApp = typeof window !== 'undefined' && !window.electronAPI;
  const updateNotesItems = useMemo(() => formatReleaseNotesItems(updateInfo.releaseNotes), [updateInfo.releaseNotes]);
  const activeProfile = useMemo(() => {
    if (!authUser) return authProfileCache;
    return {
      email: authUser.email || '',
      id: authUser.id || '',
      createdAt: authUser.created_at || '',
      lastSignInAt: authUser.last_sign_in_at || '',
    };
  }, [authProfileCache, authUser]);

  const persistOnboarding = useCallback(next => {
    setOnboardingConfig(next);
    try {
      localStorage.setItem(ONBOARDING_CONFIG_KEY, JSON.stringify(next));
    } catch {
      // Ignore onboarding persistence failures.
    }
  }, []);
  const mergedProfileDetails = useMemo(() => {
    const meta = authUser?.user_metadata || {};
    return {
      fullName: String(meta.full_name || meta.name || userProfileDetailsCache.fullName || '').trim(),
      username: String(meta.username || userProfileDetailsCache.username || '').trim(),
      location: String(meta.location || userProfileDetailsCache.location || '').trim(),
      phone: String(meta.phone || userProfileDetailsCache.phone || '').trim(),
      credentials: String(meta.credentials || userProfileDetailsCache.credentials || '').trim(),
    };
  }, [authUser?.user_metadata, userProfileDetailsCache]);
  const profileDisplayName = useMemo(() => {
    const rawName = mergedProfileDetails.fullName || mergedProfileDetails.username || activeProfile?.email || '';
    const normalized = String(rawName || '').trim();
    if (!normalized) return '';
    if (normalized.includes('@')) return normalized.split('@')[0];
    return normalized;
  }, [activeProfile?.email, mergedProfileDetails.fullName, mergedProfileDetails.username]);

  const appendSyncHistory = useCallback((level, message) => {
    setSyncHistory(prev => [{
      id: uid(),
      level,
      message,
      at: new Date().toISOString(),
    }, ...prev].slice(0, 25));
  }, []);

  // If selected branch no longer exists (e.g. venture/branch deleted), exit branch detail mode.
  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  useEffect(() => {
    venturesRef.current = ventures;
  }, [ventures]);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    loansRef.current = loans;
  }, [loans]);

  useEffect(() => {
    syncDeletionsRef.current = syncDeletions;
  }, [syncDeletions]);

  useEffect(() => {
    if (!authUser) return;
    const nextProfile = {
      email: authUser.email || '',
      id: authUser.id || '',
      createdAt: authUser.created_at || '',
      lastSignInAt: authUser.last_sign_in_at || new Date().toISOString(),
    };
    setAuthProfileCache(nextProfile);
    try {
      localStorage.setItem('fms:supabase-profile', JSON.stringify(nextProfile));
    } catch {
      // Ignore cache persistence failures.
    }
  }, [authUser]);

  const persistUserProfileDetails = useCallback(nextDetails => {
    setUserProfileDetailsCache(nextDetails);
    try {
      localStorage.setItem(USER_PROFILE_DETAILS_KEY, JSON.stringify(nextDetails));
    } catch {
      // Ignore profile details persistence failures.
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    setOfflineAccessGranted(false);
  }, [authUser]);

  useEffect(() => {
    const onPointerDown = event => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (webMenuRef.current && !webMenuRef.current.contains(event.target)) {
        setWebMenuOpen('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    if (!isSupabaseSyncEnabled) return;

    let disposed = false;

    getSupabaseSession()
      .then(session => {
        if (disposed) return;
        setAuthSession(session);
        setAuthReady(true);
      })
      .catch(error => {
        if (disposed) return;
        setAuthReady(true);
        setSyncStatus(prev => ({ ...prev, state: 'error', message: `Auth error: ${error.message}` }));
        appendSyncHistory('error', `Auth session check failed: ${error.message}`);
      });

    const { data } = onSupabaseAuthStateChange(session => {
      if (disposed) return;
      setAuthSession(session);
      setAuthReady(true);
      if (session?.user) setOfflineAccessGranted(false);
    });

    return () => {
      disposed = true;
      data?.subscription?.unsubscribe();
    };
  }, [appendSyncHistory]);

  useEffect(() => {
    if (!isSupabaseSyncEnabled || !authReady) return;
    if (!authUser) {
      setSyncStatus(prev => ({
        ...prev,
        state: 'locked',
        message: offlineAccessGranted ? 'Offline authenticated: sign in online to sync' : 'Sign in to sync with Supabase',
      }));
      return;
    }

    setSyncStatus(prev => {
      if (prev.state === 'synced' || prev.state === 'syncing' || prev.state === 'offline') return prev;
      return {
        ...prev,
        state: 'idle',
        message: 'Sync ready',
      };
    });
  }, [authReady, authUser, offlineAccessGranted]);

  useEffect(() => {
    if (!selectedBranch) return;
    const v = ventures.find(x => x.id === selectedBranch.ventureId);
    const b = v?.branches?.find(x => x.id === selectedBranch.branch.id);
    if (!v || !b) setSelectedBranch(null);
  }, [ventures, selectedBranch]);

  useEffect(() => {
    (async () => {
      const rawVentures = await load('fms:ventures', DEF_VENTURES);
      const rawAccounts = await load('fms:accounts', DEF_ACCOUNTS);
      const loadedTransactions = await load('fms:transactions', []);
      const loadedItems = await load('fms:items', DEF_ITEMS);
      const rawAgents = await load('fms:agents', DEF_AGENTS);
      const loadedLoans = await load('fms:loans', DEF_LOANS);
      const loadedSyncDeletions = await load(SUPABASE_SYNC_DELETIONS_KEY, EMPTY_SYNC_DELETIONS);

      const removedLegacySeed =
        rowsMatchLegacySeed(rawVentures, LEGACY_SAMPLE_VENTURES) ||
        rowsMatchLegacySeed(rawAccounts, LEGACY_SAMPLE_ACCOUNTS) ||
        rowsMatchLegacySeed(rawAgents, LEGACY_SAMPLE_AGENTS);

      const loadedVentures = normalizeInitialRows(rawVentures, DEF_VENTURES, LEGACY_SAMPLE_VENTURES);
      const loadedAccounts = normalizeInitialRows(rawAccounts, DEF_ACCOUNTS, LEGACY_SAMPLE_ACCOUNTS);
      const loadedAgents = normalizeInitialRows(rawAgents, DEF_AGENTS, LEGACY_SAMPLE_AGENTS);

      setVentures(normalizeStoredRows(Array.isArray(loadedVentures) ? loadedVentures : DEF_VENTURES));
      setAccounts(normalizeStoredRows(Array.isArray(loadedAccounts) ? loadedAccounts : DEF_ACCOUNTS));
      setTransactions(normalizeStoredRows(Array.isArray(loadedTransactions) ? loadedTransactions : []));
      setItems(normalizeStoredRows(Array.isArray(loadedItems) ? loadedItems : DEF_ITEMS));
      setAgents(normalizeStoredRows(Array.isArray(loadedAgents) ? loadedAgents : DEF_AGENTS));
      setLoans(normalizeStoredRows(Array.isArray(loadedLoans) ? loadedLoans : DEF_LOANS));
      setSyncDeletions(sanitizeSyncDeletions(loadedSyncDeletions));
      if (removedLegacySeed) {
        setMigrationNotice('Legacy sample data from older versions was removed. Financial Management System (FMS) now starts with your own clean workspace.');
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    let expenseMigrationDone = false;
    let incomeMigrationDone = false;

    try {
      expenseMigrationDone = localStorage.getItem(EXPENSE_CATEGORY_MIGRATION_KEY) === '1';
      incomeMigrationDone = localStorage.getItem(INCOME_CATEGORY_MIGRATION_KEY) === '1';
      if (expenseMigrationDone && incomeMigrationDone) return;
    } catch {
      // Ignore storage read failures and continue migration check in-memory.
    }

    const safeAccounts = Array.isArray(accounts) ? accounts : [];

    const existingExpenseNames = new Set(
      safeAccounts
        .filter(account => account?.type === 'expense')
        .map(account => String(account?.name || '').trim().toLowerCase())
    );
    const existingIncomeNames = new Set(
      safeAccounts
        .filter(account => account?.type === 'income')
        .map(account => String(account?.name || '').trim().toLowerCase())
    );

    const missingExpenseAccounts = expenseMigrationDone
      ? []
      : REQUIRED_EXPENSE_ACCOUNTS
          .filter(account => !existingExpenseNames.has(account.name.toLowerCase()))
          .map(account => ({ ...account, id: uid() }));

    const missingIncomeAccounts = incomeMigrationDone
      ? []
      : REQUIRED_INCOME_ACCOUNTS
          .filter(account => !existingIncomeNames.has(account.name.toLowerCase()))
          .map(account => ({ ...account, id: uid() }));

    const missingAccounts = [...missingExpenseAccounts, ...missingIncomeAccounts];

    if (missingAccounts.length > 0) {
      setAccounts(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const safeNext = normalizeStoredRows([...safePrev, ...missingAccounts], safePrev);
        save('fms:accounts', safeNext);
        return safeNext;
      });
    }

    try {
      if (!expenseMigrationDone) localStorage.setItem(EXPENSE_CATEGORY_MIGRATION_KEY, '1');
      if (!incomeMigrationDone) localStorage.setItem(INCOME_CATEGORY_MIGRATION_KEY, '1');
    } catch {
      // Ignore storage write failures.
    }
  }, [accounts, loading]);

  useEffect(() => {
    if (loading) return;

    let migrationDone = false;
    try {
      migrationDone = localStorage.getItem(HOTSPOT_COLLECTION_REVENUE_MIGRATION_KEY) === '1';
      if (migrationDone) return;
    } catch {
      // Ignore storage read failures and continue migration check in-memory.
    }

    const safeAccounts = Array.isArray(accounts) ? accounts : [];
    const salesRevenueAccountId =
      safeAccounts.find(a => a.code === '4200' && a.type === 'income')?.id ||
      safeAccounts.find(a => a.type === 'income')?.id ||
      '';

    if (!salesRevenueAccountId) return;

    let changedCount = 0;
    setTransactions(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const next = safePrev.map(t => {
        if (t?.posType !== 'hotspot-agent-collection') return t;
        if (t?.categoryAccountId === salesRevenueAccountId) return t;
        changedCount += 1;
        return {
          ...t,
          type: 'income',
          categoryAccountId: salesRevenueAccountId,
          updatedAt: new Date().toISOString(),
        };
      });

      if (changedCount > 0) {
        const safeNext = normalizeStoredRows(next, safePrev);
        save('fms:transactions', safeNext);
        return safeNext;
      }
      return safePrev;
    });

    if (changedCount > 0) {
      setMigrationNotice(`Updated ${changedCount} agent payment transaction(s) to sales revenue account for accurate reporting.`);
    }

    try {
      localStorage.setItem(HOTSPOT_COLLECTION_REVENUE_MIGRATION_KEY, '1');
    } catch {
      // Ignore storage write failures.
    }
  }, [accounts, loading]);

  // Setup Electron IPC listeners for updates and file operations
  useEffect(() => {
    if (!window.electronAPI) return;

    // Listen for update notifications
    window.electronAPI.onUpdateChecking((payload) => {
      setUpdateAvailable(false);
      setUpdateReady(false);
      setUpdateDownloading(false);
      setUpdateDownloadPercent(0);
      setUpdateDownloadMeta({ transferred: 0, total: 0, bytesPerSecond: 0 });
      setUpdateCheckDialog({
        mode: 'checking',
        version: payload?.version || '',
        message: 'Checking update information, please wait...',
        detail: '',
      });
    });

    window.electronAPI.onUpdateAvailable((payload) => {
      setUpdateCheckDialog({ mode: 'idle', version: '', message: '', detail: '' });
      setUpdateAvailable(true);
      setUpdateReady(false);
      setUpdateDownloading(false);
      setUpdateDownloadPercent(0);
      setUpdateDownloadMeta({ transferred: 0, total: 0, bytesPerSecond: 0 });
      setUpdateInfo({
        version: payload?.version || '',
        releaseNotes: payload?.releaseNotes || '',
      });
    });

    window.electronAPI.onUpdateDownloadProgress((payload) => {
      setUpdateCheckDialog({ mode: 'idle', version: '', message: '', detail: '' });
      setUpdateDownloading(true);
      const nextPercent = Math.max(1, Math.min(100, Math.round(payload?.percent || 0)));
      setUpdateDownloadPercent(nextPercent);
      setUpdateDownloadMeta({
        transferred: payload?.transferred || 0,
        total: payload?.total || 0,
        bytesPerSecond: payload?.bytesPerSecond || 0,
      });
    });

    window.electronAPI.onUpdateDownloaded((payload) => {
      setUpdateCheckDialog({ mode: 'idle', version: '', message: '', detail: '' });
      setUpdateReady(true);
      setUpdateAvailable(false);
      setUpdateDownloading(false);
      setUpdateDownloadPercent(100);
      setUpdateDownloadMeta(prev => ({ ...prev, transferred: prev.total || prev.transferred, bytesPerSecond: 0 }));
      setUpdateInfo({
        version: payload?.version || '',
        releaseNotes: payload?.releaseNotes || '',
      });
    });

    window.electronAPI.onUpdateNotAvailable((payload) => {
      setUpdateAvailable(false);
      setUpdateReady(false);
      setUpdateDownloading(false);
      setUpdateDownloadPercent(0);
      setUpdateDownloadMeta({ transferred: 0, total: 0, bytesPerSecond: 0 });
      setUpdateCheckDialog({
        mode: 'none',
        version: payload?.version || '',
        message: payload?.message || 'You are already using the latest version of Financial Management System (FMS).',
        detail: '',
      });
    });

    window.electronAPI.onUpdateCheckFailed((payload) => {
      setUpdateAvailable(false);
      setUpdateReady(false);
      setUpdateDownloading(false);
      setUpdateDownloadPercent(0);
      setUpdateDownloadMeta({ transferred: 0, total: 0, bytesPerSecond: 0 });
      setUpdateCheckDialog({
        mode: 'error',
        version: '',
        message: payload?.message || 'Could not complete update check.',
        detail: payload?.detail || '',
      });
    });

    window.electronAPI.onUpdateError((payload) => {
      const errorInfo = normalizeUpdateErrorPayload(payload);
      console.error('Update error:', errorInfo.rawDetail || errorInfo.detail || errorInfo.message);
      setUpdateDownloading(false);
      setUpdateDownloadMeta(prev => ({ ...prev, bytesPerSecond: 0 }));
      setBackupStatus(errorInfo.message);
      setTimeout(() => setBackupStatus(''), 4500);
    });

    // Listen for export requests from menu
    window.electronAPI.onExportRequest(async (filePath) => {
      const data = { ventures, accounts, transactions, items, exportDate: new Date().toISOString() };
      try {
        const result = await window.electronAPI.exportData(data, filePath);
        if (result.success) {
          setBackupStatus('Data exported successfully');
          setTimeout(() => setBackupStatus(''), 3000);
        } else {
          setBackupStatus('Export failed: ' + result.error);
        }
      } catch (error) {
        setBackupStatus('Export error: ' + error.message);
      }
    });

    // Listen for share requests from menu
    window.electronAPI.onShareRequest(async (type) => {
      if (type === 'network') {
        const data = { ventures, accounts, transactions, items, shareDate: new Date().toISOString() };
        try {
          const result = await window.electronAPI.shareNetwork(data);
          if (result.success) {
            setBackupStatus('Data prepared for sharing');
            setTimeout(() => setBackupStatus(''), 3000);
          }
        } catch (error) {
          setBackupStatus('Share error: ' + error.message);
        }
      }
    });

    // Listen for imported data
    window.electronAPI.importData().then(importedData => {
      if (importedData) {
        if (importedData.ventures) setVentures(importedData.ventures);
        if (importedData.accounts) setAccounts(importedData.accounts);
        if (importedData.transactions) setTransactions(importedData.transactions);
        if (importedData.items) setItems(importedData.items);
        setBackupStatus('Data imported successfully');
        setTimeout(() => setBackupStatus(''), 3000);
      }
    }).catch(() => {
      // No import request, this is normal
    });

    // Listen for navigate-to menu events
    if (window.electronAPI && window.electronAPI.onMenuEvent) {
      window.electronAPI.onMenuEvent('navigate-to', (target) => {
        // Supports both legacy string payloads and newer object payloads.
        if (typeof target === 'string') {
          setView(target);
          return;
        }

        if (target && typeof target === 'object') {
          if (target.page) {
            setView(target.page);
          }
          if (target.page === 'reports' && target.tab) {
            setReportTab(target.tab);
          }
        }
      });

      window.electronAPI.onMenuEvent('view-sync-history', (payload) => {
        setShowSyncHistory(Boolean(payload?.visible));
      });

      window.electronAPI.onMenuEvent('open-database-setup-guide', () => {
        setShowOnboarding(true);
        setUserMenuOpen(false);
      });
    }
  }, [ventures, accounts, transactions, items]);

  const saveV  = useCallback(async vOrUpdater => {
    setVentures(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof vOrUpdater === 'function' ? vOrUpdater(safePrev) : vOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:ventures', safeNext);
      return safeNext;
    });
  }, []);
  const saveA  = useCallback(async aOrUpdater => {
    setAccounts(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof aOrUpdater === 'function' ? aOrUpdater(safePrev) : aOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:accounts', safeNext);
      return safeNext;
    });
  }, []);
  const saveTx = useCallback(async tOrUpdater => {
    setTransactions(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof tOrUpdater === 'function' ? tOrUpdater(safePrev) : tOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:transactions', safeNext);
      return safeNext;
    });
  }, []);
  const saveIt = useCallback(async iOrUpdater => {
    setItems(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof iOrUpdater === 'function' ? iOrUpdater(safePrev) : iOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:items', safeNext);
      return safeNext;
    });
  }, []);
  const saveAg = useCallback(async agOrUpdater => {
    setAgents(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof agOrUpdater === 'function' ? agOrUpdater(safePrev) : agOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:agents', safeNext);
      return safeNext;
    });
  }, []);
  const saveLn = useCallback(async lOrUpdater => {
    setLoans(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const candidate = typeof lOrUpdater === 'function' ? lOrUpdater(safePrev) : lOrUpdater;
      const safeNext = normalizeStoredRows(Array.isArray(candidate) ? candidate : [], safePrev);
      save('fms:loans', safeNext);
      return safeNext;
    });
  }, []);

  const persistSyncDeletions = useCallback(nextOrUpdater => {
    setSyncDeletions(prev => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      const safeNext = sanitizeSyncDeletions(next);
      save(SUPABASE_SYNC_DELETIONS_KEY, safeNext);
      return safeNext;
    });
  }, []);

  const markRowsDeleted = useCallback((entityKey, rows) => {
    const safeRows = (Array.isArray(rows) ? rows : []).filter(row => row?.id);
    if (!safeRows.length) return;
    const deletedAt = new Date().toISOString();
    const deletedRows = normalizeStoredRows(
      safeRows.map(row => ({ ...row, deletedAt, updatedAt: deletedAt })),
      safeRows
    );

    persistSyncDeletions(prev => {
      const existing = (prev?.[entityKey] || []).filter(row => !deletedRows.some(deletedRow => deletedRow.id === row.id));
      return {
        ...prev,
        [entityKey]: [...deletedRows, ...existing],
      };
    });
  }, [persistSyncDeletions]);

  const deleteTransaction = useCallback(async id => {
    const existing = transactionsRef.current.find(row => row.id === id);
    await saveTx(prev => prev.filter(row => row.id !== id));
    if (existing) markRowsDeleted('transactions', [existing]);
  }, [markRowsDeleted, saveTx]);

  const deleteAgent = useCallback(async id => {
    const existing = agentsRef.current.find(row => row.id === id);
    await saveAg(prev => prev.filter(row => row.id !== id));
    if (existing) markRowsDeleted('agents', [existing]);
  }, [markRowsDeleted, saveAg]);

  const deleteAllAgents = useCallback(async () => {
    const existing = agentsRef.current;
    await saveAg([]);
    markRowsDeleted('agents', existing);
  }, [markRowsDeleted, saveAg]);

  const deleteVenture = useCallback(async id => {
    const existing = venturesRef.current.find(row => row.id === id);
    await saveV(prev => prev.filter(row => row.id !== id));
    if (existing) markRowsDeleted('ventures', [existing]);
  }, [markRowsDeleted, saveV]);

  const deleteAllVentures = useCallback(async () => {
    const existing = venturesRef.current;
    await saveV([]);
    markRowsDeleted('ventures', existing);
  }, [markRowsDeleted, saveV]);

  const deleteAccount = useCallback(async id => {
    const existing = accountsRef.current.find(row => row.id === id);
    await saveA(prev => prev.filter(row => row.id !== id));
    if (existing) markRowsDeleted('accounts', [existing]);
  }, [markRowsDeleted, saveA]);

  const deleteLoan = useCallback(async id => {
    const existing = loansRef.current.find(row => row.id === id);
    await saveLn(prev => prev.filter(row => row.id !== id));
    if (existing) markRowsDeleted('loans', [existing]);
  }, [markRowsDeleted, saveLn]);

  const persistOfflineAuth = useCallback(record => {
    setOfflineAuthCache(record);
    try {
      if (record) localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(record));
      else localStorage.removeItem(OFFLINE_AUTH_KEY);
    } catch {
      // Ignore offline auth persistence failures.
    }
  }, []);

  const cacheOfflineCredential = useCallback(async (email, userId = '') => {
    const record = createOfflineAuthRecord(email, userId);
    persistOfflineAuth(record);
  }, [persistOfflineAuth]);

  const openAuthScreen = useCallback((mode = 'sign-in') => {
    setForceAuthPrompt(true);
    setAuthMode(mode);
    setAuthError('');
    setAuthInfo('');
  }, []);

  const handleOnboardingStepChange = useCallback((step) => {
    setCurrentOnboardingStep(step);
    persistOnboarding({
      ...onboardingConfig,
      step,
    });
  }, [onboardingConfig, persistOnboarding]);

  const handleOnboardingComplete = useCallback((data) => {
    // data includes: email, provider, venture, accounts, agents
    const updatedConfig = {
      ...onboardingConfig,
      done: true,
      email: data.email,
      provider: data.provider,
      databaseConfig: data.databaseConfig || onboardingConfig.databaseConfig || DEFAULT_DATABASE_CONFIG,
      venture: data.venture,
      agents: data.agents || [],
      step: 0, // Reset step for future onboarding access
    };
    persistOnboarding(updatedConfig);
    try {
      localStorage.setItem(FIRST_INSTALL_COMPLETE_KEY, '1');
    } catch {
      // Ignore first-install marker persistence failures.
    }
    try {
      localStorage.setItem(
        DATABASE_CONNECTION_CONFIG_KEY,
        JSON.stringify(data.databaseConfig || onboardingConfig.databaseConfig || DEFAULT_DATABASE_CONFIG)
      );
    } catch {
      // Ignore runtime database config persistence failures.
    }
    
    // Initialize the app with onboarding data
    if (data.venture && !ventures.some(v => v.id)) {
      saveV([{ ...data.venture, id: 'v1', branches: [] }]);
    }
    if (data.agents && data.agents.length > 0) {
      saveAg(data.agents);
    }
    
    // Cache the email for offline usage
    if (data.email) {
      cacheOfflineCredential(data.email);
    }
    
    setShowOnboarding(false);

    const hasRuntimeSupabaseCredentials =
      data.provider === 'supabase' &&
      Boolean(data.databaseConfig?.supabase?.url?.trim()) &&
      Boolean(data.databaseConfig?.supabase?.anonKey?.trim());
    if (hasRuntimeSupabaseCredentials) {
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }, 120);
    }
  }, [onboardingConfig, persistOnboarding, ventures, saveV, saveAg, cacheOfflineCredential]);

  const handleAuthSubmit = useCallback(async (identifier, password = '', method = 'password') => {
    setAuthBusy(true);
    setAuthError('');
    setAuthInfo('');

    try {
      const email = resolveIdentifierToEmail(identifier, offlineAuthCache, activeProfile, { allowLocalPlaceholder: false });
      console.log('[Auth] Online submit:', { identifier, email, method, hasOfflineAuth, offlineCacheEmail: offlineAuthCache?.email });
      if (!email) {
        setAuthError('Use your real Supabase account email to sign in to cloud services. Local-only offline usernames cannot sign in to cloud.');
        return;
      }

      if (method === 'otp') {
        await signInWithSupabaseOtp(email);
        await cacheOfflineCredential(email);
        setForceAuthPrompt(false);
        setAuthInfo('Sign-in link sent. Check your email and open the link to complete authentication.');
        appendSyncHistory('info', `Sign-in link sent to ${email}`);
      } else {
        console.log('[Auth] Attempting password sign-in for:', email);
        const session = await signInWithSupabasePassword(email, password);
        console.log('[Auth] Password sign-in succeeded, session:', session?.user?.id);
        await cacheOfflineCredential(email, session?.user?.id || '');
        setForceAuthPrompt(false);
        console.log('[Auth] Offline credential cached');
        appendSyncHistory('success', `Signed in to Supabase as ${email}`);
      }
    } catch (error) {
      const raw = String(error?.message || 'Authentication failed');
      console.error('[Auth] Online submit error:', error, raw);
      const isNetworkError = /Failed to fetch|ERR_NAME_NOT_RESOLVED|network|offline|timeout|ENOTFOUND/i.test(raw) || !isOnline;
      if (isNetworkError) {
        setAuthError('No internet: Cloud sign-in requires connection. You can work offline, or try again when online.');
        setOfflineAccessGranted(true);
        setForceAuthPrompt(false);
        appendSyncHistory('warning', `Offline mode: cloud sign-in unavailable`);
      } else if (/rate limit/i.test(raw)) {
        setAuthError('Email rate limit exceeded. Wait a few minutes or use password sign-in.');
      } else if (/invalid login|incorrect password|unauthorized/i.test(raw)) {
        setAuthError('Invalid email/username or password. Try again.');
      } else {
        setAuthError(raw);
      }
    } finally {
      setAuthBusy(false);
    }
  }, [activeProfile, appendSyncHistory, cacheOfflineCredential, offlineAuthCache, hasOfflineAuth, isOnline]);

  const handleOfflineUnlock = useCallback(async identifier => {
    setAuthBusy(true);
    setAuthError('');
    setAuthInfo('');

    try {
      const normalizedId = normalizeIdentifier(identifier);
      // Allow any identifier for offline access - create/update local profile
      if (!normalizedId) {
        setAuthError('Please enter an email or username to continue locally.');
        return;
      }

      // Create profile record for local use
      const record = createOfflineAuthRecord(
        normalizedId.includes('@') ? normalizedId : `${normalizedId}@local`,
        normalizedId
      );
      persistOfflineAuth(record);
      
      console.log('[Auth] Offline unlock successful:', { identifier: normalizedId, record });
      setOfflineAccessGranted(true);
      setForceAuthPrompt(false);
      setAuthInfo('Working locally. Sign in online to sync your data with the cloud.');
      appendSyncHistory('success', `Local access enabled for ${normalizedId}`);
    } catch (error) {
      console.error('[Auth] Offline unlock error:', error);
      setAuthError('Unable to enable local access: ' + (error?.message || 'unknown error'));
    } finally {
      setAuthBusy(false);
    }
  }, [appendSyncHistory, persistOfflineAuth]);

  const handlePasswordReset = useCallback(async identifier => {
    setAuthBusy(true);
    setAuthError('');
    setAuthInfo('');

    try {
      const email = resolveIdentifierToEmail(identifier, offlineAuthCache, activeProfile, { allowLocalPlaceholder: false });
      if (!email) {
        setAuthError('Enter your real Supabase account email to reset your cloud password.');
        return;
      }
      try {
        await requestSupabasePasswordReset(email);
        const resetMessage = 'Password reset email sent. Check your inbox and spam folder.';
        setAuthInfo(resetMessage);
        setBackupStatus(resetMessage);
        setTimeout(() => setBackupStatus(''), 4000);
        appendSyncHistory('info', `Password reset requested for ${email}`);
      } catch (resetError) {
        console.error('[Auth] Password reset error:', resetError);
        if (/Failed to fetch|ERR_NAME_NOT_RESOLVED|network/i.test(String(resetError?.message || ''))) {
          setAuthError('No internet connection. Try again when online.');
        } else {
          setAuthError(resetError?.message || 'Unable to send password reset email');
        }
      }
    } catch (error) {
      setAuthError(error?.message || 'Password reset failed');
    } finally {
      setAuthBusy(false);
    }
  }, [activeProfile, appendSyncHistory, offlineAuthCache]);

  const handleSignOut = useCallback(async () => {
    setAuthBusy(true);
    try {
      try {
        await signOutOfSupabase();
      } catch (error) {
        // Sign-out should still proceed in offline mode.
        appendSyncHistory('warning', `Cloud sign-out unavailable: ${error.message}`);
      }
      setOfflineAccessGranted(false);
      persistOfflineAuth(null);
      setForceAuthPrompt(true);
      setAuthMode('sign-in');
      setAuthInfo('');
      setAuthError('');
      setUserMenuOpen(false);
      appendSyncHistory('info', 'Signed out');
    } catch (error) {
      setSyncStatus(prev => ({ ...prev, state: 'error', message: `Sign-out error: ${error.message}` }));
      appendSyncHistory('error', `Sign-out failed: ${error.message}`);
    } finally {
      setAuthBusy(false);
    }
  }, [appendSyncHistory, persistOfflineAuth]);

  useEffect(() => {
    if (!window.electronAPI || !window.electronAPI.onMenuEvent) return;

    window.electronAPI.onMenuEvent('auth-sign-in', () => {
      setUserMenuOpen(false);
      openAuthScreen(hasOfflineAuth && !isOnline ? 'offline' : 'sign-in');
    });

    window.electronAPI.onMenuEvent('auth-sign-out', () => {
      setUserMenuOpen(false);
      handleSignOut();
    });
  }, [handleSignOut, hasOfflineAuth, isOnline, openAuthScreen]);

  const handleSaveProfile = useCallback(async updates => {
    setProfileSaveBusy(true);
    setProfileSaveError('');
    try {
      persistUserProfileDetails(updates);
      if (authUser) {
        await updateSupabaseUserMetadata({
          full_name: updates.fullName || null,
          username: updates.username || null,
          location: updates.location || null,
          phone: updates.phone || null,
          credentials: updates.credentials || null,
        });
      }
      setShowProfileEditor(false);
      setUserMenuOpen(false);
      appendSyncHistory('success', 'Profile details updated');
    } catch (error) {
      setProfileSaveError(error?.message || 'Unable to save profile');
    } finally {
      setProfileSaveBusy(false);
    }
  }, [appendSyncHistory, authUser, persistUserProfileDetails]);

  const runSupabaseSync = useCallback(async (reason = 'manual') => {
    if (!isSupabaseSyncEnabled) return;
    if (!authReady) return;
    if (!authUser) {
      setSyncStatus(prev => ({ ...prev, state: 'locked', message: offlineAccessGranted ? 'Offline authenticated: sign in online to sync' : 'Sign in to sync with Supabase' }));
      if (reason === 'manual' || reason === 'startup') setAuthMode(hasOfflineAuth ? 'offline' : 'sign-in');
      appendSyncHistory('warn', 'Sync skipped: no authenticated Supabase user');
      return;
    }
    if (!navigator.onLine) {
      setSyncStatus(prev => ({ ...prev, state: 'offline', message: 'Offline mode: local data only' }));
      appendSyncHistory('warn', 'Sync skipped: device offline');
      return;
    }
    if (syncInFlightRef.current) return;

    syncInFlightRef.current = true;
    setSyncStatus(prev => ({ ...prev, state: 'syncing', message: reason === 'manual' ? 'Syncing now...' : 'Background sync in progress...' }));
    appendSyncHistory('info', `Sync started (${reason})`);

    try {
      const [cloudTxRes, cloudAgentsRes, cloudVenturesRes, cloudAccountsRes, cloudLoansRes] = await Promise.all([
        fetchAllTransactions(),
        fetchAllAgents(),
        fetchAllVentures(),
        fetchAllAccounts(),
        fetchAllLoans(),
      ]);

      const fetchError = cloudTxRes.error || cloudAgentsRes.error || cloudVenturesRes.error || cloudAccountsRes.error || cloudLoansRes.error;
      if (fetchError) throw fetchError;

      const remoteTx = (cloudTxRes.data || []).map(fromRemoteTransaction);
      const remoteAgents = (cloudAgentsRes.data || []).map(fromRemoteAgent);
      const remoteVentures = (cloudVenturesRes.data || []).map(fromRemoteVenture);
      const remoteAccounts = (cloudAccountsRes.data || []).map(fromRemoteAccount);
      const remoteLoans = (cloudLoansRes.data || []).map(fromRemoteLoan);

      const localSyncDeletions = syncDeletionsRef.current;
      const resolvedTx = resolveSyncRows(transactionsRef.current, localSyncDeletions.transactions, remoteTx);
      const resolvedAgents = resolveSyncRows(agentsRef.current, localSyncDeletions.agents, remoteAgents);
      const resolvedVentures = resolveSyncRows(venturesRef.current, localSyncDeletions.ventures, remoteVentures);
      const resolvedAccounts = resolveSyncRows(accountsRef.current, localSyncDeletions.accounts, remoteAccounts);
      const resolvedLoans = resolveSyncRows(loansRef.current, localSyncDeletions.loans, remoteLoans);

      const [pushTxRes, pushAgentsRes, pushVenturesRes, pushAccountsRes, pushLoansRes] = await Promise.all([
        upsertTransactions([...resolvedTx.active, ...resolvedTx.deleted].map(toRemoteTransaction)),
        upsertAgents([...resolvedAgents.active, ...resolvedAgents.deleted].map(toRemoteAgent)),
        upsertVentures([...resolvedVentures.active, ...resolvedVentures.deleted].map(toRemoteVenture)),
        upsertAccounts([...resolvedAccounts.active, ...resolvedAccounts.deleted].map(toRemoteAccount)),
        upsertLoans([...resolvedLoans.active, ...resolvedLoans.deleted].map(toRemoteLoan)),
      ]);

      const pushError = pushTxRes.error || pushAgentsRes.error || pushVenturesRes.error || pushAccountsRes.error || pushLoansRes.error;
      if (pushError) throw pushError;

      const mergedTx = resolvedTx.active;
      const mergedAgents = resolvedAgents.active;
      const mergedVentures = resolvedVentures.active;
      const mergedAccounts = resolvedAccounts.active;
      const mergedLoans = resolvedLoans.active;
      const nextSyncDeletions = sanitizeSyncDeletions({
        transactions: resolvedTx.deleted,
        agents: resolvedAgents.deleted,
        ventures: resolvedVentures.deleted,
        accounts: resolvedAccounts.deleted,
        loans: resolvedLoans.deleted,
      });

      const conflictCount =
        countMergeConflicts([...transactionsRef.current, ...(localSyncDeletions.transactions || [])], remoteTx) +
        countMergeConflicts([...agentsRef.current, ...(localSyncDeletions.agents || [])], remoteAgents) +
        countMergeConflicts([...venturesRef.current, ...(localSyncDeletions.ventures || [])], remoteVentures) +
        countMergeConflicts([...accountsRef.current, ...(localSyncDeletions.accounts || [])], remoteAccounts) +
        countMergeConflicts([...loansRef.current, ...(localSyncDeletions.loans || [])], remoteLoans);

      setAccounts(mergedAccounts);
      setTransactions(mergedTx);
      setAgents(mergedAgents);
      setVentures(mergedVentures);
      setLoans(mergedLoans);
      setSyncDeletions(nextSyncDeletions);

      await Promise.all([
        save('fms:accounts', mergedAccounts),
        save('fms:transactions', mergedTx),
        save('fms:agents', mergedAgents),
        save('fms:ventures', mergedVentures),
        save('fms:loans', mergedLoans),
        save(SUPABASE_SYNC_DELETIONS_KEY, nextSyncDeletions),
      ]);

      setSyncStatus({
        state: 'synced',
        message: 'Synced with Supabase',
        lastSyncedAt: new Date().toISOString(),
      });
      appendSyncHistory(
        conflictCount > 0 ? 'warn' : 'success',
        `Sync complete: ${mergedTx.length} transactions, ${mergedAgents.length} agents, ${mergedVentures.length} ventures, ${mergedAccounts.length} accounts, ${mergedLoans.length} loans${conflictCount > 0 ? `, ${conflictCount} conflict(s) resolved` : ''}`
      );
    } catch (error) {
      const msg = error?.message || 'Sync failed';
      setSyncStatus(prev => ({ ...prev, state: 'error', message: `Sync error: ${msg}` }));
      appendSyncHistory('error', `Sync failed: ${msg}`);
    } finally {
      syncInFlightRef.current = false;
    }
  }, [appendSyncHistory, authReady, authUser, hasOfflineAuth, offlineAccessGranted]);

  useEffect(() => {
    if (loading || !isSupabaseSyncEnabled || !authReady || !authUser) return;
    runSupabaseSync('startup');
  }, [authReady, authUser, loading, runSupabaseSync]);

  useEffect(() => {
    if (loading || !isSupabaseSyncEnabled || !authReady || !authUser) return;
    const timer = setInterval(() => runSupabaseSync('background'), 60000);
    return () => clearInterval(timer);
  }, [authReady, authUser, loading, runSupabaseSync]);

  useEffect(() => {
    if (!isSupabaseSyncEnabled || !authReady || !authUser) return;
    if (isOnline) {
      runSupabaseSync('reconnect');
    } else {
      setSyncStatus(prev => ({ ...prev, state: 'offline', message: 'Offline mode: local data only' }));
      appendSyncHistory('warn', 'Connection lost: switched to offline mode');
    }
  }, [authReady, authUser, isOnline, runSupabaseSync, appendSyncHistory]);

  const syncBadgeColor = useMemo(() => {
    if (syncStatus.state === 'synced') return 'var(--ok)';
    if (syncStatus.state === 'syncing') return 'var(--info)';
    if (syncStatus.state === 'error') return 'var(--err)';
    if (syncStatus.state === 'offline') return '#fb923c';
    if (syncStatus.state === 'locked') return '#f59e0b';
    return 'var(--tm)';
  }, [syncStatus.state]);

  const filteredTx = useMemo(() => transactions.filter(t => {
    if (filters.venture !== 'all' && t.ventureId !== filters.venture) return false;
    if (filters.type !== 'all' && getTxDisplayType(t) !== filters.type) return false;
    if (filters.itemCategory !== 'all') {
      const selectedItem = (items || []).find(item => item.id === t.itemId);
      const selectedItemCategory = String(selectedItem?.itemCategory || '').trim();
      if (filters.itemCategory === 'uncategorized') {
        if (selectedItemCategory) return false;
      } else if (selectedItemCategory !== filters.itemCategory) {
        return false;
      }
    }
    if (filters.from && t.date < filters.from) return false;
    if (filters.to && t.date > filters.to) return false;
    if (filters.q && !(t.description+t.reference+'').toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  }), [transactions, items, filters]);

  const hotspotPosEnabled = useMemo(() => ventures.some(ventureSupportsHotspot), [ventures]);

  useEffect(() => {
    if (!hotspotPosEnabled && view === 'hotspot-pos') {
      setView('dashboard');
    }
  }, [hotspotPosEnabled, view]);

  // Auto-backup data every 5 minutes
  useEffect(() => {
    const backupInterval = setInterval(async () => {
      if (window.electronAPI && window.electronAPI.backupSave) {
        const data = { ventures, accounts, transactions, items, backupDate: new Date().toISOString() };
        const result = await window.electronAPI.backupSave(data);
        if (!result.success) {
          console.error('Auto-backup failed:', result.error);
        }
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(backupInterval);
  }, [ventures, accounts, transactions, items]);

  const navItems = hotspotPosEnabled ? NAV : NAV.filter(item => item.id !== 'hotspot-pos');
  const groups = [...new Set(navItems.map(n=>n.group))];

  const handleDownloadUpdate = async () => {
    if (!window.electronAPI?.downloadUpdate) return;
    setUpdateCheckDialog({ mode: 'idle', version: '', message: '', detail: '' });
    setUpdateAvailable(false);
    setUpdateReady(false);
    setUpdateDownloading(true);
    setUpdateDownloadPercent(prev => Math.max(1, prev || 1));
    const result = await window.electronAPI.downloadUpdate();
    if (!result?.success) {
      setUpdateDownloading(false);
      setBackupStatus(`Update download failed: ${result?.error || 'Unknown error'}`);
      setTimeout(() => setBackupStatus(''), 4500);
      return;
    }
  };

  const handleInstallUpdate = async () => {
    if (!window.electronAPI?.installUpdate) return;
    await window.electronAPI.installUpdate();
  };

  const downloadJsonFile = useCallback((fileName, payload) => {
    if (typeof window === 'undefined' || !window.URL) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleBrowserExportData = useCallback(() => {
    downloadJsonFile(`Financial Management System (FMS)_Backup_${tod()}.json`, {
      ventures,
      accounts,
      agents,
      loans,
      transactions,
      items,
      exportDate: new Date().toISOString(),
    });
    setBackupStatus('Data exported to your browser downloads');
    setTimeout(() => setBackupStatus(''), 3500);
    setWebMenuOpen('');
  }, [accounts, agents, downloadJsonFile, items, loans, transactions, ventures]);

  const handleBrowserImportClick = useCallback(() => {
    importFileInputRef.current?.click();
    setWebMenuOpen('');
  }, []);

  const handleBrowserImportData = useCallback(async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const raw = await file.text();
      const importedData = JSON.parse(raw);
      if (Array.isArray(importedData.ventures)) await saveV(importedData.ventures);
      if (Array.isArray(importedData.accounts)) await saveA(importedData.accounts);
      if (Array.isArray(importedData.transactions)) await saveTx(importedData.transactions);
      if (Array.isArray(importedData.items)) await saveIt(importedData.items);
      if (Array.isArray(importedData.agents)) await saveAg(importedData.agents);
      if (Array.isArray(importedData.loans)) await saveLn(importedData.loans);
      setBackupStatus('Data imported successfully');
      setTimeout(() => setBackupStatus(''), 3500);
    } catch (error) {
      setBackupStatus(`Import failed: ${error.message}`);
      setTimeout(() => setBackupStatus(''), 4500);
    } finally {
      event.target.value = '';
    }
  }, [saveA, saveAg, saveIt, saveLn, saveTx, saveV]);

  const runBrowserEditCommand = useCallback(command => {
    try {
      document.execCommand(command);
    } catch {
      setBackupStatus(`${command} is not available in this browser`);
      setTimeout(() => setBackupStatus(''), 2500);
    }
    setWebMenuOpen('');
  }, []);

  const openWebMenu = useCallback(menuId => {
    setWebMenuOpen(prev => (prev === menuId ? '' : menuId));
  }, []);

  const jumpToReport = useCallback(tab => {
    setView('reports');
    setReportTab(tab);
    setSelectedBranch(null);
    setWebMenuOpen('');
  }, []);

  const jumpToView = useCallback(nextView => {
    setView(nextView);
    if (nextView !== 'ventures') setSelectedBranch(null);
    setWebMenuOpen('');
  }, []);

  const handleBrowserUpdateAction = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.open('/updates/FMS-Setup-0.1.12.exe', '_blank', 'noopener,noreferrer');
    }
    setWebMenuOpen('');
  }, []);

  const closeUpdateDialog = () => {
    setUpdateCheckDialog({ mode: 'idle', version: '', message: '', detail: '' });
  };

  if (loading || (isSupabaseSyncEnabled && !authReady)) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100vh',background:'#060a1a',gap:16}}>
      <div style={{fontFamily:'Playfair Display,serif',fontSize:28,color:'#c9a84c',letterSpacing:1}}>Financial Management System (FMS)</div>
      <div style={{width:200,height:2,background:'rgba(201,168,76,.2)',borderRadius:1,overflow:'hidden'}}>
        <div style={{height:'100%',background:'var(--gold)',animation:'none',width:'60%'}}/>
      </div>
      <div style={{fontFamily:'IBM Plex Mono,monospace',fontSize:12,color:'#6070a0',letterSpacing:1}}>{isSupabaseSyncEnabled && !authReady ? 'Preparing secure cloud session...' : 'Loading financial data...'}</div>
    </div>
  );

  if (showOnboarding) {
    return (
      <>
        <style>{CSS}</style>
        <ComprehensiveOnboarding
          currentStep={currentOnboardingStep}
          onStepChange={handleOnboardingStepChange}
          onComplete={handleOnboardingComplete}
          onExit={() => setShowOnboarding(false)}
          initialVenture={onboardingConfig.venture}
          initialAccounts={accounts.length > 0 ? accounts : DEF_ACCOUNTS}
          initialAgents={onboardingConfig.agents}
          initialDatabaseConfig={onboardingConfig.databaseConfig}
        />
      </>
    );
  }

  if (isSupabaseSyncEnabled && (!hasAppAccess || forceAuthPrompt)) {
    return (
      <>
        <style>{CSS}</style>
        <AuthScreen
          mode={authMode}
          busy={authBusy}
          error={authError}
          info={authInfo}
          profile={activeProfile}
          hasOfflineAuth={hasOfflineAuth}
          offlineAccessGranted={offlineAccessGranted}
          onModeChange={setAuthMode}
          onSubmit={handleAuthSubmit}
          onRequestReset={handlePasswordReset}
          onOfflineUnlock={handleOfflineUnlock}
          onOfflineSignOut={handleSignOut}
        />
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        {/* SIDEBAR */}
        <aside className={`sb ${sidebarCollapsed?'collapsed':''}`.trim()}>
          <div className="sb-logo">
            <div className="sb-logo-text">
              <h1>Financial Management System (FMS)</h1>
              <p>Financial Management</p>
            </div>
            <button className="sb-toggle" onClick={()=>setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed?'Expand':'Collapse'}>{sidebarCollapsed?'>':'<'}</button>
          </div>
          <nav className="sb-nav">
            {groups.map(g => (
              <div key={g}>
                <div className="nav-sec">{g}</div>
                {navItems.filter(n=>n.group===g).map(n => (
                  <div key={n.id} className={`ni ${view===n.id?'on':''}`} onClick={()=>{ setView(n.id); if (n.id !== 'ventures') setSelectedBranch(null); }}>
                    <span className="ni-ico"><SidebarIcon name={n.iconName} /></span>
                    {/* Hidden in collapsed mode so the sidebar shows icons only. */}
                    <span className="ni-label">{n.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </nav>
          <div className="sb-foot">
            <div style={{marginBottom:4}}>Currency: <span style={{color:'var(--gold)'}}>UGX</span></div>
            <div>{ventures.length} venture{ventures.length!==1?'s':''} - {transactions.length} transactions</div>
            {isSupabaseSyncEnabled && activeProfile?.email && (
              <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(255,255,255,.08)'}}>
                <div style={{fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Cloud Profile</div>
                <div style={{fontSize:12,color:'var(--txt)',fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{activeProfile.email}</div>
                {activeProfile.id && <div style={{fontSize:10,color:'var(--tm)',fontFamily:'var(--mono)',marginTop:2}}>User {activeProfile.id.slice(0, 8)}</div>}
                {activeProfile.lastSignInAt && <div style={{fontSize:10,color:'var(--tm)',marginTop:2}}>Last sign-in {new Date(activeProfile.lastSignInAt).toLocaleString()}</div>}
              </div>
            )}
          </div>
        </aside>

        {/* MAIN */}
        <main className="main">
          {isWebApp && (
            <div className="wm" ref={webMenuRef}>
              <div style={{position:'relative'}}>
                <button className={`wm-btn ${webMenuOpen==='file'?'on':''}`} onClick={() => openWebMenu('file')}>File</button>
                {webMenuOpen === 'file' && (
                  <div className="wm-menu">
                    <button className="wm-item" onClick={handleBrowserExportData}><span>Export Data</span><span>JSON</span></button>
                    <button className="wm-item" onClick={handleBrowserImportClick}><span>Import Data</span><span>JSON</span></button>
                    <div className="wm-sep" />
                    <button className="wm-item" onClick={() => { setShowOnboarding(true); setWebMenuOpen(''); }}><span>Database Setup Guide</span><span>Guide</span></button>
                  </div>
                )}
              </div>

              <div style={{position:'relative'}}>
                <button className={`wm-btn ${webMenuOpen==='reports'?'on':''}`} onClick={() => openWebMenu('reports')}>Reports</button>
                {webMenuOpen === 'reports' && (
                  <div className="wm-menu">
                    <button className="wm-item" onClick={() => jumpToReport('pl')}><span>Profit &amp; Loss</span><span>P&amp;L</span></button>
                    <button className="wm-item" onClick={() => jumpToReport('bs')}><span>Balance Sheet</span><span>BS</span></button>
                    <button className="wm-item" onClick={() => jumpToReport('tb')}><span>Trial Balance</span><span>TB</span></button>
                    <button className="wm-item" onClick={() => jumpToReport('cf')}><span>Cash Flow</span><span>CF</span></button>
                    <button className="wm-item" onClick={() => jumpToReport('vc')}><span>Venture Comparison</span><span>VC</span></button>
                  </div>
                )}
              </div>

              <div style={{position:'relative'}}>
                <button className={`wm-btn ${webMenuOpen==='edit'?'on':''}`} onClick={() => openWebMenu('edit')}>Edit</button>
                {webMenuOpen === 'edit' && (
                  <div className="wm-menu">
                    <button className="wm-item" onClick={() => runBrowserEditCommand('undo')}><span>Undo</span><span>Ctrl+Z</span></button>
                    <button className="wm-item" onClick={() => runBrowserEditCommand('redo')}><span>Redo</span><span>Ctrl+Y</span></button>
                    <div className="wm-sep" />
                    <button className="wm-item" onClick={() => runBrowserEditCommand('cut')}><span>Cut</span><span>Ctrl+X</span></button>
                    <button className="wm-item" onClick={() => runBrowserEditCommand('copy')}><span>Copy</span><span>Ctrl+C</span></button>
                    <button className="wm-item" onClick={() => runBrowserEditCommand('paste')}><span>Paste</span><span>Ctrl+V</span></button>
                  </div>
                )}
              </div>

              <div style={{position:'relative'}}>
                <button className={`wm-btn ${webMenuOpen==='view'?'on':''}`} onClick={() => openWebMenu('view')}>View</button>
                {webMenuOpen === 'view' && (
                  <div className="wm-menu">
                    <button className="wm-item" onClick={() => jumpToView('dashboard')}><span>Dashboard</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('transactions')}><span>Transactions</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('ventures')}><span>Ventures</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('accounts')}><span>Chart of Accounts</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('items')}><span>Item List</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('agents')}><span>Agents</span><span>View</span></button>
                    <button className="wm-item" onClick={() => jumpToView('reports')}><span>Reports</span><span>View</span></button>
                    <div className="wm-sep" />
                    <button className="wm-item" onClick={() => { setShowSyncHistory(prev => !prev); setWebMenuOpen(''); }}><span>{showSyncHistory ? 'Hide Sync History' : 'Show Sync History'}</span><span>Sync</span></button>
                  </div>
                )}
              </div>

              <div style={{position:'relative'}}>
                <button className={`wm-btn ${webMenuOpen==='help'?'on':''}`} onClick={() => openWebMenu('help')}>Help</button>
                {webMenuOpen === 'help' && (
                  <div className="wm-menu">
                    <button className="wm-item" onClick={() => { setShowOnboarding(true); setWebMenuOpen(''); }}><span>Setup Guide</span><span>Guide</span></button>
                    <button className="wm-item" onClick={handleBrowserUpdateAction}><span>Download Latest Setup</span><span>0.1.12</span></button>
                    <button className="wm-item" onClick={() => { window.open('/contact.html', '_blank', 'noopener,noreferrer'); setWebMenuOpen(''); }}><span>Contact Support</span><span>Help</span></button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="topbar">
            <h2>{TITLES[view] || TITLES.dashboard}</h2>
            <div className="tb-acts">
              {isSupabaseSyncEnabled && (
                <div style={{display:'flex',alignItems:'center',gap:8,marginRight:4,padding:'4px 8px',border:'1px solid var(--brd)',borderRadius:6}} title={syncStatus.lastSyncedAt ? `Last synced: ${new Date(syncStatus.lastSyncedAt).toLocaleString()}` : 'Not synced yet'}>
                  <span style={{fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Sync</span>
                  <span style={{fontSize:12,color:syncBadgeColor,fontWeight:600}}>{syncStatus.state === 'syncing' ? 'Syncing' : syncStatus.state === 'synced' ? 'Online' : syncStatus.state === 'offline' ? 'Offline' : syncStatus.state === 'error' ? 'Error' : syncStatus.state === 'locked' ? (offlineAccessGranted ? 'Offline Auth' : 'Sign In') : 'Idle'}</span>
                </div>
              )}
              {isSupabaseSyncEnabled && (
                <div ref={userMenuRef} style={{position:'relative',marginRight:4,order:99}}>
                  <button
                    className="btn bg bs"
                    style={{display:'flex',alignItems:'center',gap:8,padding:'4px 10px',border:'1px solid var(--brd)',borderRadius:6}}
                    onClick={()=>setUserMenuOpen(v=>!v)}
                  >
                    <span
                      style={{
                        width:22,
                        height:22,
                        borderRadius:'50%',
                        display:'inline-flex',
                        alignItems:'center',
                        justifyContent:'center',
                        color:'var(--txt)',
                        background: authUser?.email || activeProfile?.email ? 'rgba(201,168,76,.25)' : 'rgba(255,255,255,.12)',
                        border:'1px solid rgba(255,255,255,.18)',
                      }}
                      title="User Avatar"
                    >
                      <span style={{width:8,height:8,borderRadius:'50%',background:'rgba(255,255,255,.85)',display:'block'}} />
                    </span>
                    <span style={{fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>User</span>
                    <span style={{fontSize:12,color:'var(--txt)',fontWeight:600,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{profileDisplayName || authUser?.email || activeProfile?.email || 'Signed out'}</span>
                    <span style={{fontSize:10,color:'var(--tm)'}}>{userMenuOpen ? '^' : 'v'}</span>
                  </button>
                  {userMenuOpen && (
                    <div style={{position:'absolute',top:'calc(100% + 8px)',right:0,minWidth:250,background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:8,padding:10,zIndex:1000,boxShadow:'0 10px 25px rgba(0,0,0,0.25)'}}>
                      <div style={{fontSize:10,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Profile</div>
                      <div style={{fontSize:12,color:'var(--txt)',fontWeight:600,marginBottom:4,wordBreak:'break-word'}}>{profileDisplayName || 'Signed out'}</div>
                      <div style={{fontSize:11,color:'var(--tm)',marginBottom:10,wordBreak:'break-word'}}>{authUser?.email || activeProfile?.email || ''}</div>
                      {(mergedProfileDetails.location || mergedProfileDetails.credentials) && (
                        <div style={{fontSize:11,color:'var(--tm)',marginBottom:10,lineHeight:1.5}}>
                          {mergedProfileDetails.location && <div>Location: {mergedProfileDetails.location}</div>}
                          {mergedProfileDetails.credentials && <div>Credentials: {mergedProfileDetails.credentials}</div>}
                        </div>
                      )}
                      <div style={{display:'grid',gap:6}}>
                        <button className="btn bg bs" style={{justifyContent:'flex-start'}} onClick={()=>{ setShowOnboarding(true); setUserMenuOpen(false); }}>Online Database Setup Guide</button>
                        <button className="btn bg bs" style={{justifyContent:'flex-start'}} onClick={()=>{ setShowProfileEditor(true); setUserMenuOpen(false); }}>Edit Profile</button>
                        {authUser ? (
                          <>
                            <button className="btn bg bs" style={{justifyContent:'flex-start'}} onClick={()=>{ handlePasswordReset(authUser.email || ''); setUserMenuOpen(false); }}>Reset Password</button>
                            <button className="btn bg bs" style={{justifyContent:'flex-start'}} onClick={handleSignOut}>Sign Out</button>
                          </>
                        ) : (
                          <>
                            <button className="btn bp" style={{justifyContent:'flex-start'}} onClick={()=>{ openAuthScreen(hasOfflineAuth && !isOnline ? 'offline' : 'sign-in'); setUserMenuOpen(false); }}>{hasOfflineAuth && !isOnline ? 'Unlock Offline' : 'Sign In'}</button>
                            {offlineAccessGranted && <button className="btn bg bs" style={{justifyContent:'flex-start'}} onClick={()=>{ handleSignOut(); setUserMenuOpen(false); }}>Sign Out Offline</button>}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isSupabaseSyncEnabled && (
                <button className="btn bg bs" title={authUser ? 'Sync with Supabase' : 'Sign in to enable Supabase sync'} disabled={syncStatus.state==='syncing' || !authUser} onClick={()=>runSupabaseSync('manual')}>
                  Sync
                </button>
              )}
              {window.electronAPI && (
                <>
                  <button className="btn bg bs" title="Backup Data" onClick={async () => {
                    const data = { ventures, accounts, transactions, items, backupDate: new Date().toISOString() };
                    const result = await window.electronAPI.backupSave(data);
                    setBackupStatus(result.success ? 'Backup saved to Documents/Financial Management System (FMS) Backups' : 'Backup failed: ' + result.error);
                    setTimeout(() => setBackupStatus(''), 4000);
                  }}>Backup</button>
                  <button className="btn bg bs" title="Export Data" onClick={async () => {
                    const result = await window.electronAPI.getBackupDir();
                    if (result.success) {
                      setBackupStatus(`${result.files.length} backup(s) found in ${result.dir}`);
                      setTimeout(() => setBackupStatus(''), 4000);
                    }
                  }}>Backups</button>
                </>
              )}
              {(view==='dashboard'||view==='transactions') &&
                <button className="btn bp" onClick={()=>setShowTx(true)}>+ Add Transaction</button>}
              {view==='ventures' &&
                <button className="btn bp" onClick={()=>setShowV(true)}>+ Add Venture</button>}
              {view==='accounts' &&
                <button className="btn bp" onClick={()=>setShowA(true)}>+ Add Account</button>}
              {view==='agents' &&
                <button className="btn bp" onClick={()=>{ setEditAgent(null); setAgModalKey(k=>k+1); setShowAg(true); }}>+ Add Agent</button>}
            </div>
          </div>
          {migrationNotice && (
            <div style={{margin:'12px 24px 0',padding:'10px 12px',background:'rgba(96,165,250,.12)',border:'1px solid rgba(96,165,250,.35)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
              <div style={{fontSize:12,color:'var(--txt)',lineHeight:1.5}}>{migrationNotice}</div>
              <button className="btn bg bs" onClick={() => setMigrationNotice('')}>Dismiss</button>
            </div>
          )}
          <div className="content">
            {selectedBranch && <BranchDetail venture={ventures.find(v=>v.id===selectedBranch.ventureId)} branch={selectedBranch.branch} transactions={transactions} accounts={accounts} onBack={()=>setSelectedBranch(null)}/>}
            {!selectedBranch && view==='dashboard'    && <Dashboard txs={transactions} ventures={ventures} accounts={accounts} agents={agents} onOpenAgents={()=>setView('agents')}/>}
            {!selectedBranch && view==='transactions' && <Transactions txs={filteredTx} allTxs={transactions} ventures={ventures} accounts={accounts} items={items} filters={filters} setFilters={setFilters} onDelete={deleteTransaction} onEdit={setEditTx}/>} 
            {!selectedBranch && hotspotPosEnabled && view==='hotspot-pos'  && <HotspotPOSPage txs={transactions} ventures={ventures} accounts={accounts} agents={agents} onRecordSale={async sale=>{ await saveTx(prev=>[sale,...prev]); }} onUpdateSale={async sale=>{ await saveTx(prev=>prev.map(x=>x.id===sale.id?{...x,...sale}:x)); }} onDeleteSale={async id=>{ await saveTx(prev=>prev.filter(x=>x.id!==id)); }} />}
            {!selectedBranch && view==='loans'        && <ShortTermLoanPage txs={transactions} ventures={ventures} accounts={accounts} loans={loans} onAddLoan={async l=>{ await saveLn(prev=>[l,...prev]); }} onEditLoan={async l=>{ await saveLn(prev=>prev.map(x=>x.id===l.id?l:x)); }} onDeleteLoan={deleteLoan}/>} 
            {!selectedBranch && view==='ventures'     && <Ventures ventures={ventures} txs={transactions} onDelete={async id=>{ await deleteVenture(id); if (selectedBranch?.ventureId === id) setSelectedBranch(null); }} onDeleteAll={async()=>{ await deleteAllVentures(); setSelectedBranch(null); }} onEdit={v=>setEditVenture(v)} onSelectBranch={(ventureId,branch)=>setSelectedBranch({ventureId,branch})} onUpdateBranch={async(ventureId,branchId,name)=>{ await saveV(prev=>prev.map(v=>v.id===ventureId?{...v,branches:(v.branches||[]).map(b=>b.id===branchId?{...b,name}:b)}:v)); }} onAddBranch={async(ventureId,branch)=>{ await saveV(prev=>prev.map(v=>v.id===ventureId?{...v,branches:[...(v.branches||[]),branch]}:v)); }} onAdd={()=>setShowV(true)}/>} 
            {!selectedBranch && view==='accounts'     && <Accounts accounts={accounts} txs={transactions} onDelete={deleteAccount}/>} 
            {!selectedBranch && view==='items'        && <ItemListPage items={items} accounts={accounts} onSaveItems={saveIt}/>} 
            {!selectedBranch && view==='agents'       && <AgentsView agents={agents} txs={transactions} ventures={ventures} onDelete={deleteAgent} onDeleteAll={deleteAllAgents} onEdit={ag=>setEditAgent(ag)} onAdd={()=>{ setEditAgent(null); setAgModalKey(k=>k+1); setShowAg(true); }}/>} 
            {!selectedBranch && view==='snapshots'    && <SnapshotsPage txs={transactions} ventures={ventures} agents={agents}/>} 
            {!selectedBranch && view==='reports'      && <Reports txs={transactions} ventures={ventures} accounts={accounts} reportTab={reportTab}/>}
            {!selectedBranch && view==='journal'      && <Journal txs={transactions} accounts={accounts} ventures={ventures}/>}
            {!selectedBranch && view==='about'        && <AboutPage />}
          </div>


        </main>
      </div>

      {showTx && <AddTransactionModal ventures={ventures} accounts={accounts} agents={agents} loans={loans} items={items} onClose={()=>setShowTx(false)} onSave={async tx=>{ await saveTx(prev=>[tx,...prev]); setShowTx(false); }}/>} 
      {editTx && <AddTransactionModal ventures={ventures} accounts={accounts} agents={agents} loans={loans} items={items} initialTransaction={editTx} onClose={()=>setEditTx(null)} onSave={async tx=>{ await saveTx(prev=>prev.map(existing=>existing.id===tx.id?{...existing,...tx}:existing)); setEditTx(null); }}/>} 
      {showV  && <AddVentureModal onClose={()=>setShowV(false)} onSave={async v=>{ await saveV(prev=>[...prev,v]); setShowV(false); }}/>}
      {showA  && <AddAccountModal onClose={()=>setShowA(false)} onSave={async a=>{ await saveA(prev=>[...prev,a]); setShowA(false); }}/>}
      {showAg     && <AddAgentModal key={agModalKey} ventures={ventures} onClose={()=>setShowAg(false)} onSave={async ag=>{ await saveAg(prev=>[...prev,ag]); setShowAg(false); }}/>} 
      {editAgent  && <EditAgentModal agent={editAgent} ventures={ventures} onClose={()=>setEditAgent(null)} onSave={async ag=>{ await saveAg(prev=>prev.map(x=>x.id===ag.id?ag:x)); setEditAgent(null); }}/>} 
      {editVenture && <EditVentureModal venture={editVenture} onClose={()=>setEditVenture(null)} onSave={async v=>{ await saveV(prev=>prev.map(x=>x.id===v.id?v:x)); setEditVenture(null); }}/>} 
      {showProfileEditor && <EditProfileModal initialProfile={mergedProfileDetails} busy={profileSaveBusy} error={profileSaveError} onClose={()=>{ setShowProfileEditor(false); setProfileSaveError(''); }} onSave={handleSaveProfile} />}
      <input ref={importFileInputRef} type="file" accept="application/json,.json" style={{display:'none'}} onChange={handleBrowserImportData} />

      {/* UPDATE NOTIFICATIONS */}
      {(updateCheckDialog.mode === 'checking' || updateCheckDialog.mode === 'none' || updateCheckDialog.mode === 'error' || updateAvailable || updateReady) && (
        <div style={{position:'fixed',inset:0,background:'rgba(4,8,18,0.72)',backdropFilter:'blur(3px)',zIndex:1500,display:'flex',alignItems:'center',justifyContent:'center',padding:22}}>
          <div style={{width:'min(700px, 96vw)',maxHeight:'82vh',overflow:'auto',background:'var(--s2)',border:'1px solid var(--brdg)',borderRadius:14,boxShadow:'0 18px 45px rgba(0,0,0,.55)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 18px',borderBottom:'1px solid var(--brd)',background:'var(--s1)'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:10,height:10,borderRadius:'50%',background:'var(--gold)'}} />
                <span style={{fontSize:16,fontWeight:700,color:'var(--gold)',fontFamily:'var(--serif)'}}>Financial Management System (FMS) Updates</span>
              </div>
              {updateCheckDialog.mode !== 'checking' && (
                <button style={{border:'none',background:'transparent',fontSize:22,lineHeight:1,cursor:'pointer',color:'var(--tm)'}} onClick={closeUpdateDialog}>x</button>
              )}
            </div>

            <div style={{padding:'22px 20px 18px',display:'grid',gap:14,color:'var(--txt)'}}>
              {updateCheckDialog.mode === 'checking' && (
                <>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:26,height:26,border:'3px solid var(--s3)',borderTopColor:'var(--gold)',borderRadius:'50%',animation:'spin 1s linear infinite'}} />
                    <div style={{fontSize:25,fontWeight:700,lineHeight:1.2,color:'var(--tb)'}}>Checking for updates</div>
                  </div>
                  <div style={{fontSize:14,color:'var(--tm)'}}>Checking update information, please wait...</div>
                </>
              )}

              {updateAvailable && (
                <>
                  <div style={{display:'flex',alignItems:'flex-start',gap:14,paddingBottom:4}}>
                    <div style={{width:54,height:54,borderRadius:'50%',border:'2px solid var(--ok)',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(45,212,160,.12)',color:'var(--ok)',fontSize:28,lineHeight:1}}>
                      ^
                    </div>
                    <div style={{display:'grid',gap:5,flex:1,minWidth:0}}>
                      <div style={{fontSize:33,fontWeight:700,lineHeight:1.03,color:'var(--ok)',fontFamily:'var(--serif)'}}>New version is ready</div>
                      <div style={{fontSize:20,color:'var(--tb)',lineHeight:1.45}}>
                        Financial Management System (FMS) {updateInfo.version ? `v${updateInfo.version}` : 'new version'} is now available.
                        Update it to enjoy the latest improvements and features.
                      </div>
                    </div>
                  </div>

                  <div style={{display:'grid',gap:8,marginTop:2}}>
                    <div style={{fontSize:25,color:'var(--tb)',fontFamily:'var(--serif)'}}>What's new</div>
                    <div style={{border:'1px solid var(--brd)',background:'linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,.01))',borderRadius:10,padding:'12px 14px',maxHeight:220,overflow:'auto'}}>
                      {updateNotesItems.length > 0 ? (
                        <div style={{display:'grid',gap:6}}>
                          {updateNotesItems.map((line, index) => (
                            <div key={`${line}-${index}`} style={{fontSize:13,lineHeight:1.5,color:'var(--txt)'}}>+ {line}</div>
                          ))}
                        </div>
                      ) : (
                        <div style={{fontSize:13,lineHeight:1.5,color:'var(--tm)'}}>+ Performance improvements and bug fixes</div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {updateCheckDialog.mode === 'none' && (
                <>
                  <div style={{fontSize:25,fontWeight:700,lineHeight:1.2,color:'var(--tb)',fontFamily:'var(--serif)'}}>No updates available</div>
                  <div style={{fontSize:15,color:'var(--txt)'}}>{updateCheckDialog.message}</div>
                  <div style={{fontSize:13,color:'var(--tm)'}}>Current version: {updateCheckDialog.version || updateInfo.version || 'Unknown'}</div>
                </>
              )}

              {updateCheckDialog.mode === 'error' && (
                <>
                  <div style={{fontSize:25,fontWeight:700,lineHeight:1.2,color:'var(--tb)',fontFamily:'var(--serif)'}}>Update check failed</div>
                  <div style={{fontSize:15,color:'var(--txt)'}}>{updateCheckDialog.message}</div>
                  {updateCheckDialog.detail && <div style={{fontSize:13,color:'var(--err)',background:'rgba(240,92,92,.1)',border:'1px solid rgba(240,92,92,.25)',borderRadius:8,padding:10,lineHeight:1.55}}>{updateCheckDialog.detail}</div>}
                  <div style={{fontSize:12,color:'var(--tm)',lineHeight:1.6}}>If the problem persists, confirm that your live server can open <span className="mono">/updates/latest.yml</span> directly in a browser.</div>
                </>
              )}

              {updateReady && (
                <>
                  <div style={{display:'flex',alignItems:'flex-start',gap:14,paddingBottom:4}}>
                    <div style={{width:54,height:54,borderRadius:'50%',border:'2px solid var(--ok)',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(45,212,160,.12)',color:'var(--ok)',fontSize:28,lineHeight:1}}>
                      OK
                    </div>
                    <div style={{display:'grid',gap:5,flex:1,minWidth:0}}>
                      <div style={{fontSize:33,fontWeight:700,lineHeight:1.03,color:'var(--ok)',fontFamily:'var(--serif)'}}>Update is ready</div>
                      <div style={{fontSize:20,color:'var(--tb)',lineHeight:1.45}}>
                        Financial Management System (FMS) {updateInfo.version ? `v${updateInfo.version}` : 'new version'} has finished downloading.
                        Install now to apply the latest improvements and features.
                      </div>
                    </div>
                  </div>

                  <div style={{display:'grid',gap:8,marginTop:2}}>
                    <div style={{fontSize:25,color:'var(--tb)',fontFamily:'var(--serif)'}}>What's new</div>
                    <div style={{border:'1px solid var(--brd)',background:'linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,.01))',borderRadius:10,padding:'12px 14px',maxHeight:220,overflow:'auto'}}>
                      {updateNotesItems.length > 0 ? (
                        <div style={{display:'grid',gap:6}}>
                          {updateNotesItems.map((line, index) => (
                            <div key={`ready-${line}-${index}`} style={{fontSize:13,lineHeight:1.5,color:'var(--txt)'}}>+ {line}</div>
                          ))}
                        </div>
                      ) : (
                        <div style={{fontSize:13,lineHeight:1.5,color:'var(--tm)'}}>+ Performance improvements and bug fixes</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,padding:'14px 18px',borderTop:'1px solid var(--brd)',background:'var(--s1)'}}>
              <button className="btn bg bs" onClick={() => {
                if (updateAvailable) setUpdateAvailable(false);
                else if (updateReady) setUpdateReady(false);
                closeUpdateDialog();
              }}>
                {updateReady ? 'Install later' : updateAvailable ? 'Update later' : 'Close'}
              </button>

              {updateAvailable && (
                <button className="btn bp" onClick={handleDownloadUpdate}>
                  Update now
                </button>
              )}
              {updateReady && (
                <button className="btn bp" onClick={handleInstallUpdate}>
                  Install now
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {updateDownloading && (
        <div style={{position:'fixed',inset:0,background:'rgba(4,8,18,0.72)',backdropFilter:'blur(3px)',zIndex:1600,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
          <div style={{width:'min(700px, 96vw)',maxHeight:'82vh',overflow:'auto',background:'var(--s2)',border:'1px solid var(--brdg)',borderRadius:14,boxShadow:'0 18px 45px rgba(0,0,0,.55)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 18px',borderBottom:'1px solid var(--brd)',background:'var(--s1)'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:10,height:10,borderRadius:'50%',background:'var(--gold)'}} />
                <span style={{fontSize:16,fontWeight:700,color:'var(--gold)',fontFamily:'var(--serif)'}}>Financial Management System (FMS) Updates</span>
              </div>
            </div>

            <div style={{padding:'22px 20px 18px',display:'grid',gap:14,color:'var(--txt)'}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:14,paddingBottom:4}}>
                <div style={{width:54,height:54,borderRadius:'50%',border:'2px solid var(--gold)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--gold-d)',color:'var(--gold)',fontSize:26,lineHeight:1}}>
                  v
                </div>
                <div style={{display:'grid',gap:5,flex:1,minWidth:0}}>
                  <div style={{fontSize:33,fontWeight:700,lineHeight:1.03,color:'var(--gold)',fontFamily:'var(--serif)'}}>Downloading update</div>
                  <div style={{fontSize:20,color:'var(--tb)',lineHeight:1.45}}>
                    Financial Management System (FMS) {updateInfo.version ? `v${updateInfo.version}` : 'new version'} is downloading now.
                    Please keep the app open while we prepare your update.
                  </div>
                </div>
              </div>

              <div style={{display:'grid',gap:8,marginTop:2}}>
                <div style={{fontSize:25,color:'var(--tb)',fontFamily:'var(--serif)'}}>Download progress</div>
                <div style={{border:'1px solid var(--brd)',background:'linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,.01))',borderRadius:10,padding:'12px 14px',display:'grid',gap:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,color:'var(--txt)'}}>
                    <span>Progress</span>
                    <span style={{fontFamily:'var(--mono)',fontSize:20,fontWeight:700,color:'var(--gold)'}}>{updateDownloadPercent}%</span>
                  </div>

                  <div style={{height:10,background:'var(--s3)',borderRadius:999,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${updateDownloadPercent}%`,background:'linear-gradient(90deg,rgba(201,168,76,.4),var(--gold))',transition:'width .22s linear'}}/>
                  </div>

                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,color:'var(--txt)'}}>
                    <span>{formatTransferSize(updateDownloadMeta.transferred)} / {formatTransferSize(updateDownloadMeta.total || updateDownloadMeta.transferred)}</span>
                    <span style={{fontFamily:'var(--mono)',color:'var(--gold)'}}>{formatTransferSize(updateDownloadMeta.bytesPerSecond)}/s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {backupStatus && (
        <div style={{position:'fixed',bottom:20,right:20,background:backupStatus.includes('error')?'#ef4444':'#10b981',color:'white',padding:'12px 16px',borderRadius:6,fontSize:13,zIndex:999,boxShadow:'0 10px 25px rgba(0,0,0,0.2)'}}>
          {backupStatus}
        </div>
      )}
      {isSupabaseSyncEnabled && showSyncHistory && syncHistory.length > 0 && (
        <div style={{position:'fixed',left:20,bottom:20,width:360,maxHeight:260,overflow:'auto',background:'var(--s2)',border:'1px solid var(--brd)',borderRadius:8,padding:10,zIndex:998,boxShadow:'0 10px 25px rgba(0,0,0,0.25)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <div style={{fontSize:11,color:'var(--tm)',textTransform:'uppercase',letterSpacing:1}}>Sync History</div>
            <button className="btn bg bs" style={{padding:'2px 8px'}} onClick={()=>setSyncHistory([])}>Clear</button>
          </div>
          <div style={{display:'grid',gap:6}}>
            {syncHistory.map(item => {
              const tone = item.level === 'error' ? 'var(--err)' : item.level === 'warn' ? '#fb923c' : item.level === 'success' ? 'var(--ok)' : 'var(--info)';
              return (
                <div key={item.id} style={{padding:'6px 8px',background:'var(--s3)',border:'1px solid var(--brd)',borderRadius:6}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
                    <span style={{fontSize:10,color:tone,fontWeight:700,textTransform:'uppercase'}}>{item.level}</span>
                    <span style={{fontSize:10,color:'var(--tm)',fontFamily:'var(--mono)'}}>{new Date(item.at).toLocaleTimeString()}</span>
                  </div>
                  <div style={{fontSize:11,color:'var(--txt)'}}>{item.message}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}



